import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { aboutComparisonEn } from '../i18n/aboutComparison.en';
import { aboutComparisonEs } from '../i18n/aboutComparison.es';
import { aboutComparisonPt } from '../i18n/aboutComparison.pt';
import { aboutComparisonFr } from '../i18n/aboutComparison.fr';
import { aboutComparisonDe } from '../i18n/aboutComparison.de';
import { aboutComparisonJa } from '../i18n/aboutComparison.ja';
import { aboutComparisonZh } from '../i18n/aboutComparison.zh';
import { remainingEn } from '../i18n/remaining.en';
import { remainingEs } from '../i18n/remaining.es';
import { remainingPt } from '../i18n/remaining.pt';
import { remainingFr } from '../i18n/remaining.fr';
import { remainingDe } from '../i18n/remaining.de';
import { remainingJa } from '../i18n/remaining.ja';
import { remainingZh } from '../i18n/remaining.zh';
import { trustStrings } from '../i18n/trust';

export type Language = 'en' | 'es' | 'pt' | 'fr' | 'de' | 'ja' | 'zh';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const VALID_LANGUAGES: Language[] = ['en', 'es', 'pt', 'fr', 'de', 'ja', 'zh'];

function isValidLanguage(lang: string): lang is Language {
  return VALID_LANGUAGES.includes(lang as Language);
}

function resolveInitialLanguage(): Language {
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (urlLang && isValidLanguage(urlLang)) return urlLang;

  const saved = localStorage.getItem('katoa-language') || localStorage.getItem('bitwish-language');
  if (saved && isValidLanguage(saved)) return saved;

  const navLang = navigator.language?.split('-')[0]?.toLowerCase();
  if (navLang && isValidLanguage(navLang)) return navLang;

  return 'en';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const homeStrings = {
  en: {
    'home.badge': 'Bitcoin-native • Non-custodial • Open Source',
    'home.keep': 'Keep',
    'home.percent': '100%',
    'home.earnings': 'of Your Earnings',
    'home.subtitle': 'Zero-fee, privacy-first Bitcoin commerce.',
    'home.subtitle2': 'Protocol-level upgrade to creator monetization.',
    'home.cta.start': 'Start Earning 100%',
    'home.cta.compare': 'See How We Compare',
    'home.stats.processed': 'processed',
    'home.stats.countries': 'countries',
    'home.stats.fees': 'fees thru 2026',
    'home.protocol': 'Protocol',
    'home.builtDifferent': 'Built Different',
    'home.fees.title': 'Stop Losing Thousands to Fees',
    'home.fees.subtitle': 'See how much you keep with KATOA vs traditional platforms.',
    'home.howItWorks.title': 'How It Works',
    'home.howItWorks.subtitle': 'From sign-up to your first sat in four steps',
    'home.step1.title': 'Create Account',
    'home.step1.desc': 'Sign up with email, Google, or Nostr — no bank account required.',
    'home.step2.title': 'Build Wishlist',
    'home.step2.desc': 'Add goals, media, and your story. Set sat prices for each item.',
    'home.step3.title': 'Share Your Link',
    'home.step3.desc': 'Post your page anywhere. QR codes make it easy for supporters.',
    'home.step4.title': 'Receive Bitcoin',
    'home.step4.desc': 'Donations land in seconds over Lightning once receive ships (Roadmap: In progress).',
    'home.join.title': 'Join the Bitcoin Creator Economy',
    'home.join.subtitle': 'Creators worldwide are stopping platform fees and building true financial freedom.',
    'home.join.cta': 'Get Started Free',
    'home.join.browse': 'Browse Campaigns',
    'home.howItWorks.eyebrow': 'How it works',
    'home.pillar1.title': 'Settlement in seconds',
    'home.pillar1.desc': 'Funds arrive over Bitcoin Lightning in seconds. No payment processor holding your money for weeks.',
    'home.pillar1.old': '7–14 days',
    'home.pillar1.next': 'Once receive ships',
    'home.pillar2.title': 'Truly global',
    'home.pillar2.desc': 'Creators in 195+ countries. No bank account, no geographic gatekeeping, no Stripe exclusions.',
    'home.pillar2.old': '~10 countries',
    'home.pillar2.next': '195+ countries',
    'home.pillar3.title': 'Privacy by design',
    'home.pillar3.desc': 'Nostr-native identity, minimal data collection, and a roadmap toward stronger payment privacy.',
    'home.pillar3.old': 'Platform-owned',
    'home.pillar3.next': 'You own your keys',
    'home.bento.traditional': 'Traditional',
    'home.bento.katoa': 'KATOA',
    'home.cta.note': '0% platform fees through 31 December 2026 — absorbed by Give A Bit and community support. Lightning costs are minimal and usually paid by supporters. KATOA never custodies your balance.',
    'home.cta.launch': 'Live now',
    'home.cta.processed': 'Processed',
    'home.cta.fees': 'Fees',
    'home.metaTitle': 'KATOA — Zero-Fee Bitcoin Creator Platform',
    'home.metaDesc': 'Keep 100% of your earnings. Privacy-first wishlists and donations on Bitcoin Lightning. 0% fees through 31 Dec 2026.',
    'home.chip.creators': 'creators',
    'home.chip.zeroFees': '0% fees',
    'home.chip.lightning': 'Lightning',
    'home.tip.eyebrow': 'Katoa — the zero-fee, non-custodial, Bitcoin-native creator platform. Part of the Give A Bit family.',
    'home.tip.volume': 'Labeled product metrics — not sats Katoa custodies or processes. Payments go peer-to-peer on Lightning. Sample figures are marked Demo until live counters exist.',
    'home.tip.countries': 'Bitcoin Lightning works wherever the internet does. Katoa does not require a bank or KYC to start. Follow the law where you operate.',
    'home.tip.fees': 'Platform fee is 0% through 31 Dec 2026 — not a promo. Lightning network routing fees still apply and are not paid to Katoa.',
    'home.shot.demoTitle': 'Sample product preview',
    'home.shot.progressTip': 'Demo catalog sample — 65% of a 5,000,000 sats goal. Same progress UI as a real wishlist. Katoa does not custody or process the sats; they go to the creator\'s Lightning wallet.',
  },
  es: {
    'home.badge': 'Nativo Bitcoin • No custodial • Código abierto',
    'home.keep': 'Conserva',
    'home.percent': '100%',
    'home.earnings': 'de tus ganancias',
    'home.subtitle': 'Comercio Bitcoin sin comisiones y con privacidad.',
    'home.subtitle2': 'Actualización a nivel de protocolo para monetización de creadores.',
    'home.cta.start': 'Empieza a ganar 100%',
    'home.cta.compare': 'Compara plataformas',
    'home.stats.processed': 'procesado',
    'home.stats.countries': 'países',
    'home.stats.fees': 'comisiones hasta 2026',
    'home.protocol': 'Protocolo',
    'home.builtDifferent': 'Construido diferente',
    'home.fees.title': 'Deja de perder miles en comisiones',
    'home.fees.subtitle': 'Mira cuánto conservas con KATOA vs plataformas tradicionales.',
    'home.howItWorks.title': 'Cómo funciona',
    'home.howItWorks.subtitle': 'Del registro a tu primer sat en cuatro pasos',
    'home.step1.title': 'Crear cuenta',
    'home.step1.desc': 'Regístrate con email, Google o Nostr — sin cuenta bancaria.',
    'home.step2.title': 'Crear lista',
    'home.step2.desc': 'Añade metas, medios y tu historia. Pon precios en sats.',
    'home.step3.title': 'Comparte tu enlace',
    'home.step3.desc': 'Publica tu página en cualquier lugar. Códigos QR facilitan el apoyo.',
    'home.step4.title': 'Recibe Bitcoin',
    'home.step4.desc': 'Las donaciones llegan en segundos por Lightning cuando la recepción esté disponible (Roadmap: En curso).',
    'home.join.title': 'Únete a la economía creadora Bitcoin',
    'home.join.subtitle': 'Creadores en todo el mundo dejan de pagar comisiones y ganan libertad financiera.',
    'home.join.cta': 'Empezar gratis',
    'home.join.browse': 'Explorar campañas',
    'home.howItWorks.eyebrow': 'Cómo funciona',
    'home.pillar1.title': 'Liquidación en segundos',
    'home.pillar1.desc': 'Los fondos llegan por Bitcoin Lightning en segundos. Sin procesador que retenga tu dinero semanas.',
    'home.pillar1.old': '7–14 días',
    'home.pillar1.next': 'Cuando la recepción esté disponible',
    'home.pillar2.title': 'Verdaderamente global',
    'home.pillar2.desc': 'Creadores en más de 195 países. Sin cuenta bancaria, sin barreras geográficas, sin exclusiones de Stripe.',
    'home.pillar2.old': '~10 países',
    'home.pillar2.next': '195+ países',
    'home.pillar3.title': 'Privacidad de diseño',
    'home.pillar3.desc': 'Identidad nativa Nostr, datos mínimos, y un camino hacia pagos más privados.',
    'home.pillar3.old': 'De la plataforma',
    'home.pillar3.next': 'Tú tienes las llaves',
    'home.bento.traditional': 'Tradicional',
    'home.bento.katoa': 'KATOA',
    'home.cta.note': '0% de comisión de plataforma hasta el 31 de diciembre de 2026, absorbida por Give A Bit y el apoyo de la comunidad. Los costes Lightning son mínimos y suelen pagarlos los fans. KATOA nunca custodia tu saldo.',
    'home.cta.launch': 'En vivo',
    'home.cta.processed': 'Procesado',
    'home.cta.fees': 'Comisiones',
    'home.metaTitle': 'KATOA — Plataforma Bitcoin para creadores sin comisiones',
    'home.metaDesc': 'Quédate el 100% de tus ganancias. Listas de deseos y donaciones con privacidad en Bitcoin Lightning. 0% de comisión hasta el 31 dic 2026.',
    'home.chip.creators': 'creadores',
    'home.chip.zeroFees': '0% comisiones',
    'home.chip.lightning': 'Lightning',
    'home.tip.eyebrow': 'Katoa — la plataforma para creadores nativa de Bitcoin, no custodial y sin comisiones. Parte de la familia Give A Bit.',
    'home.tip.volume': 'Métricas etiquetadas del producto — no son sats que Katoa custodie o procese. Los pagos van de persona a persona por Lightning. Las cifras de muestra se marcan Demo hasta que existan contadores en vivo.',
    'home.tip.countries': 'Bitcoin Lightning funciona donde hay internet. Katoa no exige banco ni KYC para empezar. Cumple la ley donde operes.',
    'home.tip.fees': 'La comisión de plataforma es 0% hasta el 31 dic 2026 — no es una promo. Las comisiones de enrutado Lightning siguen existiendo y no se pagan a Katoa.',
    'home.shot.demoTitle': 'Vista previa de producto de muestra',
    'home.shot.progressTip': 'Muestra del catálogo demo — 65% de una meta de 5.000.000 sats. La misma UI de progreso que una lista real. Katoa no custodia ni procesa los sats; van a la cartera Lightning del creador.',
  },
  pt: {
    'home.badge': 'Nativo Bitcoin • Não custodial • Código aberto',
    'home.keep': 'Fique com',
    'home.percent': '100%',
    'home.earnings': 'dos seus ganhos',
    'home.subtitle': 'Comércio Bitcoin sem taxas e com privacidade.',
    'home.subtitle2': 'Atualização em nível de protocolo para monetização de criadores.',
    'home.cta.start': 'Comece a ganhar 100%',
    'home.cta.compare': 'Compare plataformas',
    'home.stats.processed': 'processado',
    'home.stats.countries': 'países',
    'home.stats.fees': 'taxas até 2026',
    'home.protocol': 'Protocolo',
    'home.builtDifferent': 'Construído diferente',
    'home.fees.title': 'Pare de perder milhares em taxas',
    'home.fees.subtitle': 'Veja quanto você fica com KATOA vs plataformas tradicionais.',
    'home.howItWorks.title': 'Como funciona',
    'home.howItWorks.subtitle': 'Do cadastro ao seu primeiro sat em quatro passos',
    'home.step1.title': 'Criar conta',
    'home.step1.desc': 'Cadastre-se com email, Google ou Nostr — sem conta bancária.',
    'home.step2.title': 'Montar lista',
    'home.step2.desc': 'Adicione metas, mídia e sua história. Defina preços em sats.',
    'home.step3.title': 'Compartilhe o link',
    'home.step3.desc': 'Publique sua página em qualquer lugar. QR codes facilitam o apoio.',
    'home.step4.title': 'Receba Bitcoin',
    'home.step4.desc': 'Doações chegam em segundos via Lightning quando a recepção estiver disponível (Roadmap: Em andamento).',
    'home.join.title': 'Junte-se à economia criativa Bitcoin',
    'home.join.subtitle': 'Criadores no mundo todo param de pagar taxas e ganham liberdade financeira.',
    'home.join.cta': 'Começar grátis',
    'home.join.browse': 'Ver campanhas',
    'home.howItWorks.eyebrow': 'Como funciona',
    'home.pillar1.title': 'Liquidação em segundos',
    'home.pillar1.desc': 'Os fundos chegam via Bitcoin Lightning em segundos. Sem processador retendo seu dinheiro por semanas.',
    'home.pillar1.old': '7–14 dias',
    'home.pillar1.next': 'Quando o recebimento estiver disponível',
    'home.pillar2.title': 'Verdadeiramente global',
    'home.pillar2.desc': 'Criadores em 195+ países. Sem conta bancária, sem barreiras geográficas, sem exclusões do Stripe.',
    'home.pillar2.old': '~10 países',
    'home.pillar2.next': '195+ países',
    'home.pillar3.title': 'Privacidade de raiz',
    'home.pillar3.desc': 'Identidade nativa Nostr, dados mínimos e um caminho para pagamentos mais privados.',
    'home.pillar3.old': 'Da plataforma',
    'home.pillar3.next': 'Você tem as chaves',
    'home.bento.traditional': 'Tradicional',
    'home.bento.katoa': 'KATOA',
    'home.cta.note': '0% de taxa da plataforma até 31 de dezembro de 2026, absorvida pela Give A Bit e pelo apoio da comunidade. Custos Lightning são mínimos e em geral pagos pelos fãs. A KATOA nunca custodia seu saldo.',
    'home.cta.launch': 'No ar',
    'home.cta.processed': 'Processado',
    'home.cta.fees': 'Taxas',
    'home.metaTitle': 'KATOA — Plataforma Bitcoin para criadores sem taxas',
    'home.metaDesc': 'Fique com 100% dos seus ganhos. Listas de desejos e doações com privacidade no Bitcoin Lightning. 0% de taxa até 31 dez 2026.',
    'home.chip.creators': 'criadores',
    'home.chip.zeroFees': '0% de taxas',
    'home.chip.lightning': 'Lightning',
    'home.tip.eyebrow': 'Katoa — a plataforma para criadores nativa de Bitcoin, não custodial e sem taxas. Parte da família Give A Bit.',
    'home.tip.volume': 'Métricas rotuladas do produto — não são sats que a Katoa custodie ou processe. Pagamentos vão peer-to-peer no Lightning. Números de amostra são marcados Demo até existirem contadores ao vivo.',
    'home.tip.countries': 'Bitcoin Lightning funciona onde há internet. A Katoa não exige banco nem KYC para começar. Siga a lei onde você opera.',
    'home.tip.fees': 'A taxa da plataforma é 0% até 31 dez 2026 — não é promoção. Taxas de roteamento Lightning ainda existem e não são pagas à Katoa.',
    'home.shot.demoTitle': 'Prévia de produto de amostra',
    'home.shot.progressTip': 'Amostra do catálogo demo — 65% de uma meta de 5.000.000 sats. A mesma UI de progresso de uma lista real. A Katoa não custodia nem processa os sats; eles vão para a carteira Lightning do criador.',
  },
  fr: {
    'home.badge': 'Bitcoin natif • Non custodial • Open source',
    'home.keep': 'Gardez',
    'home.percent': '100%',
    'home.earnings': 'de vos revenus',
    'home.subtitle': 'Commerce Bitcoin sans frais et axé sur la confidentialité.',
    'home.subtitle2': 'Mise à niveau au niveau protocole pour la monétisation des créateurs.',
    'home.cta.start': 'Gagnez 100%',
    'home.cta.compare': 'Comparer les plateformes',
    'home.stats.processed': 'traité',
    'home.stats.countries': 'pays',
    'home.stats.fees': 'frais jusqu’en 2026',
    'home.protocol': 'Protocole',
    'home.builtDifferent': 'Conçu différemment',
    'home.fees.title': 'Arrêtez de perdre des milliers en frais',
    'home.fees.subtitle': 'Voyez combien vous gardez avec KATOA vs les plateformes traditionnelles.',
    'home.howItWorks.title': 'Comment ça marche',
    'home.howItWorks.subtitle': 'De l\'inscription à votre premier sat en quatre étapes',
    'home.step1.title': 'Créer un compte',
    'home.step1.desc': 'Inscrivez-vous par email, Google ou Nostr — sans compte bancaire.',
    'home.step2.title': 'Créer une liste',
    'home.step2.desc': 'Ajoutez objectifs, médias et votre histoire. Fixez des prix en sats.',
    'home.step3.title': 'Partagez votre lien',
    'home.step3.desc': 'Publiez votre page partout. Les QR codes facilitent le soutien.',
    'home.step4.title': 'Recevez du Bitcoin',
    'home.step4.desc': 'Les dons arrivent en quelques secondes via Lightning dès que la réception est disponible (Roadmap : en cours).',
    'home.join.title': 'Rejoignez l\'économie créative Bitcoin',
    'home.join.subtitle': 'Les créateurs du monde entier arrêtent les frais et gagnent en liberté financière.',
    'home.join.cta': 'Commencer gratuitement',
    'home.join.browse': 'Parcourir les campagnes',
    'home.howItWorks.eyebrow': 'Comment ça marche',
    'home.pillar1.title': 'Règlement en quelques secondes',
    'home.pillar1.desc': 'Les fonds arrivent via Bitcoin Lightning en quelques secondes. Pas de processeur qui retient votre argent des semaines.',
    'home.pillar1.old': '7–14 jours',
    'home.pillar1.next': 'Quand la réception sera en ligne',
    'home.pillar2.title': 'Vraiment mondial',
    'home.pillar2.desc': 'Créateurs dans 195+ pays. Pas de compte bancaire, pas de barrière géographique, pas d’exclusions Stripe.',
    'home.pillar2.old': '~10 pays',
    'home.pillar2.next': '195+ pays',
    'home.pillar3.title': 'Confidentialité dès la conception',
    'home.pillar3.desc': 'Identité native Nostr, données minimales, et une feuille de route vers des paiements plus privés.',
    'home.pillar3.old': 'Propriété de la plateforme',
    'home.pillar3.next': 'Vous détenez les clés',
    'home.bento.traditional': 'Traditionnel',
    'home.bento.katoa': 'KATOA',
    'home.cta.note': '0 % de frais de plateforme jusqu\'au 31 décembre 2026, pris en charge par Give A Bit et le soutien de la communauté. Les coûts Lightning sont minimes et souvent payés par les fans. KATOA ne custodie jamais votre solde.',
    'home.cta.launch': 'En ligne',
    'home.cta.processed': 'Traité',
    'home.cta.fees': 'Frais',
    'home.metaTitle': 'KATOA — Plateforme Bitcoin sans frais pour créateurs',
    'home.metaDesc': 'Gardez 100 % de vos revenus. Listes de souhaits et dons axés sur la confidentialité via Bitcoin Lightning. 0 % de frais jusqu’au 31 déc. 2026.',
    'home.chip.creators': 'créateurs',
    'home.chip.zeroFees': '0 % de frais',
    'home.chip.lightning': 'Lightning',
    'home.tip.eyebrow': 'Katoa — la plateforme pour créateurs native Bitcoin, non custodiale et sans frais. Membre de la famille Give A Bit.',
    'home.tip.volume': 'Métriques produit étiquetées — ce ne sont pas des sats que Katoa custodie ou traite. Les paiements vont de pair à pair sur Lightning. Les chiffres d’exemple sont marqués Démo tant qu’il n’y a pas de compteurs en direct.',
    'home.tip.countries': 'Bitcoin Lightning fonctionne partout où il y a internet. Katoa n’exige ni banque ni KYC pour commencer. Respectez la loi là où vous opérez.',
    'home.tip.fees': 'Les frais de plateforme sont de 0 % jusqu’au 31 déc. 2026 — ce n’est pas une promo. Les frais de routage Lightning s’appliquent encore et ne sont pas versés à Katoa.',
    'home.shot.demoTitle': 'Aperçu produit d’exemple',
    'home.shot.progressTip': 'Exemple du catalogue démo — 65 % d’un objectif de 5 000 000 sats. La même UI de progression qu’une vraie liste. Katoa ne custodie ni ne traite les sats ; ils vont au portefeuille Lightning du créateur.',
  },
  de: {
    'home.badge': 'Bitcoin-nativ • Nicht-verwahrend • Open Source',
    'home.keep': 'Behalte',
    'home.percent': '100%',
    'home.earnings': 'deiner Einnahmen',
    'home.subtitle': 'Gebührenfreier, datenschutzorientierter Bitcoin-Handel.',
    'home.subtitle2': 'Protokoll-Level-Upgrade für Creator-Monetarisierung.',
    'home.cta.start': 'Verdiene 100%',
    'home.cta.compare': 'Plattformen vergleichen',
    'home.stats.processed': 'verarbeitet',
    'home.stats.countries': 'Länder',
    'home.stats.fees': 'Gebühren bis 2026',
    'home.protocol': 'Protokoll',
    'home.builtDifferent': 'Anders gebaut',
    'home.fees.title': 'Hör auf, Tausende an Gebühren zu verlieren',
    'home.fees.subtitle': 'Sieh, wie viel du mit KATOA vs. traditionellen Plattformen behältst.',
    'home.howItWorks.title': 'So funktioniert\'s',
    'home.howItWorks.subtitle': 'Von der Anmeldung bis zu deinem ersten Sat in vier Schritten',
    'home.step1.title': 'Konto erstellen',
    'home.step1.desc': 'Registriere dich per E-Mail, Google oder Nostr — ohne Bankkonto.',
    'home.step2.title': 'Wunschliste erstellen',
    'home.step2.desc': 'Füge Ziele, Medien und deine Geschichte hinzu. Setze Sat-Preise.',
    'home.step3.title': 'Link teilen',
    'home.step3.desc': 'Poste deine Seite überall. QR-Codes erleichtern die Unterstützung.',
    'home.step4.title': 'Bitcoin empfangen',
    'home.step4.desc': 'Spenden landen in Sekunden über Lightning, sobald der Empfang verfügbar ist (Roadmap: in Arbeit).',
    'home.join.title': 'Tritt der Bitcoin-Creator-Ökonomie bei',
    'home.join.subtitle': 'Creator weltweit stoppen Plattformgebühren und gewinnen finanzielle Freiheit.',
    'home.join.cta': 'Kostenlos starten',
    'home.join.browse': 'Kampagnen durchsuchen',
    'home.howItWorks.eyebrow': 'So funktioniert’s',
    'home.pillar1.title': 'Abrechnung in Sekunden',
    'home.pillar1.desc': 'Gelder kommen über Bitcoin Lightning in Sekunden. Kein Zahlungsdienstleister, der dein Geld wochenlang hält.',
    'home.pillar1.old': '7–14 Tage',
    'home.pillar1.next': 'Sobald Empfang live ist',
    'home.pillar2.title': 'Wirklich global',
    'home.pillar2.desc': 'Creator in 195+ Ländern. Kein Bankkonto, keine geografischen Tore, keine Stripe-Ausschlüsse.',
    'home.pillar2.old': '~10 Länder',
    'home.pillar2.next': '195+ Länder',
    'home.pillar3.title': 'Privatsphäre by Design',
    'home.pillar3.desc': 'Nostr-native Identität, minimale Daten, und ein Weg zu privateren Zahlungen.',
    'home.pillar3.old': 'Plattform-Eigentum',
    'home.pillar3.next': 'Du besitzt die Schlüssel',
    'home.bento.traditional': 'Klassisch',
    'home.bento.katoa': 'KATOA',
    'home.cta.note': '0 % Plattformgebühr bis zum 31. Dezember 2026, getragen von Give A Bit und der Community. Lightning-Kosten sind gering und zahlen meist die Unterstützer. KATOA verwahrt niemals dein Guthaben.',
    'home.cta.launch': 'Live',
    'home.cta.processed': 'Verarbeitet',
    'home.cta.fees': 'Gebühren',
    'home.metaTitle': 'KATOA — Gebührenfreie Bitcoin-Creator-Plattform',
    'home.metaDesc': 'Behalte 100 % deiner Einnahmen. Datenschutzorientierte Wunschlisten und Spenden über Bitcoin Lightning. 0 % Gebühren bis zum 31. Dez. 2026.',
    'home.chip.creators': 'Creator',
    'home.chip.zeroFees': '0 % Gebühren',
    'home.chip.lightning': 'Lightning',
    'home.tip.eyebrow': 'Katoa — die gebührenfreie, nicht-verwahrende, Bitcoin-native Creator-Plattform. Teil der Give-A-Bit-Familie.',
    'home.tip.volume': 'Gekennzeichnete Produktmetriken — keine Sats, die Katoa verwahrt oder verarbeitet. Zahlungen laufen Peer-to-Peer über Lightning. Beispielzahlen sind als Demo markiert, bis Live-Zähler existieren.',
    'home.tip.countries': 'Bitcoin Lightning funktioniert, wo es Internet gibt. Katoa verlangt zum Start weder Bank noch KYC. Halte dich an das Recht am Ort deiner Tätigkeit.',
    'home.tip.fees': 'Die Plattformgebühr ist bis zum 31. Dez. 2026 0 % — kein Promo. Lightning-Routinggebühren fallen weiter an und gehen nicht an Katoa.',
    'home.shot.demoTitle': 'Beispiel-Produktvorschau',
    'home.shot.progressTip': 'Demo-Katalogbeispiel — 65 % eines Ziels von 5.000.000 Sats. Dieselbe Fortschritts-UI wie eine echte Wunschliste. Katoa verwahrt oder verarbeitet die Sats nicht; sie gehen ins Lightning-Wallet des Creators.',
  },
  ja: {
    'home.badge': 'Bitcoinネイティブ • 非カストディアル • オープンソース',
    'home.keep': '手取り',
    'home.percent': '100%',
    'home.earnings': 'をキープ',
    'home.subtitle': '手数料ゼロ、プライバシー重視のBitcoinコマース。',
    'home.subtitle2': 'クリエイター収益化のプロトコルレベルアップグレード。',
    'home.cta.start': '100%を稼ぎ始める',
    'home.cta.compare': '他社と比較',
    'home.stats.processed': '処理済み',
    'home.stats.countries': 'カ国',
    'home.stats.fees': '手数料2026年まで0%',
    'home.protocol': 'プロトコル',
    'home.builtDifferent': '違う設計',
    'home.fees.title': '手数料で数千ドル失うのをやめよう',
    'home.fees.subtitle': 'KATOAと従来のプラットフォームで手取りを比較。',
    'home.howItWorks.title': '使い方',
    'home.howItWorks.subtitle': '登録から最初のsatまで4ステップ',
    'home.step1.title': 'アカウント作成',
    'home.step1.desc': 'メール、Google、Nostrで登録 — 銀行口座不要。',
    'home.step2.title': 'ウィッシュリスト作成',
    'home.step2.desc': '目標、メディア、ストーリーを追加。sats価格を設定。',
    'home.step3.title': 'リンクを共有',
    'home.step3.desc': 'どこでもページを投稿。QRコードで支援しやすく。',
    'home.step4.title': 'Bitcoinを受け取る',
    'home.step4.desc': '受信機能が提供されれば、Lightningで数秒で着金します（ロードマップ：進行中）。',
    'home.join.title': 'Bitcoinクリエイター経済に参加',
    'home.join.subtitle': '世界中のクリエイターが手数料をやめ、経済的自由を手に入れています。',
    'home.join.cta': '無料で始める',
    'home.join.browse': 'キャンペーンを見る',
    'home.howItWorks.eyebrow': '使い方',
    'home.pillar1.title': '数秒で着金',
    'home.pillar1.desc': 'Bitcoin Lightningで数秒着金。何週間も資金を止める決済代行はありません。',
    'home.pillar1.old': '7〜14日',
    'home.pillar1.next': '受信提供開始後',
    'home.pillar2.title': '本当にグローバル',
    'home.pillar2.desc': '195か国以上のクリエイター。銀行口座も地理的ゲートも、Stripe除外もなし。',
    'home.pillar2.old': '約10か国',
    'home.pillar2.next': '195か国以上',
    'home.pillar3.title': '設計からプライバシー',
    'home.pillar3.desc': 'Nostrネイティブの身元、最小限のデータ、より私的な支払いへの道。',
    'home.pillar3.old': 'プラットフォーム所有',
    'home.pillar3.next': '鍵はあなたのもの',
    'home.bento.traditional': '従来',
    'home.bento.katoa': 'KATOA',
    'home.cta.note': 'プラットフォーム手数料0%（2026年12月31日まで）— Give A Bitとコミュニティの支援で運営。Lightning手数料は小さく、多くの場合サポーター負担。KATOAは残高を預かりません。',
    'home.cta.launch': '公開中',
    'home.cta.processed': '処理済み',
    'home.cta.fees': '手数料',
    'home.metaTitle': 'KATOA — 手数料ゼロのBitcoinクリエイタープラットフォーム',
    'home.metaDesc': '収益の100%を手元に。プライバシー重視のウィッシュリストと寄付をBitcoin Lightningで。手数料0%（2026年12月31日まで）。',
    'home.chip.creators': 'クリエイター',
    'home.chip.zeroFees': '手数料0%',
    'home.chip.lightning': 'Lightning',
    'home.tip.eyebrow': 'Katoa — 手数料ゼロ・非カストディアルのBitcoinネイティブなクリエイター基盤。Give A Bitファミリーの一員です。',
    'home.tip.volume': 'ラベル付きのプロダクト指標です。Katoaが預かったり処理したりするsatsではありません。支払いはLightning上のピアツーピアです。ライブ計数が揃うまでサンプル数値はデモと表示されます。',
    'home.tip.countries': 'Bitcoin Lightningはインターネットがある場所で動きます。Katoaは開始に銀行もKYCも求めません。活動する地域の法令に従ってください。',
    'home.tip.fees': 'プラットフォーム手数料は2026年12月31日まで0%です。プロモではありません。Lightningのルーティング手数料は別途発生し、Katoaには支払われません。',
    'home.shot.demoTitle': 'サンプルのプロダクトプレビュー',
    'home.shot.progressTip': 'デモカタログのサンプル — 目標5,000,000 satsの65%。実際のウィッシュリストと同じ進捗UIです。Katoaはsatsを預かりません。クリエイターのLightningウォレットへ直接送られます。',
  },
  zh: {
    'home.badge': '比特币原生 • 非托管 • 开源',
    'home.keep': '保留',
    'home.percent': '100%',
    'home.earnings': '的收入',
    'home.subtitle': '零费用、隐私优先的比特币商务。',
    'home.subtitle2': '创作者变现的协议级升级。',
    'home.cta.start': '开始赚取100%',
    'home.cta.compare': '查看平台对比',
    'home.stats.processed': '已处理',
    'home.stats.countries': '个国家',
    'home.stats.fees': '零费用至2026年',
    'home.protocol': '协议',
    'home.builtDifferent': '与众不同',
    'home.fees.title': '别再因手续费损失数千美元',
    'home.fees.subtitle': '看看KATOA与传统平台相比你能保留多少。',
    'home.howItWorks.title': '如何运作',
    'home.howItWorks.subtitle': '从注册到收到第一个聪，只需四步',
    'home.step1.title': '创建账户',
    'home.step1.desc': '用邮箱、Google或Nostr注册 — 无需银行账户。',
    'home.step2.title': '建立心愿单',
    'home.step2.desc': '添加目标、媒体和故事。为每项设置聪价格。',
    'home.step3.title': '分享链接',
    'home.step3.desc': '在任何地方发布页面。二维码让支持者更容易找到你。',
    'home.step4.title': '接收比特币',
    'home.step4.desc': '接收功能上线后，通过闪电网络在数秒内到账（路线图：进行中）。',
    'home.join.title': '加入比特币创作者经济',
    'home.join.subtitle': '全球创作者正在停止支付平台费用，获得真正的财务自由。',
    'home.join.cta': '免费开始',
    'home.join.browse': '浏览活动',
    'home.howItWorks.eyebrow': '如何使用',
    'home.pillar1.title': '数秒结算',
    'home.pillar1.desc': '资金通过比特币闪电网络数秒到账。没有支付公司把钱扣上几周。',
    'home.pillar1.old': '7–14 天',
    'home.pillar1.next': '收款上线后',
    'home.pillar2.title': '真正全球',
    'home.pillar2.desc': '覆盖 195+ 个国家的创作者。无需银行账户，没有地域门槛，也没有 Stripe 排除。',
    'home.pillar2.old': '约 10 个国家',
    'home.pillar2.next': '195+ 个国家',
    'home.pillar3.title': '从设计保护隐私',
    'home.pillar3.desc': 'Nostr 原生身份、最少数据，以及更隐私支付的路线图。',
    'home.pillar3.old': '平台所有',
    'home.pillar3.next': '密钥归你',
    'home.bento.traditional': '传统平台',
    'home.bento.katoa': 'KATOA',
    'home.cta.note': '平台抽成 0%（截至2026年12月31日），由 Give A Bit 和社区支持承担。闪电网络费用很低，通常由支持者支付。KATOA 从不托管你的余额。',
    'home.cta.launch': '已上线',
    'home.cta.processed': '已处理',
    'home.cta.fees': '费用',
    'home.metaTitle': 'KATOA — 零费用比特币创作者平台',
    'home.metaDesc': '保留收入的 100%。隐私优先的心愿单与捐赠，走比特币闪电网络。平台抽成 0%（至2026年12月31日）。',
    'home.chip.creators': '创作者',
    'home.chip.zeroFees': '0% 费用',
    'home.chip.lightning': 'Lightning',
    'home.tip.eyebrow': 'Katoa — 零费用、非托管、比特币原生的创作者平台。Give A Bit 家族的一员。',
    'home.tip.volume': '带标注的产品指标 — 不是 Katoa 托管或处理的聪。付款在闪电网络上点对点进行。在真实计数上线前，样本数字会标为演示。',
    'home.tip.countries': '只要有互联网，比特币闪电网络就能用。Katoa 起步不要求银行或 KYC。请遵守你所在地的法律。',
    'home.tip.fees': '平台抽成 0%（至2026年12月31日）— 不是促销。闪电网络路由费仍然存在，且不付给 Katoa。',
    'home.shot.demoTitle': '示例产品预览',
    'home.shot.progressTip': '演示目录样本 — 5,000,000 聪目标的 65%。与真实心愿单相同的进度界面。Katoa 不托管也不处理这些聪；它们直接进入创作者的闪电钱包。',
  },
};

const navUiStrings = {
  en: {
    'nav.comparison': 'Why KATOA?',
    'nav.home': 'Home',
    'nav.more': 'More',
    'nav.signOut': 'Sign Out',
    'nav.faq': 'FAQ',
    'nav.menu': 'KATOA Menu',
    'nav.getStarted': 'Get Started Free',
    'nav.changeLanguage': 'Change language',
    'nav.chooseLanguage': 'Choose Language',
    'nav.templates': 'Templates',
    'nav.security': 'Security',
    'nav.roadmap': 'Roadmap',
    'nav.messages': 'Messages',
    'nav.currency': 'Currency',
    'tipMenu.title': 'Send a tip',
    'tipMenu.help': 'Quick sats — 21k, 50k, or custom. Goes straight to the creator.',
    'tipMenu.custom': 'Custom sats',
    'tipMenu.go': 'Tip',
    'tipMenu.presetsLabel': 'Your tip presets (settings)',
    'favorites.copyPack': 'Copy pack',
    'favorites.sharePack': 'Share pack',
    'favorites.download': 'Download',
    'favorites.count': 'saved',
    'favorites.copied': 'Favorites pack copied',
    'favorites.copyFailed': 'Could not copy',
    'favorites.shareTitle': 'My KATOA favorites',
    'favorites.exportHeader': 'My favorite KATOA creators / wishlists',
    'favorites.exportFooter': 'Zero platform fees — katoa.org',
    'visibility.public': 'Public',
    'visibility.private': 'Unlisted',
    'visibility.draft': 'Draft',
    'visibility.help': 'Who can see this wishlist',
    'messages.blocked': 'Blocked',
    'messages.block': 'Block',
    'messages.unblock': 'Unblock',
    'messages.blockedList': 'Blocked accounts',
    'messages.unread': 'unread',
    'explore.verticals': 'Creator types',
    'explore.allVerticals': 'All types',
    'pwa.creatorTitle': 'Add KATOA to your phone',
    'pwa.creatorBody': 'Save the creator hub for one-tap tips and wishlists. Share your page like a link-in-bio — without the fee cut.',
    'pwa.creatorCopy': 'Copy creator link',
    'pwa.creatorOpen': 'Open playbook',
    'pwa.creatorCopied': 'Creator link copied',
    'creators.pageTitle': 'For creators — keep 100%',
    'creators.pageSubtitle': 'Tips & wishlists without the platform tax — for models, athletes, coaches, meal planners, and every creator.',
    'creators.guidelinesTitle': 'Creator guidelines',
    'creator.feedTitle': 'Latest posts',
    'creator.posts': 'posts',
    'creator.likes': 'likes',
    'creator.subscribers': 'subscribers',
    'creator.subscribe': 'Subscribe',
    'creator.subscribed': 'Subscribed',
    'creator.locked': 'Subscribe to unlock',
    'creator.unlockFor': 'Unlock for',
    'creator.pinned': 'Pinned',
    'creator.tip': 'Tip',
    'creator.comments': 'comments',
    'creator.unlock': 'Unlock',
    'creator.like': 'Like',
    'creator.unlike': 'Unlike',
    'creator.liked': 'Liked!',
    'creator.ppvUnlocked': 'Post unlocked!',
    'creator.newDrop': 'NEW',
    'creator.newDrops': 'new',
    'creator.commentPlaceholder': 'Add a comment…',
    'creator.sendComment': 'Send comment',
    'creator.commentPosted': 'Comment posted',
    'creator.youreSubscribed': "You're subscribed",
    'creator.tier': 'Tier',
    'creator.since': 'since',
    'creator.unsubscribe': 'Unsubscribe',
    'creator.unsubscribed': 'Unsubscribed',
    'creator.discoverEyebrow': 'Discover creators',
    'creator.discoverTitle': 'Find your next favorite creator',
    'creator.discoverSubtitle': 'Trending and new — Bitcoin-native, 0% fees.',
    'creator.followers': 'followers',
    'creator.searchPlaceholder': 'Search by name, tag or city…',
    'creator.filterByVertical': 'Filter by vertical',
    'creator.allVertical': 'All',
    'creator.noResults': 'No creators match your search',
    'creator.trending': 'Trending',
    'creator.newCreators': 'New this week',
    'creator.browseAll': 'Browse all creators',
    'creator.profile': 'Creator profile',
    'creator.profileNotFound': 'Creator not found',
    'creator.profileNotFoundDesc': "This creator doesn't exist or has been removed.",
    'creator.wishlists': 'Wishlists',
    'creator.satsRaised': 'Sats raised',
    'creator.exploreCreators': 'Explore creators',
    'messages.pageTitle': 'Private messages',
    'messages.pageSubtitle': 'Optional encrypted DMs with fans via Nostr. Opt-in only.',
    'messages.enable': 'Enable private messages on this device',
    'a11y.messagesMain': 'Private messages inbox',
    'a11y.creatorsMain': 'Creator playbook content',
    'nav.btcPrice': 'BTC Price',
    'nav.tapProfile': 'Tap to view profile',
    'nav.closeMenu': 'Close menu',
    'nav.faqLink': 'FAQ',
    'demo.banner': 'Demo mode — explore with sample data. No account required.',
    'demo.tryWishlist': 'Try demo wishlist →',
    'pwa.installTitle': 'Add KATOA to Home Screen',
    'pwa.installSubtitle': 'Quick access, app-like experience.',
    'pwa.install': 'Install',
    'pwa.dismiss': 'Dismiss',
    'changelog.gotIt': 'Got it',
    'changelog.whatsNew': "What's new in",
    'common.backHome': 'Back to Home',
    'wishlist.title': 'Wishlist',
    'auth.passwordWeak': 'Weak password',
    'auth.passwordMedium': 'Medium strength',
    'auth.passwordStrong': 'Strong password',
    'share.button': 'Share',
    'share.title': 'Share this wishlist',
    'share.twitter': 'Share on Twitter',
    'share.copy': 'Copy link',
    'share.copied': 'Link copied!',
    'share.promo': 'Support this wishlist with Bitcoin!',
    'share.facebook': 'Share on Facebook',
    'share.linkedin': 'Share on LinkedIn',
    'share.whatsapp': 'Share on WhatsApp',
    'share.telegram': 'Share on Telegram',
    'share.reddit': 'Share on Reddit',
    'a11y.closeDialog': 'Close dialog',
    'a11y.dialog': 'Dialog',
    'a11y.navigatedTo': 'Navigated to',
    'errorBoundary.title': 'Something went wrong',
    'errorBoundary.message': 'Try refreshing the page. If the problem persists, head home.',
    'errorBoundary.tryAgain': 'Try again',
    'errorBoundary.goHome': 'Go Home',
    'payment.tab.lightning': 'Lightning',
    'payment.tab.onchain': 'On-chain',
    'payment.tab.nostr': 'Nostr Zap',
    'payment.tabList': 'Payment method',
    'nav.beta': 'Beta',
    'nav.betaTitle': 'KATOA is in active development',
    'nav.route.home': 'Home',
    'nav.route.explore': 'Explore',
    'nav.route.wishlist': 'Wishlist',
    'nav.route.settings': 'Settings',
    'nav.route.about': 'About',
    'nav.route.contact': 'Contact',
    'nav.route.faq': 'FAQ',
    'nav.route.pricing': 'Pricing',
    'nav.route.comparison': 'Comparison',
    'nav.route.pitch': 'Pitch',
    'nav.route.terms': 'Terms',
    'nav.route.privacy': 'Privacy',
    'nav.route.project': 'Project',
    'footer.copyFailed': 'Could not copy address',
    'ticker.summary': 'Recent creator wins: zero fees, instant Lightning payouts, global support.',
    'explore.loadingMore': 'Loading more projects',
    'auth.metaTitle': 'Sign In',
    'auth.metaDesc': 'Sign in or create your free KATOA account. Email, Google, or Nostr — start receiving Bitcoin in minutes.',
    'gift.invalidAmount': 'Enter a valid amount in sats (greater than 0)',
    'home.statsUnavailable': 'Live counters unavailable right now — no vanity estimates shown.',
    'home.statsSample': 'Stats below are from the public sample catalog (labeled demo) until live product counters are wired.',
    'home.stats.sampleRaised': 'Sample sats raised',
    'trust.proof.aria': 'Trust and proof',
    'trust.proof.zeroFees': '0% platform fees',
    'trust.proof.nonCustodial': 'Non-custodial',
    'trust.proof.openSource': 'Open source',
    'donate.tipAmount': 'Suggested amount',
    'donate.customAmount': 'Custom amount (sats)',
    'donate.openInWallet': 'Open in wallet',
    'donate.openDefaultWallet': 'Default wallet',
    'donate.scanTitle': 'Scan to donate',
    'donate.scanSubtitle': 'Any Bitcoin wallet · self-custody',
    'donate.addressLabel': 'Bitcoin address',
    'donate.tapCopy': 'Tap to copy address',
    'donate.copied': 'Copied to clipboard',
    'donate.copyAddress': 'Copy address',
    'donate.share': 'Share',
    'donate.thanks': 'Your sats keep KATOA free and open-source. Thank you.',
    'donate.safeHarbour': 'Educational software. You send sats directly to the recipient — KATOA never holds your funds.',
    'donate.stickyFund': 'Send sats',
    'donate.stickyShare': 'Share',
    'product.importTitle': 'Add from product link',
    'product.importHelp':
      'Paste Amazon, Nike, Etsy, Shopify, or any shop URL. Supporters can fund in sats or buy the item for you.',
    'product.invalidUrl': 'Enter a valid product link (https://…)',
    'product.parseFailed': 'Could not fully read that page — edit details, then add. The buy link still works.',

    'share.nostr': 'Share via Nostr note',
    'share.native': 'System share',
    'empty.defaultAction': 'Explore projects',
    'security.title': 'Security & custody',
    'security.metaDesc': 'How KATOA handles keys, funds, and data — non-custodial by design.',
    'roadmap.inApp.title': 'Public roadmap',
    'roadmap.inApp.meta': 'What shipped and what is next for KATOA.',
    'faq.trust.heading': 'Trust & fees',
    'demo.badge': 'Demo',
    'demo.badgeTitle': 'Sample content for preview — not a live creator account',
    'contact.messageCopied': 'Message copied — paste into your email if no mail app opens',
    'map.title': 'Bitcoin Map',
    'map.subtitle': 'Merchants + KATOA creator projects · toggle layers',
    'map.merchants': 'Merchants',
    'map.katoa': 'KATOA',
    'map.legendMerchant': 'BTC Map merchant',
    'map.legendKatoa': 'KATOA project',
    'map.turnOnLayer': 'Turn on a layer to see the map',
    'map.locateMe': 'My location',
    'map.fitAll': 'Fit all pins',
    'map.showAllLayers': 'Show all layers',
    'map.merchantsUnavailable': 'Merchants unavailable',
    'map.expand': 'Expand map',
    'map.collapse': 'Collapse map',
    'map.search': 'Search merchants and areas',
    'map.searchPlaceholder': 'Search BTC Map…',
    'map.searchClear': 'Clear search',
    'map.searchPlace': 'Merchant',
    'map.searchArea': 'Area',
    'map.popupMerchant': 'BTC Map merchant',
    'map.popupBoosted': 'Boosted',
    'map.popupHours': 'Hours',
    'map.popupVerified': 'Verified',
    'map.popupWebsite': 'Website',
    'map.popupViewOnMap': 'View on BTC Map',
    'map.popupLoading': 'Loading details…',
    'map.popupComment': 'comment',
    'map.popupComments': 'comments',
    'map.popupKatoa': 'KATOA creator project',
    'map.popupSatsRaised': 'sats raised',
    'map.popupViewWishlist': 'View wishlist',
    'map.categoryLabel': 'Filter merchants by category',
    'map.categoryAll': 'All',
    'map.categoryFood': 'Food & drink',
    'map.categoryShopping': 'Shopping',
    'map.categoryStay': 'Stay',
    'map.categoryServices': 'Services',
    'map.categoryFun': 'Fun & culture',
    'map.categoryTravel': 'Travel',
    'map.events': 'Events',
    'map.areasHere': 'Areas here',
    'map.activity': 'Recent activity',
    'map.activityAdded': 'place added',
    'map.activityUpdated': 'place updated',
    'map.activityDeleted': 'place removed',
    'map.activityCommented': 'new comment',
    'map.activityBoosted': 'boosted',
    'map.shareView': 'Share map view',
    'map.shareCopied': 'Map link copied',
    'map.shareFailed': 'Copy failed',
    'map.contribute': 'Contribute',
    'map.addPlace': 'Add a place',
    'map.suggestEdit': 'Suggest an edit',
    'map.details': 'Details',
    'map.drawerDirections': 'Directions',
    'map.drawerReport': 'Report an issue',
    'map.drawerViewOsm': 'View on OpenStreetMap',
    'map.drawerComments': 'Comments',
    'map.drawerClose': 'Close details',
    'map.loadMore': 'Load more here',
    'map.loading': 'Loading…',
    'map.noActivity': 'No recent activity near here yet',
    'ticker.1': 'Creator in Nigeria saved $2,400/yr',
    'ticker.2': 'Skate park in Medellín — 65% funded',
    'ticker.3': '0% fees — 100% to creators',
    'ticker.4': 'Lightning payout in 3 seconds',
    'ticker.5': '195+ countries supported',
    'trust.fees': 'Platform fees',
    'trust.payout': 'Lightning payout',
    'trust.countries': 'Countries',
    'trust.earnings': 'Creator earnings',
    'trust.aria': 'Platform highlights',
    'trust.tip.fees': 'Platform fee is 0% through 31 Dec 2026 — Katoa takes no cut. Lightning network routing fees still exist and are not paid to Katoa.',
    'trust.tip.payout': 'Katoa does not run a payout queue. When a supporter pays your Lightning invoice, sats settle to your wallet — typically in seconds, not banking days.',
    'trust.tip.countries': 'Bitcoin is global wherever the internet reaches. Katoa does not geo-block creators. Follow the law where you operate. No fake traction — 195+ is the Bitcoin map, not a Katoa user count.',
    'trust.tip.earnings': '0% platform fee, non-custodial: sats go to your wallet, not Katoa\'s. You still pay whatever your wallet or Lightning path charges.',
    'progress.goalReached': 'Goal Reached!',
    'progress.ofGoal': 'of goal reached',
    'breadcrumb.home': 'Home',
    'explore.map': 'Map',
    'footer.company': 'Company',
    'footer.product': 'Product',
    'footer.legal': 'Legal',
    'footer.contributors': 'Contributors',
    'footer.openRoles': 'Open roles',
    'footer.jobsTagline': 'Build sovereign gifting infrastructure · paid in sats or fiat',
    'footer.openRolesCount': 'open',
    'footer.apply': 'Apply',
    'footer.applyEmailPrefix': 'Applications:',
    'footer.apis': 'Developer APIs',
    'footer.press': 'Press kit',
    'footer.meetup': 'Meetup kit',
    'footer.creators': 'For creators',
    'footer.guidelines': 'Guidelines',
    'footer.caseStudies': 'Case studies',
    'footer.nip05': 'NIP-05 claim',
    'footer.videoCreators': 'Video creators',
    'footer.bounty': 'Bug bounty',
    'footer.donateSats': 'Donate sats',
    'explore.mapOpened': 'Map opened',
    'explore.mapClosed': 'Map hidden',
    'explore.loadingResults': 'Loading projects',
    'explore.resultsCount': '${count} projects found',
    'explore.resultsCountOne': '1 project found',
    'media.mute': 'Mute video',
    'media.unmute': 'Unmute video',
  },
  es: {
    'nav.comparison': '¿Por qué KATOA?',
    'nav.home': 'Inicio',
    'nav.more': 'Más',
    'nav.signOut': 'Cerrar sesión',
    'nav.faq': 'FAQ',
    'nav.menu': 'Menú KATOA',
    'nav.getStarted': 'Empezar gratis',
    'nav.changeLanguage': 'Cambiar idioma',
    'nav.chooseLanguage': 'Elegir idioma',
    'nav.currency': 'Moneda',
    'nav.messages': 'Mensajes',
    'tipMenu.title': 'Enviar propina',
    'tipMenu.help': 'Sats rápidos — 21k, 50k o personalizado. Directo al creador.',
    'tipMenu.custom': 'Sats personalizados',
    'tipMenu.go': 'Enviar',
    'favorites.copyPack': 'Copiar pack',
    'favorites.sharePack': 'Compartir pack',
    'favorites.download': 'Descargar',
    'favorites.count': 'guardados',
    'favorites.copied': 'Pack copiado',
    'favorites.copyFailed': 'No se pudo copiar',
    'favorites.shareTitle': 'Mis favoritos en KATOA',
    'favorites.exportHeader': 'Mis favoritos en KATOA',
    'favorites.exportFooter': 'Cero comisiones — katoa.org',
    'visibility.public': 'Público',
    'visibility.private': 'No listado',
    'visibility.draft': 'Borrador',
    'visibility.help': 'Quién puede ver esta lista',
    'messages.blocked': 'Bloqueado',
    'messages.block': 'Bloquear',
    'messages.unblock': 'Desbloquear',
    'messages.blockedList': 'Cuentas bloqueadas',
    'messages.unread': 'sin leer',
    'explore.verticals': 'Tipos de creador',
    'explore.allVerticals': 'Todos',
    'pwa.creatorTitle': 'Añade KATOA a tu móvil',
    'pwa.creatorBody': 'Guarda el hub de creadores para tips y listas en un toque.',
    'pwa.creatorCopy': 'Copiar enlace creador',
    'pwa.creatorOpen': 'Abrir guía',
    'pwa.creatorCopied': 'Enlace copiado',
    'creators.pageTitle': 'Para creadores — 100% tuyo',
    'creators.pageSubtitle': 'Propinas y listas sin impuesto de plataforma.',
    'creators.guidelinesTitle': 'Guías para creadores',
    'creator.feedTitle': 'Últimas publicaciones',
    'creator.posts': 'publicaciones',
    'creator.likes': 'me gusta',
    'creator.subscribers': 'suscriptores',
    'creator.subscribe': 'Suscribirse',
    'creator.subscribed': 'Suscrito',
    'creator.locked': 'Suscríbete para desbloquear',
    'creator.unlockFor': 'Desbloquear por',
    'creator.pinned': 'Fijado',
    'creator.tip': 'Propina',
    'creator.comments': 'comentarios',
    'creator.unlock': 'Desbloquear',
    'creator.like': 'Me gusta',
    'creator.unlike': 'Ya no me gusta',
    'creator.liked': '¡Te gusta!',
    'creator.ppvUnlocked': '¡Publicación desbloqueada!',
    'creator.newDrop': 'NUEVO',
    'creator.newDrops': 'nuevo',
    'creator.commentPlaceholder': 'Añade un comentario…',
    'creator.sendComment': 'Enviar comentario',
    'creator.commentPosted': 'Comentario enviado',
    'creator.youreSubscribed': 'Estás suscrito',
    'creator.tier': 'Nivel',
    'creator.since': 'desde',
    'creator.unsubscribe': 'Cancelar suscripción',
    'creator.unsubscribed': 'Suscripción cancelada',
    'creator.discoverEyebrow': 'Descubre creadores',
    'creator.discoverTitle': 'Encuentra a tu próximo creador favorito',
    'creator.discoverSubtitle': 'Tendencias y novedades: nativo de Bitcoin, 0% de comisiones.',
    'creator.followers': 'seguidores',
    'creator.searchPlaceholder': 'Busca por nombre, etiqueta o ciudad…',
    'creator.filterByVertical': 'Filtrar por vertical',
    'creator.allVertical': 'Todos',
    'creator.noResults': 'Ningún creador coincide con tu búsqueda',
    'creator.trending': 'Tendencias',
    'creator.newCreators': 'Nuevos esta semana',
    'creator.browseAll': 'Ver todos los creadores',
    'creator.profile': 'Perfil del creador',
    'creator.profileNotFound': 'Creador no encontrado',
    'creator.profileNotFoundDesc': 'Este creador no existe o fue eliminado.',
    'creator.wishlists': 'Listas',
    'creator.satsRaised': 'Sats recaudados',
    'creator.exploreCreators': 'Explorar creadores',
    'messages.pageTitle': 'Mensajes privados',
    'messages.pageSubtitle': 'DMs cifrados opcionales vía Nostr.',
    'messages.enable': 'Activar mensajes privados en este dispositivo',
    'a11y.messagesMain': 'Bandeja de mensajes privados',
    'a11y.creatorsMain': 'Contenido para creadores',
    'nav.btcPrice': 'Precio BTC',
    'nav.tapProfile': 'Toca para ver perfil',
    'nav.closeMenu': 'Cerrar menú',
    'nav.faqLink': 'FAQ',
    'demo.banner': 'Modo demo — explora con datos de ejemplo. Sin cuenta.',
    'demo.tryWishlist': 'Probar wishlist demo →',
    'pwa.installTitle': 'Añadir KATOA a inicio',
    'pwa.installSubtitle': 'Acceso rápido, experiencia tipo app.',
    'pwa.install': 'Instalar',
    'pwa.dismiss': 'Descartar',
    'changelog.gotIt': 'Entendido',
    'changelog.whatsNew': 'Novedades en',
    'common.backHome': 'Volver al inicio',
    'wishlist.title': 'Wishlist',
    'auth.passwordWeak': 'Contraseña débil',
    'auth.passwordMedium': 'Contraseña media',
    'auth.passwordStrong': 'Contraseña fuerte',
    'share.button': 'Compartir',
    'share.title': 'Compartir esta wishlist',
    'share.twitter': 'Compartir en Twitter',
    'share.copy': 'Copiar enlace',
    'share.copied': '¡Enlace copiado!',
    'map.title': 'Mapa Bitcoin',
    'map.subtitle': 'Comercios + proyectos KATOA · activar capas',
    'map.merchants': 'Comercios',
    'map.katoa': 'KATOA',
    'map.legendMerchant': 'Comercio BTC Map',
    'map.legendKatoa': 'Proyecto KATOA',
    'map.turnOnLayer': 'Activa una capa para ver el mapa',
    'map.locateMe': 'Mi ubicación',
    'map.fitAll': 'Ajustar todos los pins',
    'map.showAllLayers': 'Mostrar todas las capas',
    'map.merchantsUnavailable': 'Comercios no disponibles',
    'map.expand': 'Expandir mapa',
    'map.collapse': 'Contraer mapa',
    'map.search': 'Buscar comercios y áreas',
    'map.searchPlaceholder': 'Buscar en BTC Map…',
    'map.searchClear': 'Borrar búsqueda',
    'map.searchPlace': 'Comercio',
    'map.searchArea': 'Área',
    'map.popupMerchant': 'Comercio BTC Map',
    'map.popupBoosted': 'Destacado',
    'map.popupHours': 'Horario',
    'map.popupVerified': 'Verificado',
    'map.popupWebsite': 'Sitio web',
    'map.popupViewOnMap': 'Ver en BTC Map',
    'map.popupLoading': 'Cargando detalles…',
    'map.popupComment': 'comentario',
    'map.popupComments': 'comentarios',
    'map.popupKatoa': 'Proyecto de creador KATOA',
    'map.popupSatsRaised': 'sats recaudados',
    'map.popupViewWishlist': 'Ver wishlist',
    'map.categoryLabel': 'Filtrar comercios por categoría',
    'map.categoryAll': 'Todos',
    'map.categoryFood': 'Comida y bebida',
    'map.categoryShopping': 'Compras',
    'map.categoryStay': 'Alojamiento',
    'map.categoryServices': 'Servicios',
    'map.categoryFun': 'Ocio y cultura',
    'map.categoryTravel': 'Viajes',
    'map.events': 'Eventos',
    'map.areasHere': 'Áreas aquí',
    'map.activity': 'Actividad reciente',
    'map.activityAdded': 'comercio añadido',
    'map.activityUpdated': 'comercio actualizado',
    'map.activityDeleted': 'comercio eliminado',
    'map.activityCommented': 'nuevo comentario',
    'map.activityBoosted': 'destacado',
    'map.shareView': 'Compartir vista del mapa',
    'map.shareCopied': 'Enlace del mapa copiado',
    'map.shareFailed': 'No se pudo copiar',
    'map.contribute': 'Contribuir',
    'map.addPlace': 'Añadir un comercio',
    'map.suggestEdit': 'Sugerir una edición',
    'map.details': 'Detalles',
    'map.drawerDirections': 'Indicaciones',
    'map.drawerReport': 'Reportar un problema',
    'map.drawerViewOsm': 'Ver en OpenStreetMap',
    'map.drawerComments': 'Comentarios',
    'map.drawerClose': 'Cerrar detalles',
    'map.loadMore': 'Cargar más aquí',
    'map.loading': 'Cargando…',
    'map.noActivity': 'Aún no hay actividad reciente cerca',
    'ticker.1': 'Creador en Nigeria ahorró $2,400/año',
    'ticker.2': 'Skate park en Medellín — 65% financiado',
    'ticker.3': '0% comisiones — 100% para creadores',
    'ticker.4': 'Pago Lightning en 3 segundos',
    'ticker.5': '195+ países soportados',
    'trust.fees': 'Comisiones de plataforma',
    'trust.payout': 'Pago Lightning',
    'trust.countries': 'Países',
    'trust.earnings': 'Ganancias del creador',
    'trust.aria': 'Destacados de la plataforma',
    'trust.tip.fees': 'La comisión de plataforma es 0% hasta el 31 dic 2026 — Katoa no se lleva nada. Las comisiones de enrutado Lightning existen y no se pagan a Katoa.',
    'trust.tip.payout': 'Katoa no opera una cola de pagos. Cuando un fan paga tu factura Lightning, los sats llegan a tu cartera — en segundos, no en días bancarios.',
    'trust.tip.countries': 'Bitcoin es global donde llega internet. Katoa no bloquea creadores por geografía. Cumple la ley donde operes. 195+ es el mapa de Bitcoin, no un recuento de usuarios de Katoa.',
    'trust.tip.earnings': '0% de comisión, no custodial: los sats van a tu cartera, no a la de Katoa. Sigues pagando lo que cobre tu cartera o la ruta Lightning.',
    'trust.proof.aria': 'Confianza y pruebas',
    'trust.proof.zeroFees': '0% de comisión de plataforma',
    'trust.proof.nonCustodial': 'No custodial',
    'trust.proof.openSource': 'Código abierto',
    'progress.goalReached': '¡Meta alcanzada!',
    'breadcrumb.home': 'Inicio',
    'explore.map': 'Mapa',
    'footer.company': 'Empresa',
    'footer.product': 'Producto',
    'footer.legal': 'Legal',
    'footer.contributors': 'Colaboradores',
    'footer.openRoles': 'Vacantes',
    'footer.jobsTagline': 'Construye infraestructura soberana de regalos · pago en sats o fiat',
    'footer.openRolesCount': 'abiertas',
    'footer.apply': 'Aplicar',
    'footer.applyEmailPrefix': 'Postulaciones:',
    'footer.apis': 'APIs para desarrolladores',
    'footer.press': 'Kit de prensa',
    'footer.meetup': 'Kit de meetup',
    'footer.creators': 'Para creadores',
    'footer.guidelines': 'Directrices',
    'footer.caseStudies': 'Casos de estudio',
    'footer.nip05': 'Reclamar NIP-05',
    'footer.videoCreators': 'Creadores de video',
    'footer.bounty': 'Bug bounty',
    'footer.donateSats': 'Donar sats',
    'explore.mapOpened': 'Mapa abierto',
    'explore.mapClosed': 'Mapa oculto',
    'explore.loadingResults': 'Cargando proyectos',
    'explore.resultsCount': '${count} proyectos encontrados',
    'explore.resultsCountOne': '1 proyecto encontrado',
    'media.mute': 'Silenciar video',
    'media.unmute': 'Activar sonido del video',
    'auth.metaTitle': 'Iniciar sesión',
    'auth.metaDesc': 'Inicia sesión o crea tu cuenta KATOA gratis. Email, Google o Nostr — empieza a recibir Bitcoin en minutos.',
    'security.metaDesc': 'Cómo KATOA maneja claves, fondos y datos — no custodial por diseño.',
  },
  pt: {
    'nav.comparison': 'Por que KATOA?',
    'nav.home': 'Início',
    'nav.more': 'Mais',
    'nav.signOut': 'Sair',
    'nav.faq': 'FAQ',
    'nav.menu': 'Menu KATOA',
    'nav.getStarted': 'Começar grátis',
    'nav.changeLanguage': 'Mudar idioma',
    'nav.chooseLanguage': 'Escolher idioma',
    'nav.currency': 'Moeda',
    'nav.messages': 'Mensagens',
    'tipMenu.title': 'Enviar gorjeta',
    'tipMenu.help': 'Sats rápidos — 21k, 50k ou personalizado. Direto ao criador.',
    'tipMenu.custom': 'Sats personalizados',
    'tipMenu.go': 'Enviar',
    'favorites.copyPack': 'Copiar pack',
    'favorites.sharePack': 'Compartilhar pack',
    'favorites.download': 'Baixar',
    'favorites.count': 'salvos',
    'favorites.copied': 'Pack copiado',
    'favorites.copyFailed': 'Não foi possível copiar',
    'favorites.shareTitle': 'Meus favoritos no KATOA',
    'favorites.exportHeader': 'Meus favoritos no KATOA',
    'favorites.exportFooter': 'Zero taxas — katoa.org',
    'visibility.public': 'Público',
    'visibility.private': 'Não listado',
    'visibility.draft': 'Rascunho',
    'visibility.help': 'Quem pode ver esta lista',
    'messages.blocked': 'Bloqueado',
    'messages.block': 'Bloquear',
    'messages.unblock': 'Desbloquear',
    'messages.blockedList': 'Contas bloqueadas',
    'messages.unread': 'não lidas',
    'explore.verticals': 'Tipos de criador',
    'explore.allVerticals': 'Todos',
    'pwa.creatorTitle': 'Adicione o KATOA ao celular',
    'pwa.creatorBody': 'Salve o hub de criadores para tips e listas em um toque.',
    'pwa.creatorCopy': 'Copiar link do criador',
    'pwa.creatorOpen': 'Abrir guia',
    'pwa.creatorCopied': 'Link copiado',
    'creators.pageTitle': 'Para criadores — fique com 100%',
    'creators.pageSubtitle': 'Tips e listas sem imposto de plataforma.',
    'creators.guidelinesTitle': 'Diretrizes para criadores',
    'creator.feedTitle': 'Últimas publicações',
    'creator.posts': 'publicações',
    'creator.likes': 'curtidas',
    'creator.subscribers': 'assinantes',
    'creator.subscribe': 'Assinar',
    'creator.subscribed': 'Inscrito',
    'creator.locked': 'Assine para desbloquear',
    'creator.unlockFor': 'Desbloquear por',
    'creator.pinned': 'Fixado',
    'creator.tip': 'Gorjeta',
    'creator.comments': 'comentários',
    'creator.unlock': 'Desbloquear',
    'creator.like': 'Curtir',
    'creator.unlike': 'Descurtir',
    'creator.liked': 'Curtido!',
    'creator.ppvUnlocked': 'Publicação desbloqueada!',
    'creator.newDrop': 'NOVO',
    'creator.newDrops': 'novo',
    'creator.commentPlaceholder': 'Adicione um comentário…',
    'creator.sendComment': 'Enviar comentário',
    'creator.commentPosted': 'Comentário enviado',
    'creator.youreSubscribed': 'Você está inscrito',
    'creator.tier': 'Nível',
    'creator.since': 'desde',
    'creator.unsubscribe': 'Cancelar assinatura',
    'creator.unsubscribed': 'Assinatura cancelada',
    'creator.discoverEyebrow': 'Descubra criadores',
    'creator.discoverTitle': 'Encontre seu próximo criador favorito',
    'creator.discoverSubtitle': 'Em alta e novidades — Bitcoin nativo, 0% de taxas.',
    'creator.followers': 'seguidores',
    'creator.searchPlaceholder': 'Pesquise por nome, tag ou cidade…',
    'creator.filterByVertical': 'Filtrar por vertical',
    'creator.allVertical': 'Todos',
    'creator.noResults': 'Nenhum criador corresponde à sua busca',
    'creator.trending': 'Em alta',
    'creator.newCreators': 'Novos esta semana',
    'creator.browseAll': 'Ver todos os criadores',
    'creator.profile': 'Perfil do criador',
    'creator.profileNotFound': 'Criador não encontrado',
    'creator.profileNotFoundDesc': 'Este criador não existe ou foi removido.',
    'creator.wishlists': 'Listas',
    'creator.satsRaised': 'Sats arrecadados',
    'creator.exploreCreators': 'Explorar criadores',
    'messages.pageTitle': 'Mensagens privadas',
    'messages.pageSubtitle': 'DMs criptografadas opcionais via Nostr.',
    'messages.enable': 'Ativar mensagens privadas neste dispositivo',
    'a11y.messagesMain': 'Caixa de mensagens privadas',
    'a11y.creatorsMain': 'Conteúdo para criadores',
    'nav.btcPrice': 'Preço BTC',
    'nav.tapProfile': 'Toque para ver perfil',
    'nav.closeMenu': 'Fechar menu',
    'nav.faqLink': 'FAQ',
    'demo.banner': 'Modo demo — explore com dados de exemplo. Sem conta.',
    'demo.tryWishlist': 'Testar wishlist demo →',
    'pwa.installTitle': 'Adicionar KATOA à tela inicial',
    'pwa.installSubtitle': 'Acesso rápido, experiência de app.',
    'pwa.install': 'Instalar',
    'pwa.dismiss': 'Dispensar',
    'changelog.gotIt': 'Entendi',
    'changelog.whatsNew': 'Novidades em',
    'common.backHome': 'Voltar ao início',
    'wishlist.title': 'Wishlist',
    'auth.passwordWeak': 'Senha fraca',
    'auth.passwordMedium': 'Senha média',
    'auth.passwordStrong': 'Senha forte',
    'share.button': 'Compartilhar',
    'share.title': 'Compartilhar esta wishlist',
    'share.twitter': 'Compartilhar no Twitter',
    'share.copy': 'Copiar link',
    'share.copied': 'Link copiado!',
    'map.title': 'Mapa Bitcoin',
    'map.subtitle': 'Comerciantes + projetos KATOA · alternar camadas',
    'map.merchants': 'Comerciantes',
    'map.katoa': 'KATOA',
    'map.legendMerchant': 'Comerciante BTC Map',
    'map.legendKatoa': 'Projeto KATOA',
    'map.turnOnLayer': 'Ative uma camada para ver o mapa',
    'map.locateMe': 'Minha localização',
    'map.fitAll': 'Ajustar todos os pins',
    'map.showAllLayers': 'Mostrar todas as camadas',
    'map.merchantsUnavailable': 'Comerciantes indisponíveis',
    'map.expand': 'Expandir mapa',
    'map.collapse': 'Recolher mapa',
    'map.search': 'Buscar comerciantes e áreas',
    'map.searchPlaceholder': 'Buscar no BTC Map…',
    'map.searchClear': 'Limpar busca',
    'map.searchPlace': 'Comerciante',
    'map.searchArea': 'Área',
    'map.popupMerchant': 'Comerciante BTC Map',
    'map.popupBoosted': 'Impulsionado',
    'map.popupHours': 'Horário',
    'map.popupVerified': 'Verificado',
    'map.popupWebsite': 'Site',
    'map.popupViewOnMap': 'Ver no BTC Map',
    'map.popupLoading': 'Carregando detalhes…',
    'map.popupComment': 'comentário',
    'map.popupComments': 'comentários',
    'map.popupKatoa': 'Projeto de criador KATOA',
    'map.popupSatsRaised': 'sats arrecadados',
    'map.popupViewWishlist': 'Ver wishlist',
    'map.categoryLabel': 'Filtrar comerciantes por categoria',
    'map.categoryAll': 'Todos',
    'map.categoryFood': 'Comida e bebida',
    'map.categoryShopping': 'Compras',
    'map.categoryStay': 'Hospedagem',
    'map.categoryServices': 'Serviços',
    'map.categoryFun': 'Lazer e cultura',
    'map.categoryTravel': 'Viagem',
    'map.events': 'Eventos',
    'map.areasHere': 'Áreas aqui',
    'map.activity': 'Atividade recente',
    'map.activityAdded': 'comércio adicionado',
    'map.activityUpdated': 'comércio atualizado',
    'map.activityDeleted': 'comércio removido',
    'map.activityCommented': 'novo comentário',
    'map.activityBoosted': 'em destaque',
    'map.shareView': 'Compartilhar vista do mapa',
    'map.shareCopied': 'Link do mapa copiado',
    'map.shareFailed': 'Não foi possível copiar',
    'map.contribute': 'Contribuir',
    'map.addPlace': 'Adicionar um comércio',
    'map.suggestEdit': 'Sugerir uma edição',
    'map.details': 'Detalhes',
    'map.drawerDirections': 'Rota',
    'map.drawerReport': 'Reportar um problema',
    'map.drawerViewOsm': 'Ver no OpenStreetMap',
    'map.drawerComments': 'Comentários',
    'map.drawerClose': 'Fechar detalhes',
    'map.loadMore': 'Carregar mais aqui',
    'map.loading': 'Carregando…',
    'map.noActivity': 'Nenhuma atividade recente por aqui ainda',
    'ticker.1': 'Criador na Nigéria economizou $2.400/ano',
    'ticker.2': 'Skate park em Medellín — 65% financiado',
    'ticker.3': '0% taxas — 100% para criadores',
    'ticker.4': 'Pagamento Lightning em 3 segundos',
    'ticker.5': '195+ países suportados',
    'trust.fees': 'Taxas de plataforma',
    'trust.payout': 'Pagamento Lightning',
    'trust.countries': 'Países',
    'trust.earnings': 'Ganhos do criador',
    'trust.aria': 'Destaques da plataforma',
    'trust.tip.fees': 'A taxa da plataforma é 0% até 31 dez 2026 — a Katoa não fica com nada. Taxas de roteamento Lightning existem e não são pagas à Katoa.',
    'trust.tip.payout': 'A Katoa não opera fila de saque. Quando um fã paga sua fatura Lightning, os sats liquidam na sua carteira — em segundos, não em dias bancários.',
    'trust.tip.countries': 'Bitcoin é global onde há internet. A Katoa não bloqueia criadores por geografia. Siga a lei onde você opera. 195+ é o mapa do Bitcoin, não uma contagem de usuários da Katoa.',
    'trust.tip.earnings': '0% de taxa, não custodial: os sats vão para a sua carteira, não para a da Katoa. Você ainda paga o que a carteira ou a rota Lightning cobrarem.',
    'trust.proof.aria': 'Confiança e provas',
    'trust.proof.zeroFees': '0% de taxa da plataforma',
    'trust.proof.nonCustodial': 'Não custodial',
    'trust.proof.openSource': 'Código aberto',
    'progress.goalReached': 'Meta alcançada!',
    'breadcrumb.home': 'Início',
    'explore.map': 'Mapa',
    'footer.company': 'Empresa',
    'footer.product': 'Produto',
    'footer.legal': 'Legal',
    'footer.contributors': 'Colaboradores',
    'footer.openRoles': 'Vagas',
    'footer.jobsTagline': 'Construa infraestrutura soberana de presentos · pago em sats ou fiat',
    'footer.openRolesCount': 'abertas',
    'footer.apply': 'Candidatar-se',
    'footer.applyEmailPrefix': 'Candidaturas:',
    'footer.apis': 'APIs para desenvolvedores',
    'footer.press': 'Kit de imprensa',
    'footer.meetup': 'Kit de encontro',
    'footer.creators': 'Para criadores',
    'footer.guidelines': 'Diretrizes',
    'footer.caseStudies': 'Estudos de caso',
    'footer.nip05': 'Reivindicar NIP-05',
    'footer.videoCreators': 'Criadores de vídeo',
    'footer.bounty': 'Bug bounty',
    'footer.donateSats': 'Doar sats',
    'explore.mapOpened': 'Mapa aberto',
    'explore.mapClosed': 'Mapa oculto',
    'explore.loadingResults': 'Carregando projetos',
    'explore.resultsCount': '${count} projetos encontrados',
    'explore.resultsCountOne': '1 projeto encontrado',
    'media.mute': 'Silenciar vídeo',
    'media.unmute': 'Ativar som do vídeo',
    'auth.metaTitle': 'Entrar',
    'auth.metaDesc': 'Entre ou crie sua conta KATOA grátis. Email, Google ou Nostr — comece a receber Bitcoin em minutos.',
    'security.metaDesc': 'Como a KATOA lida com chaves, fundos e dados — não custodial por design.',
  },
  fr: {
    'nav.comparison': 'Pourquoi KATOA ?',
    'nav.home': 'Accueil',
    'nav.more': 'Plus',
    'nav.signOut': 'Déconnexion',
    'nav.faq': 'FAQ',
    'nav.menu': 'Menu KATOA',
    'nav.getStarted': 'Commencer gratuitement',
    'nav.changeLanguage': 'Changer de langue',
    'nav.chooseLanguage': 'Choisir la langue',
    'nav.currency': 'Devise',
    'nav.messages': 'Messages',
    'tipMenu.title': 'Envoyer un pourboire',
    'tipMenu.help': 'Sats rapides — 21k, 50k ou personnalisé. Direct au créateur.',
    'tipMenu.custom': 'Sats personnalisés',
    'tipMenu.go': 'Envoyer',
    'favorites.copyPack': 'Copier le pack',
    'favorites.sharePack': 'Partager le pack',
    'favorites.download': 'Télécharger',
    'favorites.count': 'enregistrés',
    'favorites.copied': 'Pack copié',
    'favorites.copyFailed': 'Échec de la copie',
    'favorites.shareTitle': 'Mes favoris KATOA',
    'favorites.exportHeader': 'Mes favoris KATOA',
    'favorites.exportFooter': 'Zéro frais — katoa.org',
    'visibility.public': 'Public',
    'visibility.private': 'Non listé',
    'visibility.draft': 'Brouillon',
    'visibility.help': 'Qui peut voir cette liste',
    'messages.blocked': 'Bloqué',
    'messages.block': 'Bloquer',
    'messages.unblock': 'Débloquer',
    'messages.blockedList': 'Comptes bloqués',
    'messages.unread': 'non lus',
    'explore.verticals': 'Types de créateurs',
    'explore.allVerticals': 'Tous',
    'pwa.creatorTitle': 'Ajoutez KATOA à votre téléphone',
    'pwa.creatorBody': 'Enregistrez le hub créateurs pour tips et listes en un tap.',
    'pwa.creatorCopy': 'Copier le lien créateur',
    'pwa.creatorOpen': 'Ouvrir le guide',
    'pwa.creatorCopied': 'Lien copié',
    'creators.pageTitle': 'Pour les créateurs — gardez 100%',
    'creators.pageSubtitle': 'Tips et listes sans taxe de plateforme.',
    'creators.guidelinesTitle': 'Règles créateurs',
    'creator.feedTitle': 'Dernières publications',
    'creator.posts': 'publications',
    'creator.likes': 'j\'aime',
    'creator.subscribers': 'abonnés',
    'creator.subscribe': 'S\'abonner',
    'creator.subscribed': 'Abonné',
    'creator.locked': 'Abonnez-vous pour débloquer',
    'creator.unlockFor': 'Débloquer pour',
    'creator.pinned': 'Épinglé',
    'creator.tip': 'Pourboire',
    'creator.comments': 'commentaires',
    'creator.unlock': 'Débloquer',
    'creator.like': 'J\'aime',
    'creator.unlike': 'Je n\'aime plus',
    'creator.liked': 'C\'est aimé !',
    'creator.ppvUnlocked': 'Publication débloquée !',
    'creator.newDrop': 'NOUVEAU',
    'creator.newDrops': 'nouveau',
    'creator.commentPlaceholder': 'Ajouter un commentaire…',
    'creator.sendComment': 'Envoyer le commentaire',
    'creator.commentPosted': 'Commentaire envoyé',
    'creator.youreSubscribed': 'Vous êtes abonné',
    'creator.tier': 'Formule',
    'creator.since': 'depuis',
    'creator.unsubscribe': 'Se désabonner',
    'creator.unsubscribed': 'Désabonné',
    'creator.discoverEyebrow': 'Découvrir des créateurs',
    'creator.discoverTitle': 'Trouvez votre prochain créateur favori',
    'creator.discoverSubtitle': 'Tendances et nouveautés — Bitcoin natif, 0% de frais.',
    'creator.followers': 'abonnés',
    'creator.searchPlaceholder': 'Recherchez par nom, tag ou ville…',
    'creator.filterByVertical': 'Filtrer par verticale',
    'creator.allVertical': 'Tous',
    'creator.noResults': 'Aucun créateur ne correspond à votre recherche',
    'creator.trending': 'Tendances',
    'creator.newCreators': 'Nouveaux cette semaine',
    'creator.browseAll': 'Voir tous les créateurs',
    'creator.profile': 'Profil créateur',
    'creator.profileNotFound': 'Créateur introuvable',
    'creator.profileNotFoundDesc': "Ce créateur n'existe pas ou a été retiré.",
    'creator.wishlists': 'Listes',
    'creator.satsRaised': 'Sats collectés',
    'creator.exploreCreators': 'Explorer les créateurs',
    'messages.pageTitle': 'Messages privés',
    'messages.pageSubtitle': 'DMs chiffrés optionnels via Nostr.',
    'messages.enable': 'Activer les messages privés sur cet appareil',
    'a11y.messagesMain': 'Boîte de messages privés',
    'a11y.creatorsMain': 'Contenu créateurs',
    'nav.btcPrice': 'Prix BTC',
    'nav.tapProfile': 'Appuyer pour voir le profil',
    'nav.closeMenu': 'Fermer le menu',
    'nav.faqLink': 'FAQ',
    'demo.banner': 'Mode démo — explorez avec des données exemple. Sans compte.',
    'demo.tryWishlist': 'Essayer la wishlist démo →',
    'pwa.installTitle': 'Ajouter KATOA à l\'écran d\'accueil',
    'pwa.installSubtitle': 'Accès rapide, expérience type app.',
    'pwa.install': 'Installer',
    'pwa.dismiss': 'Ignorer',
    'changelog.gotIt': 'Compris',
    'changelog.whatsNew': 'Nouveautés dans',
    'common.backHome': 'Retour à l\'accueil',
    'wishlist.title': 'Wishlist',
    'auth.passwordWeak': 'Mot de passe faible',
    'auth.passwordMedium': 'Mot de passe moyen',
    'auth.passwordStrong': 'Mot de passe fort',
    'share.button': 'Partager',
    'share.title': 'Partager cette wishlist',
    'share.twitter': 'Partager sur Twitter',
    'share.copy': 'Copier le lien',
    'share.copied': 'Lien copié !',
    'map.title': 'Carte Bitcoin',
    'map.subtitle': 'Commerces + projets KATOA · basculer les couches',
    'map.merchants': 'Commerces',
    'map.katoa': 'KATOA',
    'map.legendMerchant': 'Commerce BTC Map',
    'map.legendKatoa': 'Projet KATOA',
    'map.turnOnLayer': 'Activez une couche pour voir la carte',
    'map.locateMe': 'Ma position',
    'map.fitAll': 'Ajuster tous les pins',
    'map.showAllLayers': 'Afficher toutes les couches',
    'map.merchantsUnavailable': 'Commerces indisponibles',
    'map.expand': 'Agrandir la carte',
    'map.collapse': 'Réduire la carte',
    'map.search': 'Rechercher commerces et zones',
    'map.searchPlaceholder': 'Rechercher BTC Map…',
    'map.searchClear': 'Effacer',
    'map.searchPlace': 'Commerce',
    'map.searchArea': 'Zone',
    'map.popupMerchant': 'Commerce BTC Map',
    'map.popupBoosted': 'Boosté',
    'map.popupHours': 'Horaires',
    'map.popupVerified': 'Vérifié',
    'map.popupWebsite': 'Site web',
    'map.popupViewOnMap': 'Voir sur BTC Map',
    'map.popupLoading': 'Chargement des détails…',
    'map.popupComment': 'commentaire',
    'map.popupComments': 'commentaires',
    'map.popupKatoa': 'Projet créateur KATOA',
    'map.popupSatsRaised': 'sats récoltés',
    'map.popupViewWishlist': 'Voir la wishlist',
    'map.categoryLabel': 'Filtrer les commerces par catégorie',
    'map.categoryAll': 'Tous',
    'map.categoryFood': 'Nourriture et boissons',
    'map.categoryShopping': 'Shopping',
    'map.categoryStay': 'Hébergement',
    'map.categoryServices': 'Services',
    'map.categoryFun': 'Loisirs et culture',
    'map.categoryTravel': 'Voyage',
    'map.events': 'Événements',
    'map.areasHere': 'Zones ici',
    'map.activity': 'Activité récente',
    'map.activityAdded': 'commerce ajouté',
    'map.activityUpdated': 'commerce mis à jour',
    'map.activityDeleted': 'commerce supprimé',
    'map.activityCommented': 'nouveau commentaire',
    'map.activityBoosted': 'mis en avant',
    'map.shareView': 'Partager la vue de la carte',
    'map.shareCopied': 'Lien de la carte copié',
    'map.shareFailed': 'Échec de la copie',
    'map.contribute': 'Contribuer',
    'map.addPlace': 'Ajouter un commerce',
    'map.suggestEdit': 'Suggérer une modification',
    'map.details': 'Détails',
    'map.drawerDirections': 'Itinéraire',
    'map.drawerReport': 'Signaler un problème',
    'map.drawerViewOsm': 'Voir sur OpenStreetMap',
    'map.drawerComments': 'Commentaires',
    'map.drawerClose': 'Fermer les détails',
    'map.loadMore': 'Charger plus ici',
    'map.loading': 'Chargement…',
    'map.noActivity': 'Aucune activité récente près d’ici',
    'ticker.1': 'Créateur au Nigeria a économisé 2 400 $/an',
    'ticker.2': 'Skate park à Medellín — 65 % financé',
    'ticker.3': '0 % de frais — 100 % aux créateurs',
    'ticker.4': 'Paiement Lightning en 3 secondes',
    'ticker.5': '195+ pays pris en charge',
    'trust.fees': 'Frais de plateforme',
    'trust.payout': 'Paiement Lightning',
    'trust.countries': 'Pays',
    'trust.earnings': 'Revenus créateur',
    'trust.aria': 'Points forts de la plateforme',
    'trust.tip.fees': 'Les frais de plateforme sont de 0 % jusqu’au 31 déc. 2026 — Katoa ne prend aucune commission. Les frais de routage Lightning existent encore et ne sont pas versés à Katoa.',
    'trust.tip.payout': 'Katoa n’a pas de file de paiement. Quand un fan paie votre facture Lightning, les sats arrivent dans votre portefeuille — en secondes, pas en jours bancaires.',
    'trust.tip.countries': 'Bitcoin est mondial partout où il y a internet. Katoa ne géo-bloque pas les créateurs. Respectez la loi là où vous opérez. 195+ est la carte Bitcoin, pas un nombre d’utilisateurs Katoa.',
    'trust.tip.earnings': '0 % de frais, non custodial : les sats vont dans votre portefeuille, pas celui de Katoa. Vous payez toujours ce que votre portefeuille ou le chemin Lightning facture.',
    'trust.proof.aria': 'Confiance et preuves',
    'trust.proof.zeroFees': '0 % de frais de plateforme',
    'trust.proof.nonCustodial': 'Non custodial',
    'trust.proof.openSource': 'Open source',
    'progress.goalReached': 'Objectif atteint !',
    'breadcrumb.home': 'Accueil',
    'explore.map': 'Carte',
    'footer.company': 'Entreprise',
    'footer.product': 'Produit',
    'footer.legal': 'Mentions légales',
    'footer.contributors': 'Contributeurs',
    'footer.openRoles': 'Postes ouverts',
    'footer.jobsTagline': 'Construisez une infrastructure de cadeaux souveraine · payé en sats ou fiat',
    'footer.openRolesCount': 'ouverts',
    'footer.apply': 'Postuler',
    'footer.applyEmailPrefix': 'Candidatures :',
    'footer.apis': 'APIs développeur',
    'footer.press': 'Dossier de presse',
    'footer.meetup': 'Kit meetup',
    'footer.creators': 'Pour les créateurs',
    'footer.guidelines': 'Règles',
    'footer.caseStudies': 'Études de cas',
    'footer.nip05': 'Réclamer NIP-05',
    'footer.videoCreators': 'Créateurs vidéo',
    'footer.bounty': 'Bug bounty',
    'footer.donateSats': 'Donner des sats',
    'explore.mapOpened': 'Carte ouverte',
    'explore.mapClosed': 'Carte masquée',
    'explore.loadingResults': 'Chargement des projets',
    'explore.resultsCount': '${count} projets trouvés',
    'explore.resultsCountOne': '1 projet trouvé',
    'media.mute': 'Couper le son de la vidéo',
    'media.unmute': 'Activer le son de la vidéo',
    'auth.metaTitle': 'Connexion',
    'auth.metaDesc': 'Connectez-vous ou créez votre compte KATOA gratuit. E-mail, Google ou Nostr — recevez du Bitcoin en quelques minutes.',
    'security.metaDesc': 'Comment KATOA gère les clés, les fonds et les données — non custodial par conception.',
  },
  de: {
    'nav.comparison': 'Warum KATOA?',
    'nav.home': 'Start',
    'nav.more': 'Mehr',
    'nav.signOut': 'Abmelden',
    'nav.faq': 'FAQ',
    'nav.menu': 'KATOA Menü',
    'nav.getStarted': 'Kostenlos starten',
    'nav.changeLanguage': 'Sprache ändern',
    'nav.chooseLanguage': 'Sprache wählen',
    'nav.currency': 'Währung',
    'nav.messages': 'Nachrichten',
    'tipMenu.title': 'Trinkgeld senden',
    'tipMenu.help': 'Schnelle Sats — 21k, 50k oder custom. Direkt an den Creator.',
    'tipMenu.custom': 'Eigene Sats',
    'tipMenu.go': 'Senden',
    'favorites.copyPack': 'Pack kopieren',
    'favorites.sharePack': 'Pack teilen',
    'favorites.download': 'Download',
    'favorites.count': 'gespeichert',
    'favorites.copied': 'Pack kopiert',
    'favorites.copyFailed': 'Kopieren fehlgeschlagen',
    'favorites.shareTitle': 'Meine KATOA-Favoriten',
    'favorites.exportHeader': 'Meine KATOA-Favoriten',
    'favorites.exportFooter': 'Null Gebühren — katoa.org',
    'visibility.public': 'Öffentlich',
    'visibility.private': 'Nicht gelistet',
    'visibility.draft': 'Entwurf',
    'visibility.help': 'Wer diese Liste sehen kann',
    'messages.blocked': 'Blockiert',
    'messages.block': 'Blockieren',
    'messages.unblock': 'Entsperren',
    'messages.blockedList': 'Blockierte Konten',
    'messages.unread': 'ungelesen',
    'explore.verticals': 'Creator-Typen',
    'explore.allVerticals': 'Alle',
    'pwa.creatorTitle': 'KATOA aufs Handy',
    'pwa.creatorBody': 'Creator-Hub speichern für Tips und Listen mit einem Tipp.',
    'pwa.creatorCopy': 'Creator-Link kopieren',
    'pwa.creatorOpen': 'Guide öffnen',
    'pwa.creatorCopied': 'Link kopiert',
    'creators.pageTitle': 'Für Creator — 100% behalten',
    'creators.pageSubtitle': 'Tips und Listen ohne Plattformsteuer.',
    'creators.guidelinesTitle': 'Creator-Richtlinien',
    'creator.feedTitle': 'Neueste Beiträge',
    'creator.posts': 'Beiträge',
    'creator.likes': 'Gefällt mir',
    'creator.subscribers': 'Abonnenten',
    'creator.subscribe': 'Abonnieren',
    'creator.subscribed': 'Abonniert',
    'creator.locked': 'Zum Entsperren abonnieren',
    'creator.unlockFor': 'Entsperren für',
    'creator.pinned': 'Angepinnt',
    'creator.tip': 'Trinkgeld',
    'creator.comments': 'Kommentare',
    'creator.unlock': 'Entsperren',
    'creator.like': 'Gefällt mir',
    'creator.unlike': 'Nicht mehr mögen',
    'creator.liked': 'Gefällt dir!',
    'creator.ppvUnlocked': 'Beitrag entsperrt!',
    'creator.newDrop': 'NEU',
    'creator.newDrops': 'neu',
    'creator.commentPlaceholder': 'Kommentar hinzufügen…',
    'creator.sendComment': 'Kommentar senden',
    'creator.commentPosted': 'Kommentar gesendet',
    'creator.youreSubscribed': 'Du bist abonniert',
    'creator.tier': 'Stufe',
    'creator.since': 'seit',
    'creator.unsubscribe': 'Abonnement kündigen',
    'creator.unsubscribed': 'Abonnement gekündigt',
    'creator.discoverEyebrow': 'Creators entdecken',
    'creator.discoverTitle': 'Finde deinen nächsten Lieblings-Creator',
    'creator.discoverSubtitle': 'Trending und neu — Bitcoin-nativ, 0% Gebühren.',
    'creator.followers': 'Follower',
    'creator.searchPlaceholder': 'Nach Name, Tag oder Stadt suchen…',
    'creator.filterByVertical': 'Nach Vertikale filtern',
    'creator.allVertical': 'Alle',
    'creator.noResults': 'Keine Creators passen zu deiner Suche',
    'creator.trending': 'Trending',
    'creator.newCreators': 'Neu diese Woche',
    'creator.browseAll': 'Alle Creators ansehen',
    'creator.profile': 'Creator-Profil',
    'creator.profileNotFound': 'Creator nicht gefunden',
    'creator.profileNotFoundDesc': 'Diesen Creator gibt es nicht oder er wurde entfernt.',
    'creator.wishlists': 'Wunschlisten',
    'creator.satsRaised': 'Sats gesammelt',
    'creator.exploreCreators': 'Creators entdecken',
    'messages.pageTitle': 'Private Nachrichten',
    'messages.pageSubtitle': 'Optionale verschlüsselte DMs via Nostr.',
    'messages.enable': 'Private Nachrichten auf diesem Gerät aktivieren',
    'a11y.messagesMain': 'Posteingang private Nachrichten',
    'a11y.creatorsMain': 'Creator-Inhalte',
    'nav.btcPrice': 'BTC-Preis',
    'nav.tapProfile': 'Tippen für Profil',
    'nav.closeMenu': 'Menü schließen',
    'nav.faqLink': 'FAQ',
    'demo.banner': 'Demo-Modus — mit Beispieldaten erkunden. Kein Konto nötig.',
    'demo.tryWishlist': 'Demo-Wishlist testen →',
    'pwa.installTitle': 'KATOA zum Startbildschirm hinzufügen',
    'pwa.installSubtitle': 'Schneller Zugriff, App-Erlebnis.',
    'pwa.install': 'Installieren',
    'pwa.dismiss': 'Schließen',
    'changelog.gotIt': 'Verstanden',
    'changelog.whatsNew': 'Neu in',
    'common.backHome': 'Zurück zur Startseite',
    'wishlist.title': 'Wishlist',
    'auth.passwordWeak': 'Schwaches Passwort',
    'auth.passwordMedium': 'Mittlere Stärke',
    'auth.passwordStrong': 'Starkes Passwort',
    'share.button': 'Teilen',
    'share.title': 'Diese Wishlist teilen',
    'share.twitter': 'Auf Twitter teilen',
    'share.copy': 'Link kopieren',
    'share.copied': 'Link kopiert!',
    'map.title': 'Bitcoin-Karte',
    'map.subtitle': 'Händler + KATOA-Projekte · Ebenen umschalten',
    'map.merchants': 'Händler',
    'map.katoa': 'KATOA',
    'map.legendMerchant': 'BTC Map Händler',
    'map.legendKatoa': 'KATOA-Projekt',
    'map.turnOnLayer': 'Ebene aktivieren, um die Karte zu sehen',
    'map.locateMe': 'Mein Standort',
    'map.fitAll': 'Alle Pins anpassen',
    'map.showAllLayers': 'Alle Ebenen anzeigen',
    'map.merchantsUnavailable': 'Händler nicht verfügbar',
    'map.expand': 'Karte erweitern',
    'map.collapse': 'Karte einklappen',
    'map.search': 'Händler und Gebiete suchen',
    'map.searchPlaceholder': 'BTC Map suchen…',
    'map.searchClear': 'Suche löschen',
    'map.searchPlace': 'Händler',
    'map.searchArea': 'Gebiet',
    'map.popupMerchant': 'BTC Map Händler',
    'map.popupBoosted': 'Geboostet',
    'map.popupHours': 'Öffnungszeiten',
    'map.popupVerified': 'Verifiziert',
    'map.popupWebsite': 'Webseite',
    'map.popupViewOnMap': 'Auf BTC Map ansehen',
    'map.popupLoading': 'Lade Details…',
    'map.popupComment': 'Kommentar',
    'map.popupComments': 'Kommentare',
    'map.popupKatoa': 'KATOA Creator-Projekt',
    'map.popupSatsRaised': 'Sats gesammelt',
    'map.popupViewWishlist': 'Wishlist ansehen',
    'map.categoryLabel': 'Händler nach Kategorie filtern',
    'map.categoryAll': 'Alle',
    'map.categoryFood': 'Essen & Trinken',
    'map.categoryShopping': 'Einkaufen',
    'map.categoryStay': 'Übernachtung',
    'map.categoryServices': 'Dienstleistungen',
    'map.categoryFun': 'Freizeit & Kultur',
    'map.categoryTravel': 'Reisen',
    'map.events': 'Veranstaltungen',
    'map.areasHere': 'Gebiete hier',
    'map.activity': 'Letzte Aktivität',
    'map.activityAdded': 'Ort hinzugefügt',
    'map.activityUpdated': 'Ort aktualisiert',
    'map.activityDeleted': 'Ort entfernt',
    'map.activityCommented': 'neuer Kommentar',
    'map.activityBoosted': 'hervorgehoben',
    'map.shareView': 'Kartenansicht teilen',
    'map.shareCopied': 'Kartenlink kopiert',
    'map.shareFailed': 'Kopieren fehlgeschlagen',
    'map.contribute': 'Beitragen',
    'map.addPlace': 'Ort hinzufügen',
    'map.suggestEdit': 'Bearbeitung vorschlagen',
    'map.details': 'Details',
    'map.drawerDirections': 'Route',
    'map.drawerReport': 'Problem melden',
    'map.drawerViewOsm': 'Auf OpenStreetMap ansehen',
    'map.drawerComments': 'Kommentare',
    'map.drawerClose': 'Details schließen',
    'map.loadMore': 'Mehr hier laden',
    'map.loading': 'Lädt…',
    'map.noActivity': 'Noch keine aktuelle Aktivität in der Nähe',
    'ticker.1': 'Creator in Nigeria sparte 2.400 $/Jahr',
    'ticker.2': 'Skatepark in Medellín — 65 % finanziert',
    'ticker.3': '0 % Gebühren — 100 % für Creator',
    'ticker.4': 'Lightning-Auszahlung in 3 Sekunden',
    'ticker.5': '195+ Länder unterstützt',
    'trust.fees': 'Plattformgebühren',
    'trust.payout': 'Lightning-Auszahlung',
    'trust.countries': 'Länder',
    'trust.earnings': 'Creator-Einnahmen',
    'trust.aria': 'Plattform-Highlights',
    'trust.tip.fees': 'Die Plattformgebühr ist bis zum 31. Dez. 2026 0 % — Katoa behält nichts. Lightning-Routinggebühren gibt es weiter und sie gehen nicht an Katoa.',
    'trust.tip.payout': 'Katoa betreibt keine Auszahlungswarteschlange. Zahlt ein Unterstützer deine Lightning-Rechnung, landen Sats in deiner Wallet — in Sekunden, nicht Banktagen.',
    'trust.tip.countries': 'Bitcoin ist global, wo Internet ist. Katoa sperrt Creator nicht geografisch. Halte dich an das Recht am Ort deiner Tätigkeit. 195+ ist die Bitcoin-Karte, keine Katoa-Nutzerzahl.',
    'trust.tip.earnings': '0 % Plattformgebühr, nicht-verwahrend: Sats gehen in deine Wallet, nicht zu Katoa. Du zahlst weiterhin, was Wallet oder Lightning-Pfad verlangen.',
    'trust.proof.aria': 'Vertrauen und Nachweis',
    'trust.proof.zeroFees': '0 % Plattformgebühr',
    'trust.proof.nonCustodial': 'Nicht-verwahrend',
    'trust.proof.openSource': 'Open Source',
    'progress.goalReached': 'Ziel erreicht!',
    'breadcrumb.home': 'Start',
    'explore.map': 'Karte',
    'footer.company': 'Unternehmen',
    'footer.product': 'Produkt',
    'footer.legal': 'Rechtliches',
    'footer.contributors': 'Mitwirkende',
    'footer.openRoles': 'Offene Stellen',
    'footer.jobsTagline': 'Souveräne Schenk-Infrastruktur mitbauen · Bezahlung in Sats oder Fiat',
    'footer.openRolesCount': 'offen',
    'footer.apply': 'Bewerben',
    'footer.applyEmailPrefix': 'Bewerbungen:',
    'footer.apis': 'Entwickler-APIs',
    'footer.press': 'Pressekit',
    'footer.meetup': 'Meetup-Kit',
    'footer.creators': 'Für Creator',
    'footer.guidelines': 'Richtlinien',
    'footer.caseStudies': 'Fallstudien',
    'footer.nip05': 'NIP-05 beantragen',
    'footer.videoCreators': 'Video-Creator',
    'footer.bounty': 'Bug-Bounty',
    'footer.donateSats': 'Sats spenden',
    'explore.mapOpened': 'Karte geöffnet',
    'explore.mapClosed': 'Karte ausgeblendet',
    'explore.loadingResults': 'Projekte werden geladen',
    'explore.resultsCount': '${count} Projekte gefunden',
    'explore.resultsCountOne': '1 Projekt gefunden',
    'media.mute': 'Video stumm schalten',
    'media.unmute': 'Video-Ton einschalten',
    'auth.metaTitle': 'Anmelden',
    'auth.metaDesc': 'Melde dich an oder erstelle dein kostenloses KATOA-Konto. E-Mail, Google oder Nostr — empfange in Minuten Bitcoin.',
    'security.metaDesc': 'Wie KATOA Schlüssel, Gelder und Daten handhabt — von Grund auf nicht-verwahrend.',
  },
  ja: {
    'nav.comparison': 'なぜKATOA？',
    'nav.home': 'ホーム',
    'nav.more': 'その他',
    'nav.signOut': 'ログアウト',
    'nav.faq': 'FAQ',
    'nav.menu': 'KATOAメニュー',
    'nav.getStarted': '無料で始める',
    'nav.changeLanguage': '言語を変更',
    'nav.chooseLanguage': '言語を選択',
    'nav.currency': '通貨',
    'nav.messages': 'メッセージ',
    'tipMenu.title': 'チップを送る',
    'tipMenu.help': 'クイックsats — 21k / 50k / カスタム。クリエイターへ直接。',
    'tipMenu.custom': 'カスタムsats',
    'tipMenu.go': '送る',
    'favorites.copyPack': 'パックをコピー',
    'favorites.sharePack': 'パックを共有',
    'favorites.download': 'ダウンロード',
    'favorites.count': '件',
    'favorites.copied': 'コピーしました',
    'favorites.copyFailed': 'コピー失敗',
    'favorites.shareTitle': 'KATOAのお気に入り',
    'favorites.exportHeader': 'KATOAのお気に入り',
    'favorites.exportFooter': '手数料0 — katoa.org',
    'visibility.public': '公開',
    'visibility.private': '限定公開',
    'visibility.draft': '下書き',
    'visibility.help': 'このリストを見られる人',
    'messages.blocked': 'ブロック中',
    'messages.block': 'ブロック',
    'messages.unblock': '解除',
    'messages.blockedList': 'ブロックしたアカウント',
    'messages.unread': '未読',
    'explore.verticals': 'クリエイター種別',
    'explore.allVerticals': 'すべて',
    'pwa.creatorTitle': 'KATOAをホーム画面に',
    'pwa.creatorBody': 'クリエイターハブを保存してワンタップでチップとリストへ。',
    'pwa.creatorCopy': 'クリエイターリンクをコピー',
    'pwa.creatorOpen': 'ガイドを開く',
    'pwa.creatorCopied': 'コピーしました',
    'creators.pageTitle': 'クリエイター向け — 100%キープ',
    'creators.pageSubtitle': 'プラットフォーム税なしのチップとリスト。',
    'creators.guidelinesTitle': 'クリエイターガイドライン',
    'creator.feedTitle': '最新の投稿',
    'creator.posts': '投稿',
    'creator.likes': 'いいね',
    'creator.subscribers': '登録者',
    'creator.subscribe': '登録',
    'creator.subscribed': '登録済み',
    'creator.locked': '登録して解除',
    'creator.unlockFor': '解除: ',
    'creator.pinned': 'ピン留め',
    'creator.tip': 'チップ',
    'creator.comments': 'コメント',
    'creator.unlock': '解除',
    'creator.like': 'いいね',
    'creator.unlike': 'いいねを外す',
    'creator.liked': 'いいねしました！',
    'creator.ppvUnlocked': '投稿を解除しました！',
    'creator.newDrop': '新着',
    'creator.newDrops': '件の新着',
    'creator.commentPlaceholder': 'コメントを追加…',
    'creator.sendComment': 'コメントを送信',
    'creator.commentPosted': 'コメントを投稿しました',
    'creator.youreSubscribed': '登録済みです',
    'creator.tier': 'ティア',
    'creator.since': '以来',
    'creator.unsubscribe': '登録解除',
    'creator.unsubscribed': '登録を解除しました',
    'creator.discoverEyebrow': 'クリエイターを見つける',
    'creator.discoverTitle': '次のお気に入りクリエイターを見つけよう',
    'creator.discoverSubtitle': 'トレンドと新着 — ビットコインネイティブ、手数料0%。',
    'creator.followers': 'フォロワー',
    'creator.searchPlaceholder': '名前、タグ、都市で検索…',
    'creator.filterByVertical': 'カテゴリで絞り込む',
    'creator.allVertical': 'すべて',
    'creator.noResults': '検索に一致するクリエイターがいません',
    'creator.trending': 'トレンド',
    'creator.newCreators': '今週の新着',
    'creator.browseAll': 'すべてのクリエイターを見る',
    'creator.profile': 'クリエイタープロフィール',
    'creator.profileNotFound': 'クリエイターが見つかりません',
    'creator.profileNotFoundDesc': 'このクリエイターは存在しないか、削除されています。',
    'creator.wishlists': 'ウィッシュリスト',
    'creator.satsRaised': '集まったsats',
    'creator.exploreCreators': 'クリエイターを探す',
    'messages.pageTitle': 'プライベートメッセージ',
    'messages.pageSubtitle': 'Nostrによる任意の暗号化DM。',
    'messages.enable': 'この端末でプライベートメッセージを有効化',
    'a11y.messagesMain': 'プライベートメッセージ受信箱',
    'a11y.creatorsMain': 'クリエイター向けコンテンツ',
    'nav.btcPrice': 'BTC価格',
    'nav.tapProfile': 'タップしてプロフィールを表示',
    'nav.closeMenu': 'メニューを閉じる',
    'nav.faqLink': 'FAQ',
    'demo.banner': 'デモモード — サンプルデータで探索。アカウント不要。',
    'demo.tryWishlist': 'デモウィッシュリストを試す →',
    'pwa.installTitle': 'KATOAをホーム画面に追加',
    'pwa.installSubtitle': 'クイックアクセス、アプリのような体験。',
    'pwa.install': 'インストール',
    'pwa.dismiss': '閉じる',
    'changelog.gotIt': '了解',
    'changelog.whatsNew': '新機能',
    'common.backHome': 'ホームに戻る',
    'wishlist.title': 'ウィッシュリスト',
    'auth.passwordWeak': '弱いパスワード',
    'auth.passwordMedium': '中程度の強度',
    'auth.passwordStrong': '強いパスワード',
    'share.button': '共有',
    'share.title': 'このウィッシュリストを共有',
    'share.twitter': 'Twitterで共有',
    'share.copy': 'リンクをコピー',
    'share.copied': 'リンクをコピーしました！',
    'map.title': 'Bitcoinマップ',
    'map.subtitle': '加盟店 + KATOAプロジェクト · レイヤー切替',
    'map.merchants': '加盟店',
    'map.katoa': 'KATOA',
    'map.legendMerchant': 'BTC Map加盟店',
    'map.legendKatoa': 'KATOAプロジェクト',
    'map.turnOnLayer': 'レイヤーをオンにして地図を表示',
    'map.locateMe': '現在地',
    'map.fitAll': 'すべてのピンに合わせる',
    'map.showAllLayers': 'すべてのレイヤーを表示',
    'map.merchantsUnavailable': '加盟店は利用できません',
    'map.expand': '地図を展開',
    'map.collapse': '地図を折りたたむ',
    'map.search': '加盟店とエリアを検索',
    'map.searchPlaceholder': 'BTC Map を検索…',
    'map.searchClear': 'クリア',
    'map.searchPlace': '加盟店',
    'map.searchArea': 'エリア',
    'map.popupMerchant': 'BTC Map加盟店',
    'map.popupBoosted': 'ブースト',
    'map.popupHours': '営業時間',
    'map.popupVerified': '確認済み',
    'map.popupWebsite': 'ウェブサイト',
    'map.popupViewOnMap': 'BTC Mapで見る',
    'map.popupLoading': '詳細を読み込み中…',
    'map.popupComment': 'コメント',
    'map.popupComments': 'コメント',
    'map.popupKatoa': 'KATOAクリエイタープロジェクト',
    'map.popupSatsRaised': 'sats獲得',
    'map.popupViewWishlist': 'ウィッシュリストを見る',
    'map.categoryLabel': 'カテゴリで加盟店を絞り込む',
    'map.categoryAll': 'すべて',
    'map.categoryFood': '飲食',
    'map.categoryShopping': 'ショッピング',
    'map.categoryStay': '宿泊',
    'map.categoryServices': 'サービス',
    'map.categoryFun': 'レジャー・文化',
    'map.categoryTravel': '旅行',
    'map.events': 'イベント',
    'map.areasHere': 'ここのエリア',
    'map.activity': '最近の活動',
    'map.activityAdded': 'お店が追加されました',
    'map.activityUpdated': 'お店が更新されました',
    'map.activityDeleted': 'お店が削除されました',
    'map.activityCommented': '新しいコメント',
    'map.activityBoosted': '注目',
    'map.shareView': '地図ビューを共有',
    'map.shareCopied': '地図リンクをコピーしました',
    'map.shareFailed': 'コピーできませんでした',
    'map.contribute': '貢献する',
    'map.addPlace': 'お店を追加',
    'map.suggestEdit': '編集を提案',
    'map.details': '詳細',
    'map.drawerDirections': '経路',
    'map.drawerReport': '問題を報告',
    'map.drawerViewOsm': 'OpenStreetMapで見る',
    'map.drawerComments': 'コメント',
    'map.drawerClose': '詳細を閉じる',
    'map.loadMore': 'さらに読み込む',
    'map.loading': '読み込み中…',
    'map.noActivity': 'この近くに最近の活動はまだありません',
    'ticker.1': 'ナイジェリアのクリエイターが年$2,400節約',
    'ticker.2': 'メデジンのスケートパーク — 65%達成',
    'ticker.3': '0%手数料 — クリエイターに100%',
    'ticker.4': 'Lightning支払いは3秒',
    'ticker.5': '195カ国以上対応',
    'trust.fees': 'プラットフォーム手数料',
    'trust.payout': 'Lightning支払い',
    'trust.countries': 'カ国',
    'trust.earnings': 'クリエイター収益',
    'trust.aria': 'プラットフォームの要点',
    'trust.tip.fees': 'プラットフォーム手数料は2026年12月31日まで0% — Katoaは取り分を取りません。Lightningのルーティング手数料は別途あり、Katoaには支払われません。',
    'trust.tip.payout': 'Katoaに出金キューはありません。サポーターがLightningインボイスを払うと、satsはあなたのウォレットへ着金します — 銀行日数ではなく、通常は数秒です。',
    'trust.tip.countries': 'インターネットがある場所ならBitcoinはグローバルです。Katoaはクリエイターを地理で遮断しません。活動する地域の法令に従ってください。195+はBitcoinの地図であり、Katoaのユーザー数ではありません。',
    'trust.tip.earnings': 'プラットフォーム手数料0%、非カストディアル：satsはKatoaではなくあなたのウォレットへ。ウォレットやLightning経路の手数料は別途かかります。',
    'trust.proof.aria': '信頼と証明',
    'trust.proof.zeroFees': 'プラットフォーム手数料0%',
    'trust.proof.nonCustodial': '非カストディアル',
    'trust.proof.openSource': 'オープンソース',
    'progress.goalReached': '目標達成！',
    'breadcrumb.home': 'ホーム',
    'explore.map': '地図',
    'footer.company': '会社',
    'footer.product': '製品',
    'footer.legal': '法的情報',
    'footer.contributors': '貢献者',
    'footer.openRoles': '募集中の役職',
    'footer.jobsTagline': '主権あるギフト基盤を一緒に作る · 報酬はsatsまたはfiat',
    'footer.openRolesCount': '件',
    'footer.apply': '応募',
    'footer.applyEmailPrefix': '応募先:',
    'footer.apis': '開発者API',
    'footer.press': 'プレスキット',
    'footer.meetup': 'ミートアップキット',
    'footer.creators': 'クリエイター向け',
    'footer.guidelines': 'ガイドライン',
    'footer.caseStudies': '事例',
    'footer.nip05': 'NIP-05を申請',
    'footer.videoCreators': '動画クリエイター',
    'footer.bounty': 'バグバウンティ',
    'footer.donateSats': 'satsを寄付',
    'explore.mapOpened': '地図を開きました',
    'explore.mapClosed': '地図を非表示',
    'explore.loadingResults': 'プロジェクトを読み込み中',
    'explore.resultsCount': '${count}件のプロジェクト',
    'explore.resultsCountOne': '1件のプロジェクト',
    'media.mute': '動画をミュート',
    'media.unmute': '動画の音声をオン',
    'auth.metaTitle': 'サインイン',
    'auth.metaDesc': 'サインインするか、無料のKATOAアカウントを作成。メール、Google、Nostr対応 — 数分でBitcoinを受け取れます。',
    'security.metaDesc': 'KATOAの鍵・資金・データの取り扱い — 設計上ノンカストディアル。',
  },
  zh: {
    'nav.comparison': '为什么选择KATOA？',
    'nav.home': '首页',
    'nav.more': '更多',
    'nav.signOut': '退出登录',
    'nav.faq': '常见问题',
    'nav.menu': 'KATOA菜单',
    'nav.getStarted': '免费开始',
    'nav.changeLanguage': '更改语言',
    'nav.chooseLanguage': '选择语言',
    'nav.currency': '货币',
    'nav.messages': '消息',
    'tipMenu.title': '发送打赏',
    'tipMenu.help': '快捷sats — 21k、50k或自定义。直达创作者。',
    'tipMenu.custom': '自定义sats',
    'tipMenu.go': '打赏',
    'favorites.copyPack': '复制合集',
    'favorites.sharePack': '分享合集',
    'favorites.download': '下载',
    'favorites.count': '已收藏',
    'favorites.copied': '已复制',
    'favorites.copyFailed': '复制失败',
    'favorites.shareTitle': '我的KATOA收藏',
    'favorites.exportHeader': '我的KATOA收藏',
    'favorites.exportFooter': '零平台费 — katoa.org',
    'visibility.public': '公开',
    'visibility.private': '不公开列出',
    'visibility.draft': '草稿',
    'visibility.help': '谁可以看到此心愿单',
    'messages.blocked': '已屏蔽',
    'messages.block': '屏蔽',
    'messages.unblock': '取消屏蔽',
    'messages.blockedList': '已屏蔽账号',
    'messages.unread': '未读',
    'explore.verticals': '创作者类型',
    'explore.allVerticals': '全部',
    'pwa.creatorTitle': '把KATOA加到手机',
    'pwa.creatorBody': '保存创作者中心，一键打赏与心愿单。',
    'pwa.creatorCopy': '复制创作者链接',
    'pwa.creatorOpen': '打开指南',
    'pwa.creatorCopied': '已复制',
    'creators.pageTitle': '创作者 — 保留100%',
    'creators.pageSubtitle': '无平台抽成的打赏与心愿单。',
    'creators.guidelinesTitle': '创作者准则',
    'creator.feedTitle': '最新帖子',
    'creator.posts': '帖子',
    'creator.likes': '赞',
    'creator.subscribers': '订阅者',
    'creator.subscribe': '订阅',
    'creator.subscribed': '已订阅',
    'creator.locked': '订阅后解锁',
    'creator.unlockFor': '解锁费用',
    'creator.pinned': '置顶',
    'creator.tip': '打赏',
    'creator.comments': '评论',
    'creator.unlock': '解锁',
    'creator.like': '点赞',
    'creator.unlike': '取消点赞',
    'creator.liked': '已点赞！',
    'creator.ppvUnlocked': '帖子已解锁！',
    'creator.newDrop': '新帖',
    'creator.newDrops': '条新内容',
    'creator.commentPlaceholder': '添加评论…',
    'creator.sendComment': '发送评论',
    'creator.commentPosted': '评论已发布',
    'creator.youreSubscribed': '您已订阅',
    'creator.tier': '等级',
    'creator.since': '自',
    'creator.unsubscribe': '取消订阅',
    'creator.unsubscribed': '已取消订阅',
    'creator.discoverEyebrow': '发现创作者',
    'creator.discoverTitle': '找到你下一个最喜欢的创作者',
    'creator.discoverSubtitle': '热门与新作 — 比特币原生，0% 费用。',
    'creator.followers': '粉丝',
    'creator.searchPlaceholder': '按名称、标签或城市搜索…',
    'creator.filterByVertical': '按领域筛选',
    'creator.allVertical': '全部',
    'creator.noResults': '没有匹配的创作者',
    'creator.trending': '热门',
    'creator.newCreators': '本周新作',
    'creator.browseAll': '浏览所有创作者',
    'creator.profile': '创作者主页',
    'creator.profileNotFound': '未找到创作者',
    'creator.profileNotFoundDesc': '该创作者不存在或已被移除。',
    'creator.wishlists': '心愿单',
    'creator.satsRaised': '已筹集 sats',
    'creator.exploreCreators': '发现创作者',
    'messages.pageTitle': '私信',
    'messages.pageSubtitle': '可选的Nostr加密私信。',
    'messages.enable': '在此设备启用私信',
    'a11y.messagesMain': '私信收件箱',
    'a11y.creatorsMain': '创作者内容',
    'nav.btcPrice': 'BTC价格',
    'nav.tapProfile': '点击查看个人资料',
    'nav.closeMenu': '关闭菜单',
    'nav.faqLink': '常见问题',
    'demo.banner': '演示模式 — 使用示例数据探索。无需账户。',
    'demo.tryWishlist': '试用演示心愿单 →',
    'pwa.installTitle': '将KATOA添加到主屏幕',
    'pwa.installSubtitle': '快速访问，类似应用的体验。',
    'pwa.install': '安装',
    'pwa.dismiss': '关闭',
    'changelog.gotIt': '知道了',
    'changelog.whatsNew': '新功能',
    'common.backHome': '返回首页',
    'wishlist.title': '心愿单',
    'auth.passwordWeak': '密码较弱',
    'auth.passwordMedium': '密码中等',
    'auth.passwordStrong': '密码较强',
    'share.button': '分享',
    'share.title': '分享此心愿单',
    'share.twitter': '在Twitter上分享',
    'share.copy': '复制链接',
    'share.copied': '链接已复制！',
    'map.title': '比特币地图',
    'map.subtitle': '商户 + KATOA创作者项目 · 切换图层',
    'map.merchants': '商户',
    'map.katoa': 'KATOA',
    'map.legendMerchant': 'BTC Map商户',
    'map.legendKatoa': 'KATOA项目',
    'map.turnOnLayer': '开启图层以查看地图',
    'map.locateMe': '我的位置',
    'map.fitAll': '适应所有标记',
    'map.showAllLayers': '显示所有图层',
    'map.merchantsUnavailable': '商户不可用',
    'map.expand': '展开地图',
    'map.collapse': '收起地图',
    'map.search': '搜索商家与区域',
    'map.searchPlaceholder': '搜索 BTC Map…',
    'map.searchClear': '清除搜索',
    'map.searchPlace': '商家',
    'map.searchArea': '区域',
    'map.popupMerchant': 'BTC Map商户',
    'map.popupBoosted': '已推广',
    'map.popupHours': '营业时间',
    'map.popupVerified': '已验证',
    'map.popupWebsite': '网站',
    'map.popupViewOnMap': '在BTC Map查看',
    'map.popupLoading': '正在加载详情…',
    'map.popupComment': '条评论',
    'map.popupComments': '条评论',
    'map.popupKatoa': 'KATOA创作者项目',
    'map.popupSatsRaised': '已筹集sats',
    'map.popupViewWishlist': '查看心愿单',
    'map.categoryLabel': '按类别筛选商户',
    'map.categoryAll': '全部',
    'map.categoryFood': '餐饮',
    'map.categoryShopping': '购物',
    'map.categoryStay': '住宿',
    'map.categoryServices': '服务',
    'map.categoryFun': '休闲与文化',
    'map.categoryTravel': '旅行',
    'map.events': '活动',
    'map.areasHere': '所在区域',
    'map.activity': '近期动态',
    'map.activityAdded': '新增商家',
    'map.activityUpdated': '商家已更新',
    'map.activityDeleted': '商家已移除',
    'map.activityCommented': '新评论',
    'map.activityBoosted': '推荐',
    'map.shareView': '分享地图视图',
    'map.shareCopied': '地图链接已复制',
    'map.shareFailed': '复制失败',
    'map.contribute': '贡献',
    'map.addPlace': '添加商家',
    'map.suggestEdit': '建议修改',
    'map.details': '详情',
    'map.drawerDirections': '路线',
    'map.drawerReport': '报告问题',
    'map.drawerViewOsm': '在 OpenStreetMap 查看',
    'map.drawerComments': '评论',
    'map.drawerClose': '关闭详情',
    'map.loadMore': '加载更多',
    'map.loading': '加载中…',
    'map.noActivity': '附近暂无近期动态',
    'ticker.1': '尼日利亚创作者每年节省$2,400',
    'ticker.2': '麦德林滑板公园 — 65%已筹集',
    'ticker.3': '0%费用 — 100%归创作者',
    'ticker.4': '闪电支付仅需3秒',
    'ticker.5': '支持195+个国家',
    'trust.fees': '平台费用',
    'trust.payout': '闪电支付',
    'trust.countries': '国家',
    'trust.earnings': '创作者收入',
    'trust.aria': '平台亮点',
    'trust.tip.fees': '平台抽成 0%（至2026年12月31日）— Katoa 不抽成。闪电网络路由费仍然存在，且不付给 Katoa。',
    'trust.tip.payout': 'Katoa 没有打款队列。支持者支付你的闪电发票后，聪会结算到你的钱包 — 通常是数秒，不是银行工作日。',
    'trust.tip.countries': '只要有互联网，比特币就是全球的。Katoa 不按地区封锁创作者。请遵守你所在地的法律。195+ 是比特币地图，不是 Katoa 用户数。',
    'trust.tip.earnings': '平台抽成 0%、非托管：聪进入你的钱包，不是 Katoa 的。你仍需支付钱包或闪电路径收取的费用。',
    'trust.proof.aria': '信任与证明',
    'trust.proof.zeroFees': '平台抽成 0%',
    'trust.proof.nonCustodial': '非托管',
    'trust.proof.openSource': '开源',
    'progress.goalReached': '目标达成！',
    'breadcrumb.home': '首页',
    'explore.map': '地图',
    'footer.company': '公司',
    'footer.product': '产品',
    'footer.legal': '法律',
    'footer.contributors': '贡献者',
    'footer.openRoles': '在招职位',
    'footer.jobsTagline': '共建主权赠与基础设施 · 以sats或法币支付',
    'footer.openRolesCount': '个在招',
    'footer.apply': '申请',
    'footer.applyEmailPrefix': '申请邮箱:',
    'footer.apis': '开发者API',
    'footer.press': '媒体资料',
    'footer.meetup': '聚会工具包',
    'footer.creators': '创作者',
    'footer.guidelines': '准则',
    'footer.caseStudies': '案例',
    'footer.nip05': '申请 NIP-05',
    'footer.videoCreators': '视频创作者',
    'footer.bounty': '漏洞赏金',
    'footer.donateSats': '捐赠 sats',
    'explore.mapOpened': '地图已打开',
    'explore.mapClosed': '地图已隐藏',
    'explore.loadingResults': '正在加载项目',
    'explore.resultsCount': '找到${count}个项目',
    'explore.resultsCountOne': '找到1个项目',
    'media.mute': '静音视频',
    'media.unmute': '取消视频静音',
    'auth.metaTitle': '登录',
    'auth.metaDesc': '登录或创建免费 KATOA 账户。支持邮箱、Google 或 Nostr — 几分钟即可开始接收比特币。',
    'security.metaDesc': 'KATOA 如何处理密钥、资金与数据 — 设计即非托管。',
  },
};

const contactPageStrings = {
  en: {
    'contact.email': 'Email',
    'contact.community': 'Community',
    'contact.response': 'Response',
    'contact.responseTime': 'Within 48 hours',
    'contact.sendMessage': 'Send us a message',
    'contact.copy': 'Copy',
    'contact.copySuccess': 'Email copied!',
    'contact.copyFail': 'Could not copy email',
    'contact.send': 'Send Message',
    'contact.name': 'Name',
    'contact.subject': 'Subject',
    'contact.message': 'Message',
    'contact.openingMail': 'Opening your mail app…',
    'contact.githubIssues': 'GitHub Issues',
    'contact.error.name': 'Name is required',
    'contact.error.email': 'Valid email required',
    'contact.error.subject': 'Subject is required',
    'contact.error.message': 'Message must be at least 10 characters',
    'contact.metaTitle': 'Contact',
    'contact.metaDesc': 'Get in touch with the KATOA team.',
    'contact.successTitle': 'Your message is ready',
    'contact.successTo': 'Your message is on its way to',
    'contact.composeAgain': 'Open in your email app',
    'contact.newMessage': 'Send another message',
  },
  es: {
    'contact.email': 'Correo',
    'contact.community': 'Comunidad',
    'contact.response': 'Respuesta',
    'contact.responseTime': 'En 48 horas',
    'contact.sendMessage': 'Envíanos un mensaje',
    'contact.copy': 'Copiar',
    'contact.copySuccess': '¡Correo copiado!',
    'contact.copyFail': 'No se pudo copiar el correo',
    'contact.send': 'Enviar mensaje',
    'contact.name': 'Nombre',
    'contact.subject': 'Asunto',
    'contact.message': 'Mensaje',
    'contact.openingMail': 'Abriendo tu app de correo…',
    'contact.githubIssues': 'Problemas en GitHub',
    'contact.error.name': 'El nombre es obligatorio',
    'contact.error.email': 'Se requiere un correo válido',
    'contact.error.subject': 'El asunto es obligatorio',
    'contact.error.message': 'El mensaje debe tener al menos 10 caracteres',
    'contact.metaTitle': 'Contacto',
    'contact.metaDesc': 'Habla con el equipo de KATOA.',
    'contact.successTitle': 'Tu mensaje está listo',
    'contact.successTo': 'Tu mensaje va en camino a',
    'contact.composeAgain': 'Abrir en tu app de correo',
    'contact.newMessage': 'Enviar otro mensaje',
  },
  pt: {
    'contact.email': 'E-mail',
    'contact.community': 'Comunidade',
    'contact.response': 'Resposta',
    'contact.responseTime': 'Em até 48 horas',
    'contact.sendMessage': 'Envie uma mensagem',
    'contact.copy': 'Copiar',
    'contact.copySuccess': 'E-mail copiado!',
    'contact.copyFail': 'Não foi possível copiar o e-mail',
    'contact.send': 'Enviar mensagem',
    'contact.name': 'Nome',
    'contact.subject': 'Assunto',
    'contact.message': 'Mensagem',
    'contact.openingMail': 'Abrindo seu app de e-mail…',
    'contact.githubIssues': 'Problemas no GitHub',
    'contact.error.name': 'Nome é obrigatório',
    'contact.error.email': 'E-mail válido é obrigatório',
    'contact.error.subject': 'Assunto é obrigatório',
    'contact.error.message': 'A mensagem deve ter pelo menos 10 caracteres',
    'contact.metaTitle': 'Contato',
    'contact.metaDesc': 'Fale com a equipe da KATOA.',
    'contact.successTitle': 'Sua mensagem está pronta',
    'contact.successTo': 'Sua mensagem está a caminho de',
    'contact.composeAgain': 'Abrir no seu app de e-mail',
    'contact.newMessage': 'Enviar outra mensagem',
  },
  fr: {
    'contact.email': 'E-mail',
    'contact.community': 'Communauté',
    'contact.response': 'Réponse',
    'contact.responseTime': 'Sous 48 heures',
    'contact.sendMessage': 'Envoyez-nous un message',
    'contact.copy': 'Copier',
    'contact.copySuccess': 'E-mail copié !',
    'contact.copyFail': 'Impossible de copier l\'e-mail',
    'contact.send': 'Envoyer le message',
    'contact.name': 'Nom',
    'contact.subject': 'Sujet',
    'contact.message': 'Message',
    'contact.openingMail': 'Ouverture de votre app mail…',
    'contact.githubIssues': 'Problèmes GitHub',
    'contact.error.name': 'Le nom est requis',
    'contact.error.email': 'E-mail valide requis',
    'contact.error.subject': 'Le sujet est requis',
    'contact.error.message': 'Le message doit contenir au moins 10 caractères',
    'contact.metaTitle': 'Contact',
    'contact.metaDesc': 'Contactez l’équipe KATOA.',
    'contact.successTitle': 'Votre message est prêt',
    'contact.successTo': 'Votre message est en route vers',
    'contact.composeAgain': 'Ouvrir dans votre app mail',
    'contact.newMessage': 'Envoyer un autre message',
  },
  de: {
    'contact.email': 'E-Mail',
    'contact.community': 'Community',
    'contact.response': 'Antwort',
    'contact.responseTime': 'Innerhalb von 48 Stunden',
    'contact.sendMessage': 'Schreib uns eine Nachricht',
    'contact.copy': 'Kopieren',
    'contact.copySuccess': 'E-Mail kopiert!',
    'contact.copyFail': 'E-Mail konnte nicht kopiert werden',
    'contact.send': 'Nachricht senden',
    'contact.name': 'Name',
    'contact.subject': 'Betreff',
    'contact.message': 'Nachricht',
    'contact.openingMail': 'E-Mail-App wird geöffnet…',
    'contact.githubIssues': 'GitHub-Issues',
    'contact.error.name': 'Name ist erforderlich',
    'contact.error.email': 'Gültige E-Mail erforderlich',
    'contact.error.subject': 'Betreff ist erforderlich',
    'contact.error.message': 'Nachricht muss mindestens 10 Zeichen haben',
    'contact.metaTitle': 'Kontakt',
    'contact.metaDesc': 'Nimm Kontakt mit dem KATOA-Team auf.',
    'contact.successTitle': 'Deine Nachricht ist fertig',
    'contact.successTo': 'Deine Nachricht ist unterwegs zu',
    'contact.composeAgain': 'In deiner E-Mail-App öffnen',
    'contact.newMessage': 'Eine weitere Nachricht senden',
  },
  ja: {
    'contact.email': 'メール',
    'contact.community': 'コミュニティ',
    'contact.response': '返信',
    'contact.responseTime': '48時間以内',
    'contact.sendMessage': 'メッセージを送る',
    'contact.copy': 'コピー',
    'contact.copySuccess': 'メールをコピーしました！',
    'contact.copyFail': 'メールをコピーできませんでした',
    'contact.send': 'メッセージを送信',
    'contact.name': '名前',
    'contact.subject': '件名',
    'contact.message': 'メッセージ',
    'contact.openingMail': 'メールアプリを開いています…',
    'contact.githubIssues': 'GitHubのIssue',
    'contact.error.name': '名前は必須です',
    'contact.error.email': '有効なメールアドレスが必要です',
    'contact.error.subject': '件名は必須です',
    'contact.error.message': 'メッセージは10文字以上必要です',
    'contact.metaTitle': 'お問い合わせ',
    'contact.metaDesc': 'KATOAチームにご連絡ください。',
    'contact.successTitle': 'メッセージの準備ができました',
    'contact.successTo': 'メッセージを送信先へ',
    'contact.composeAgain': 'メールアプリで開く',
    'contact.newMessage': '別のメッセージを送る',
  },
  zh: {
    'contact.email': '电子邮件',
    'contact.community': '社区',
    'contact.response': '回复',
    'contact.responseTime': '48小时内',
    'contact.sendMessage': '给我们留言',
    'contact.copy': '复制',
    'contact.copySuccess': '邮箱已复制！',
    'contact.copyFail': '无法复制邮箱',
    'contact.send': '发送消息',
    'contact.name': '姓名',
    'contact.subject': '主题',
    'contact.message': '消息',
    'contact.openingMail': '正在打开邮件应用…',
    'contact.githubIssues': 'GitHub 问题',
    'contact.error.name': '姓名为必填项',
    'contact.error.email': '需要有效的电子邮件',
    'contact.error.subject': '主题为必填项',
    'contact.error.message': '消息至少需要10个字符',
    'contact.metaTitle': '联系我们',
    'contact.metaDesc': '联系 KATOA 团队。',
    'contact.successTitle': '消息已准备好',
    'contact.successTo': '消息正在发送至',
    'contact.composeAgain': '在邮件应用中打开',
    'contact.newMessage': '发送另一条消息',
  },
};

const faqPageStrings = {
  en: {
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Everything about KATOA\'s zero-fee, privacy-first Bitcoin platform',
    'faq.search': 'Search questions...',
    'faq.all': 'All',
    'faq.expandAll': 'Expand all',
    'faq.collapseAll': 'Collapse all',
    'faq.learnMore': 'Learn more →',
    'faq.noResults': 'No questions match your search.',
    'faq.stillHaveQuestions': 'Still have questions?',
    'faq.reachOut': 'Reach out — we\'re here to help.',
    'faq.contactUs': 'Contact Us',
    'faq.github': 'GitHub',
    'faq.metaTitle': 'FAQ',
    'faq.metaDesc': 'Frequently asked questions about KATOA — zero fees, Lightning, privacy, and wishlists.',
    'faq.cat.gettingStarted': 'Getting Started',
    'faq.cat.payments': 'Payments',
    'faq.cat.privacy': 'Privacy',
    'faq.cat.features': 'Features',
    'faq.cat.technical': 'Technical',
    'faq.cat.security': 'Security',
    'faq.cat.trust': 'Trust & fees',
    'faq.q.whatIs': 'What is KATOA?',
    'faq.a.whatIs': 'KATOA is a privacy-centric, zero-fee creator platform powered by Bitcoin Lightning and Nostr. Create wishlists, receive gifts, and keep 100% of what supporters send.',
    'faq.q.createAccount': 'How do I create an account?',
    'faq.a.createAccount': 'Sign up with email or Google, or try demo mode when Supabase is not configured. Secure Nostr sign-in is coming (challenge-based); you can still link a Nostr key in Settings after email/Google login.',
    'faq.q.lightning': 'What is the Lightning Network?',
    'faq.a.lightning': 'Lightning is Bitcoin\'s fast payment layer. KATOA settles in seconds once Lightning receive ships (Roadmap: In progress).',
    'faq.q.zeroFees': 'Are there really zero fees?',
    'faq.a.zeroFees': 'Yes. KATOA charges 0% platform fees through 31 Dec 2026. You only pay tiny Lightning network fees.',
    'faq.q.bolt12': 'What is BOLT 12?',
    'faq.a.bolt12': 'BOLT 12 enables reusable payment offers and better privacy on Lightning. KATOA is building toward full BOLT 12 support — recurring Lightning settlement is not live yet.',
    'faq.q.privacy': 'How does KATOA protect my privacy?',
    'faq.a.privacy': 'Nostr identity, minimal data collection, and a roadmap toward stronger payment privacy with PayNyms and ZK proofs.',
    'faq.q.nostr': 'What is Nostr?',
    'faq.a.nostr': 'A decentralized protocol for identity and messaging. You own your keys — no company can ban your account.',
    'faq.q.kyc': 'Do I need KYC?',
    'faq.a.kyc': 'No. Start with a username or Nostr key. Bitcoin does the settlement.',
  },
  es: {
    'faq.title': 'Preguntas frecuentes',
    'faq.subtitle': 'Todo sobre la plataforma Bitcoin sin comisiones y con privacidad de KATOA',
    'faq.search': 'Buscar preguntas...',
    'faq.all': 'Todas',
    'faq.expandAll': 'Expandir todo',
    'faq.collapseAll': 'Contraer todo',
    'faq.learnMore': 'Saber más →',
    'faq.noResults': 'Ninguna pregunta coincide con tu búsqueda.',
    'faq.stillHaveQuestions': '¿Aún tienes preguntas?',
    'faq.reachOut': 'Escríbenos — estamos para ayudarte.',
    'faq.contactUs': 'Contáctanos',
    'faq.github': 'GitHub',
    'faq.metaTitle': 'Preguntas frecuentes',
    'faq.metaDesc': 'Preguntas frecuentes sobre KATOA: cero comisiones, Lightning, privacidad y listas de deseos.',
    'faq.cat.gettingStarted': 'Primeros pasos',
    'faq.cat.payments': 'Pagos',
    'faq.cat.privacy': 'Privacidad',
    'faq.cat.features': 'Funciones',
    'faq.cat.technical': 'Técnico',
    'faq.cat.security': 'Seguridad',
    'faq.cat.trust': 'Confianza y comisiones',
    'faq.q.whatIs': '¿Qué es KATOA?',
    'faq.a.whatIs': 'KATOA es una plataforma para creadores centrada en la privacidad y sin comisiones, impulsada por Bitcoin Lightning y Nostr. Crea listas de deseos, recibe regalos y quédate el 100% de lo que envían tus fans.',
    'faq.q.createAccount': '¿Cómo creo una cuenta?',
    'faq.a.createAccount': 'Regístrate con email o Google, o prueba el modo demo si Supabase no está configurado. El acceso seguro con Nostr está en camino (con desafío); puedes vincular una clave Nostr en Ajustes después de entrar con email o Google.',
    'faq.q.lightning': '¿Qué es Lightning Network?',
    'faq.a.lightning': 'Lightning es la capa de pagos rápida de Bitcoin. KATOA liquida en segundos cuando la recepción Lightning esté activa (Roadmap: en curso).',
    'faq.q.zeroFees': '¿De verdad no hay comisiones?',
    'faq.a.zeroFees': 'Sí. KATOA cobra 0% de comisión de plataforma hasta el 31 dic 2026. Solo pagas las minúsculas comisiones de la red Lightning.',
    'faq.q.bolt12': '¿Qué es BOLT 12?',
    'faq.a.bolt12': 'BOLT 12 permite ofertas de pago reutilizables y más privacidad en Lightning. KATOA avanza hacia un soporte completo de BOLT 12: la liquidación recurrente por Lightning aún no está en vivo.',
    'faq.q.privacy': '¿Cómo protege KATOA mi privacidad?',
    'faq.a.privacy': 'Identidad Nostr, recopilación mínima de datos y una hoja de ruta hacia pagos más privados con PayNyms y pruebas de conocimiento cero.',
    'faq.q.nostr': '¿Qué es Nostr?',
    'faq.a.nostr': 'Un protocolo descentralizado de identidad y mensajería. Tú tienes las claves: ninguna empresa puede banear tu cuenta.',
    'faq.q.kyc': '¿Necesito KYC?',
    'faq.a.kyc': 'No. Empieza con un nombre de usuario o una clave Nostr. Bitcoin se encarga de la liquidación.',
  },
  pt: {
    'faq.title': 'Perguntas frequentes',
    'faq.subtitle': 'Tudo sobre a plataforma Bitcoin sem taxas e com privacidade da KATOA',
    'faq.search': 'Buscar perguntas...',
    'faq.all': 'Todas',
    'faq.expandAll': 'Expandir tudo',
    'faq.collapseAll': 'Recolher tudo',
    'faq.learnMore': 'Saiba mais →',
    'faq.noResults': 'Nenhuma pergunta corresponde à sua busca.',
    'faq.stillHaveQuestions': 'Ainda tem dúvidas?',
    'faq.reachOut': 'Fale conosco — estamos aqui para ajudar.',
    'faq.contactUs': 'Fale conosco',
    'faq.github': 'GitHub',
    'faq.metaTitle': 'Perguntas frequentes',
    'faq.metaDesc': 'Perguntas frequentes sobre a KATOA — zero taxas, Lightning, privacidade e listas de desejos.',
    'faq.cat.gettingStarted': 'Primeiros passos',
    'faq.cat.payments': 'Pagamentos',
    'faq.cat.privacy': 'Privacidade',
    'faq.cat.features': 'Recursos',
    'faq.cat.technical': 'Técnico',
    'faq.cat.security': 'Segurança',
    'faq.cat.trust': 'Confiança e taxas',
    'faq.q.whatIs': 'O que é a KATOA?',
    'faq.a.whatIs': 'KATOA é uma plataforma para criadores centrada em privacidade e sem taxas, com Bitcoin Lightning e Nostr. Crie listas de desejos, receba presentes e fique com 100% do que os fãs enviarem.',
    'faq.q.createAccount': 'Como crio uma conta?',
    'faq.a.createAccount': 'Cadastre-se com e-mail ou Google, ou use o modo demo se o Supabase não estiver configurado. O login seguro com Nostr está a caminho (com desafio); você já pode vincular uma chave Nostr em Configurações depois de entrar com e-mail ou Google.',
    'faq.q.lightning': 'O que é a Lightning Network?',
    'faq.a.lightning': 'Lightning é a camada de pagamentos rápida do Bitcoin. A KATOA liquida em segundos assim que a recepção Lightning estiver ativa (Roadmap: em andamento).',
    'faq.q.zeroFees': 'É mesmo taxa zero?',
    'faq.a.zeroFees': 'Sim. A KATOA cobra 0% de taxa da plataforma até 31 dez 2026. Você só paga as pequenas taxas da rede Lightning.',
    'faq.q.bolt12': 'O que é BOLT 12?',
    'faq.a.bolt12': 'BOLT 12 permite ofertas de pagamento reutilizáveis e mais privacidade no Lightning. A KATOA está construindo suporte completo a BOLT 12 — liquidação recorrente via Lightning ainda não está ao vivo.',
    'faq.q.privacy': 'Como a KATOA protege minha privacidade?',
    'faq.a.privacy': 'Identidade Nostr, coleta mínima de dados e um roteiro rumo a pagamentos mais privados com PayNyms e provas de conhecimento zero.',
    'faq.q.nostr': 'O que é Nostr?',
    'faq.a.nostr': 'Um protocolo descentralizado de identidade e mensagens. Você tem as chaves — nenhuma empresa pode banir sua conta.',
    'faq.q.kyc': 'Preciso de KYC?',
    'faq.a.kyc': 'Não. Comece com um nome de usuário ou uma chave Nostr. O Bitcoin faz a liquidação.',
  },
  fr: {
    'faq.title': 'Questions fréquentes',
    'faq.subtitle': 'Tout sur la plateforme Bitcoin sans frais et axée sur la confidentialité de KATOA',
    'faq.search': 'Rechercher des questions...',
    'faq.all': 'Toutes',
    'faq.expandAll': 'Tout développer',
    'faq.collapseAll': 'Tout réduire',
    'faq.learnMore': 'En savoir plus →',
    'faq.noResults': 'Aucune question ne correspond à votre recherche.',
    'faq.stillHaveQuestions': 'Encore des questions ?',
    'faq.reachOut': 'Contactez-nous — nous sommes là pour vous aider.',
    'faq.contactUs': 'Nous contacter',
    'faq.github': 'GitHub',
    'faq.metaTitle': 'FAQ',
    'faq.metaDesc': 'Questions fréquentes sur KATOA — zéro frais, Lightning, confidentialité et listes de souhaits.',
    'faq.cat.gettingStarted': 'Premiers pas',
    'faq.cat.payments': 'Paiements',
    'faq.cat.privacy': 'Confidentialité',
    'faq.cat.features': 'Fonctionnalités',
    'faq.cat.technical': 'Technique',
    'faq.cat.security': 'Sécurité',
    'faq.cat.trust': 'Confiance et frais',
    'faq.q.whatIs': 'Qu’est-ce que KATOA ?',
    'faq.a.whatIs': 'KATOA est une plateforme pour créateurs centrée sur la confidentialité et sans frais, propulsée par Bitcoin Lightning et Nostr. Créez des listes de souhaits, recevez des dons et gardez 100 % de ce que vos fans envoient.',
    'faq.q.createAccount': 'Comment créer un compte ?',
    'faq.a.createAccount': 'Inscrivez-vous par e-mail ou Google, ou essayez le mode démo si Supabase n’est pas configuré. La connexion Nostr sécurisée arrive (par défi) ; vous pouvez déjà lier une clé Nostr dans les Paramètres après une connexion e-mail/Google.',
    'faq.q.lightning': 'Qu’est-ce que le Lightning Network ?',
    'faq.a.lightning': 'Lightning est la couche de paiement rapide de Bitcoin. KATOA règle en quelques secondes dès que la réception Lightning sera en ligne (Roadmap : en cours).',
    'faq.q.zeroFees': 'Y a-t-il vraiment zéro frais ?',
    'faq.a.zeroFees': 'Oui. KATOA prélève 0 % de frais de plateforme jusqu’au 31 déc. 2026. Vous ne payez que les infimes frais du réseau Lightning.',
    'faq.q.bolt12': 'Qu’est-ce que BOLT 12 ?',
    'faq.a.bolt12': 'BOLT 12 permet des offres de paiement réutilisables et plus de confidentialité sur Lightning. KATOA construit un support BOLT 12 complet — le règlement Lightning récurrent n’est pas encore en production.',
    'faq.q.privacy': 'Comment KATOA protège-t-il ma vie privée ?',
    'faq.a.privacy': 'Identité Nostr, collecte minimale de données, et une feuille de route vers des paiements plus privés avec PayNyms et preuves à connaissance nulle.',
    'faq.q.nostr': 'Qu’est-ce que Nostr ?',
    'faq.a.nostr': 'Un protocole décentralisé d’identité et de messagerie. Vous détenez vos clés — aucune entreprise ne peut bannir votre compte.',
    'faq.q.kyc': 'Ai-je besoin d’un KYC ?',
    'faq.a.kyc': 'Non. Commencez avec un nom d’utilisateur ou une clé Nostr. Bitcoin assure le règlement.',
  },
  de: {
    'faq.title': 'Häufig gestellte Fragen',
    'faq.subtitle': 'Alles über KATOAs gebührenfreie, datenschutzorientierte Bitcoin-Plattform',
    'faq.search': 'Fragen suchen...',
    'faq.all': 'Alle',
    'faq.expandAll': 'Alle aufklappen',
    'faq.collapseAll': 'Alle zuklappen',
    'faq.learnMore': 'Mehr erfahren →',
    'faq.noResults': 'Keine Fragen entsprechen Ihrer Suche.',
    'faq.stillHaveQuestions': 'Noch Fragen?',
    'faq.reachOut': 'Melde dich — wir helfen gerne.',
    'faq.contactUs': 'Kontakt',
    'faq.github': 'GitHub',
    'faq.metaTitle': 'FAQ',
    'faq.metaDesc': 'Häufige Fragen zu KATOA — null Gebühren, Lightning, Privatsphäre und Wunschlisten.',
    'faq.cat.gettingStarted': 'Erste Schritte',
    'faq.cat.payments': 'Zahlungen',
    'faq.cat.privacy': 'Privatsphäre',
    'faq.cat.features': 'Funktionen',
    'faq.cat.technical': 'Technik',
    'faq.cat.security': 'Sicherheit',
    'faq.cat.trust': 'Vertrauen & Gebühren',
    'faq.q.whatIs': 'Was ist KATOA?',
    'faq.a.whatIs': 'KATOA ist eine datenschutzorientierte, gebührenfreie Creator-Plattform auf Bitcoin Lightning und Nostr. Erstelle Wunschlisten, empfange Geschenke und behalte 100 % dessen, was Unterstützer senden.',
    'faq.q.createAccount': 'Wie erstelle ich ein Konto?',
    'faq.a.createAccount': 'Melde dich mit E-Mail oder Google an, oder nutze den Demo-Modus, wenn Supabase nicht konfiguriert ist. Sicheres Nostr-Login kommt (challenge-basiert); du kannst schon jetzt einen Nostr-Schlüssel in den Einstellungen nach E-Mail-/Google-Login verknüpfen.',
    'faq.q.lightning': 'Was ist das Lightning Network?',
    'faq.a.lightning': 'Lightning ist Bitcoins schnelle Zahlungsschicht. KATOA rechnet in Sekunden ab, sobald der Lightning-Empfang live ist (Roadmap: in Arbeit).',
    'faq.q.zeroFees': 'Gibt es wirklich null Gebühren?',
    'faq.a.zeroFees': 'Ja. KATOA erhebt bis zum 31. Dez. 2026 0 % Plattformgebühr. Du zahlst nur winzige Lightning-Netzwerkgebühren.',
    'faq.q.bolt12': 'Was ist BOLT 12?',
    'faq.a.bolt12': 'BOLT 12 ermöglicht wiederverwendbare Zahlungsangebote und mehr Privatsphäre auf Lightning. KATOA baut vollen BOLT-12-Support — wiederkehrende Lightning-Abrechnung ist noch nicht live.',
    'faq.q.privacy': 'Wie schützt KATOA meine Privatsphäre?',
    'faq.a.privacy': 'Nostr-Identität, minimale Datenerfassung und eine Roadmap zu stärkerer Zahlungsprivatsphäre mit PayNyms und ZK-Proofs.',
    'faq.q.nostr': 'Was ist Nostr?',
    'faq.a.nostr': 'Ein dezentrales Protokoll für Identität und Nachrichten. Du besitzt deine Schlüssel — keine Firma kann dein Konto sperren.',
    'faq.q.kyc': 'Brauche ich KYC?',
    'faq.a.kyc': 'Nein. Starte mit einem Nutzernamen oder einem Nostr-Schlüssel. Bitcoin übernimmt die Abrechnung.',
  },
  ja: {
    'faq.title': 'よくある質問',
    'faq.subtitle': '手数料ゼロ・プライバシー重視のKATOA Bitcoinプラットフォームについて',
    'faq.search': '質問を検索...',
    'faq.all': 'すべて',
    'faq.expandAll': 'すべて展開',
    'faq.collapseAll': 'すべて折りたたむ',
    'faq.learnMore': '詳しく見る →',
    'faq.noResults': '検索に一致する質問がありません。',
    'faq.stillHaveQuestions': 'まだ質問がありますか？',
    'faq.reachOut': 'お気軽にご連絡ください。',
    'faq.contactUs': 'お問い合わせ',
    'faq.github': 'GitHub',
    'faq.metaTitle': 'よくある質問',
    'faq.metaDesc': 'KATOAのよくある質問 — 手数料ゼロ、Lightning、プライバシー、ウィッシュリスト。',
    'faq.cat.gettingStarted': 'はじめに',
    'faq.cat.payments': '支払い',
    'faq.cat.privacy': 'プライバシー',
    'faq.cat.features': '機能',
    'faq.cat.technical': '技術',
    'faq.cat.security': 'セキュリティ',
    'faq.cat.trust': '信頼と手数料',
    'faq.q.whatIs': 'KATOAとは？',
    'faq.a.whatIs': 'KATOAは、Bitcoin LightningとNostrを使ったプライバシー重視・手数料ゼロのクリエイター基盤です。ウィッシュリストを作り、ギフトを受け取り、サポーターが送った額の100%を手元に残せます。',
    'faq.q.createAccount': 'アカウントはどう作りますか？',
    'faq.a.createAccount': 'メールまたはGoogleで登録するか、Supabase未設定時はデモモードを使えます。安全なNostrログインは準備中（チャレンジ方式）です。メール/Googleで入ったあと、設定でNostr鍵を紐づけできます。',
    'faq.q.lightning': 'Lightning Networkとは？',
    'faq.a.lightning': 'LightningはBitcoinの高速決済レイヤーです。KATOAはLightning受信の提供開始後、数秒で決済します（Roadmap: 進行中）。',
    'faq.q.zeroFees': '本当に手数料ゼロですか？',
    'faq.a.zeroFees': 'はい。KATOAのプラットフォーム手数料は2026年12月31日まで0%です。支払うのはごく小さなLightningネットワーク手数料だけです。',
    'faq.q.bolt12': 'BOLT 12とは？',
    'faq.a.bolt12': 'BOLT 12は再利用できる支払いオファーと、より高いプライバシーをLightning上で実現します。KATOAは完全対応を進めていますが、定期Lightning決済はまだ本番稼働していません。',
    'faq.q.privacy': 'KATOAはプライバシーをどう守りますか？',
    'faq.a.privacy': 'Nostrの身元、最小限のデータ収集、そしてPayNymsやゼロ知識証明による支払いプライバシー強化のロードマップです。',
    'faq.q.nostr': 'Nostrとは？',
    'faq.a.nostr': '身元とメッセージのための分散プロトコルです。鍵はあなたのもの — 企業がアカウントをBANすることはできません。',
    'faq.q.kyc': 'KYCは必要ですか？',
    'faq.a.kyc': '不要です。ユーザー名またはNostr鍵で始められます。決済はBitcoinが行います。',
  },
  zh: {
    'faq.title': '常见问题',
    'faq.subtitle': '关于KATOA零费用、隐私优先的比特币平台的一切',
    'faq.search': '搜索问题...',
    'faq.all': '全部',
    'faq.expandAll': '全部展开',
    'faq.collapseAll': '全部收起',
    'faq.learnMore': '了解更多 →',
    'faq.noResults': '没有匹配您搜索的问题。',
    'faq.stillHaveQuestions': '还有问题？',
    'faq.reachOut': '联系我们 — 我们随时为您提供帮助。',
    'faq.contactUs': '联系我们',
    'faq.github': 'GitHub',
    'faq.metaTitle': '常见问题',
    'faq.metaDesc': '关于 KATOA 的常见问题 — 零费用、闪电网络、隐私与心愿单。',
    'faq.cat.gettingStarted': '入门',
    'faq.cat.payments': '支付',
    'faq.cat.privacy': '隐私',
    'faq.cat.features': '功能',
    'faq.cat.technical': '技术',
    'faq.cat.security': '安全',
    'faq.cat.trust': '信任与费用',
    'faq.q.whatIs': '什么是 KATOA？',
    'faq.a.whatIs': 'KATOA 是以隐私为先、零费用的创作者平台，基于比特币闪电网络和 Nostr。创建心愿单、接收礼物，支持者送来的金额你保留 100%。',
    'faq.q.createAccount': '如何创建账户？',
    'faq.a.createAccount': '用邮箱或 Google 注册；若未配置 Supabase，可试用演示模式。安全的 Nostr 登录即将推出（挑战式）；用邮箱/Google 登录后，仍可在设置中绑定 Nostr 密钥。',
    'faq.q.lightning': '什么是闪电网络？',
    'faq.a.lightning': '闪电网络是比特币的快速支付层。Lightning 收款上线后，KATOA 可在数秒内完成结算（路线图：进行中）。',
    'faq.q.zeroFees': '真的是零费用吗？',
    'faq.a.zeroFees': '是的。KATOA 至2026年12月31日收取 0% 平台抽成。你只需支付极小的闪电网络费用。',
    'faq.q.bolt12': '什么是 BOLT 12？',
    'faq.a.bolt12': 'BOLT 12 可实现可复用的支付要约，并提高闪电网络上的隐私。KATOA 正在建设完整 BOLT 12 支持 — 定期闪电结算尚未上线。',
    'faq.q.privacy': 'KATOA 如何保护我的隐私？',
    'faq.a.privacy': 'Nostr 身份、最少数据采集，以及通过 PayNyms 和零知识证明加强支付隐私的路线图。',
    'faq.q.nostr': '什么是 Nostr？',
    'faq.a.nostr': '去中心化的身份与消息协议。密钥归你所有 — 没有公司能封禁你的账户。',
    'faq.q.kyc': '需要 KYC 吗？',
    'faq.a.kyc': '不需要。用用户名或 Nostr 密钥即可开始。结算由比特币完成。',
  },
};

const pricingPageStrings = {
  en: {
    'pricing.metaTitle': 'Pricing',
    'pricing.metaDesc': 'KATOA pricing: $0/month, 0% platform fees through 31 Dec 2026. Keep 100% of your Bitcoin earnings on Lightning.',
    'pricing.hero.fees': '0% platform fees through 31 December 2026',
    'pricing.hero.body': 'For every creator, nonprofit and cause — absorbed by Give A Bit and community support; Lightning/network fees paid by supporters.',
    'pricing.feat.zeroFees.title': '0% Platform Fees',
    'pricing.feat.zeroFees.desc': 'Keep every sat you earn. No tiers, no upsells, no surprises—ever.',
    'pricing.feat.lightning.title': 'Bitcoin Lightning',
    'pricing.feat.lightning.desc': 'Lightning settlement in seconds once Lightning receive ships (Roadmap: In progress). No 7-day holds.',
    'pricing.feat.global.title': 'Global by Default',
    'pricing.feat.global.desc': 'Accept support from 195+ countries without currency conversion fees.',
    'pricing.feat.noBank.title': 'No Banking Required',
    'pricing.feat.noBank.desc': 'Start without a bank account, KYC, or payment processor approval.',
    'pricing.feat.onchain.title': 'On-Chain & Lightning',
    'pricing.feat.onchain.desc': 'Supporters pay however their wallet works—main chain or L2.',
    'pricing.feat.unlimited.title': 'Unlimited Wishlists',
    'pricing.feat.unlimited.desc': 'Unlimited wishlists while Give A Bit covers storage; we\'ll publish any cap in advance if infra costs change.',
    'pricing.feat.dashboard.title': 'Creator dashboard',
    'pricing.feat.dashboard.desc': 'Track gifts, followers, and goals on your dashboard. Honest numbers — demo labeled until live counters exist.',
    'pricing.feat.shareable.title': 'Shareable Pages',
    'pricing.feat.shareable.desc': 'Beautiful public wishlist pages with QR codes and social-ready links.',
    'pricing.feat.privacy.title': 'Privacy First',
    'pricing.feat.privacy.desc': 'Decentralized infrastructure. You own your keys, data, and audience.',
    'pricing.feat.subscriptions.title': 'Subscription tiers (path)',
    'pricing.feat.subscriptions.desc': 'Show supporter / patron / champion tiers today. Recurring Lightning (BOLT 12) is planned — not live settlement yet.',
  },
  es: {
    'pricing.metaTitle': 'Precios',
    'pricing.metaDesc': 'Precios de KATOA: 0 $/mes, 0% de comisión de plataforma hasta el 31 dic 2026. Quédate el 100% de tus ganancias Bitcoin en Lightning.',
    'pricing.hero.fees': '0% de comisión de plataforma hasta el 31 de diciembre de 2026',
    'pricing.hero.body': 'Para cada creador, ONG y causa — absorbido por Give A Bit y el apoyo de la comunidad; las comisiones de Lightning las pagan los seguidores.',
    'pricing.feat.zeroFees.title': '0% de comisión de plataforma',
    'pricing.feat.zeroFees.desc': 'Quédate cada sat que ganes. Sin niveles, sin upsells, sin sorpresas — nunca.',
    'pricing.feat.lightning.title': 'Bitcoin Lightning',
    'pricing.feat.lightning.desc': 'Liquidación Lightning en segundos una vez que la recepción esté disponible (Roadmap: En curso). Sin retenciones de 7 días.',
    'pricing.feat.global.title': 'Global por defecto',
    'pricing.feat.global.desc': 'Recibe apoyo de más de 195 países sin comisiones de conversión de moneda.',
    'pricing.feat.noBank.title': 'Sin banco',
    'pricing.feat.noBank.desc': 'Empieza sin cuenta bancaria, KYC ni aprobación de un procesador de pagos.',
    'pricing.feat.onchain.title': 'On-chain y Lightning',
    'pricing.feat.onchain.desc': 'Los fans pagan como funcione su cartera: cadena principal o L2.',
    'pricing.feat.unlimited.title': 'Listas ilimitadas',
    'pricing.feat.unlimited.desc': 'Listas ilimitadas mientras Give A Bit cubre el almacenamiento; publicaremos cualquier límite con antelación si cambian los costes de infraestructura.',
    'pricing.feat.dashboard.title': 'Panel del creador',
    'pricing.feat.dashboard.desc': 'Sigue regalos, seguidores y metas en tu panel. Números honestos: lo demo se etiqueta hasta que existan contadores en vivo.',
    'pricing.feat.shareable.title': 'Páginas para compartir',
    'pricing.feat.shareable.desc': 'Páginas públicas de listas con códigos QR y enlaces listos para redes.',
    'pricing.feat.privacy.title': 'Privacidad primero',
    'pricing.feat.privacy.desc': 'Infraestructura descentralizada. Tú tienes las claves, los datos y tu audiencia.',
    'pricing.feat.subscriptions.title': 'Niveles de suscripción (camino)',
    'pricing.feat.subscriptions.desc': 'Muestra hoy los niveles supporter / patron / champion. Lightning recurrente (BOLT 12) está previsto — aún no hay liquidación en vivo.',
  },
  pt: {
    'pricing.metaTitle': 'Preços',
    'pricing.metaDesc': 'Preços da KATOA: US$ 0/mês, 0% de taxa da plataforma até 31 dez 2026. Fique com 100% dos ganhos em Bitcoin via Lightning.',
    'pricing.hero.fees': '0% de taxa da plataforma até 31 de dezembro de 2026',
    'pricing.hero.body': 'Para cada criador, ONG e causa — absorvido pela Give A Bit e pelo apoio da comunidade; as taxas da Lightning são pagas pelos apoiadores.',
    'pricing.feat.zeroFees.title': '0% de taxa da plataforma',
    'pricing.feat.zeroFees.desc': 'Fique com cada sat que ganhar. Sem níveis, sem upsells, sem surpresas — nunca.',
    'pricing.feat.lightning.title': 'Bitcoin Lightning',
    'pricing.feat.lightning.desc': 'Liquidação Lightning em segundos assim que a recepção estiver disponível (Roadmap: Em andamento). Sem retenção de 7 dias.',
    'pricing.feat.global.title': 'Global por padrão',
    'pricing.feat.global.desc': 'Receba apoio de 195+ países sem taxas de conversão de moeda.',
    'pricing.feat.noBank.title': 'Sem banco',
    'pricing.feat.noBank.desc': 'Comece sem conta bancária, KYC ou aprovação de processador de pagamento.',
    'pricing.feat.onchain.title': 'On-chain e Lightning',
    'pricing.feat.onchain.desc': 'Os fãs pagam como a carteira funcionar — cadeia principal ou L2.',
    'pricing.feat.unlimited.title': 'Listas ilimitadas',
    'pricing.feat.unlimited.desc': 'Listas ilimitadas enquanto a Give A Bit cobre o armazenamento; publicaremos qualquer limite com antecedência se os custos de infraestrutura mudarem.',
    'pricing.feat.dashboard.title': 'Painel do criador',
    'pricing.feat.dashboard.desc': 'Acompanhe presentes, seguidores e metas no painel. Números honestos — o demo é rotulado até existirem contadores ao vivo.',
    'pricing.feat.shareable.title': 'Páginas compartilháveis',
    'pricing.feat.shareable.desc': 'Páginas públicas de listas com QR codes e links prontos para redes.',
    'pricing.feat.privacy.title': 'Privacidade em primeiro lugar',
    'pricing.feat.privacy.desc': 'Infraestrutura descentralizada. Você tem as chaves, os dados e o público.',
    'pricing.feat.subscriptions.title': 'Níveis de assinatura (caminho)',
    'pricing.feat.subscriptions.desc': 'Mostre hoje os níveis supporter / patron / champion. Lightning recorrente (BOLT 12) está previsto — liquidação ao vivo ainda não.',
  },
  fr: {
    'pricing.metaTitle': 'Tarifs',
    'pricing.metaDesc': 'Tarifs KATOA : 0 $/mois, 0 % de frais de plateforme jusqu’au 31 déc. 2026. Gardez 100 % de vos revenus Bitcoin via Lightning.',
    'pricing.hero.fees': '0 % de frais de plateforme jusqu’au 31 décembre 2026',
    'pricing.hero.body': 'Pour chaque créateur, association et cause — pris en charge par Give A Bit et le soutien de la communauté ; les frais Lightning sont payés par les soutiens.',
    'pricing.feat.zeroFees.title': '0 % de frais de plateforme',
    'pricing.feat.zeroFees.desc': 'Gardez chaque sat gagné. Pas de paliers, pas d’upsell, pas de surprise — jamais.',
    'pricing.feat.lightning.title': 'Bitcoin Lightning',
    'pricing.feat.lightning.desc': 'Règlement Lightning en secondes dès que la réception est disponible (Roadmap : en cours). Pas de gel de 7 jours.',
    'pricing.feat.global.title': 'Mondial par défaut',
    'pricing.feat.global.desc': 'Acceptez le soutien de 195+ pays sans frais de conversion de devises.',
    'pricing.feat.noBank.title': 'Sans banque',
    'pricing.feat.noBank.desc': 'Démarrez sans compte bancaire, KYC ni agrément d’un processeur de paiement.',
    'pricing.feat.onchain.title': 'On-chain et Lightning',
    'pricing.feat.onchain.desc': 'Les fans paient comme leur portefeuille le permet — chaîne principale ou L2.',
    'pricing.feat.unlimited.title': 'Listes illimitées',
    'pricing.feat.unlimited.desc': 'Listes illimitées tant que Give A Bit couvre le stockage ; nous publierons tout plafond à l\'avance si les coûts d\'infrastructure changent.',
    'pricing.feat.dashboard.title': 'Tableau de bord créateur',
    'pricing.feat.dashboard.desc': 'Suivez dons, abonnés et objectifs. Chiffres honnêtes — le démo est étiqueté tant qu’il n’y a pas de compteurs en direct.',
    'pricing.feat.shareable.title': 'Pages partageables',
    'pricing.feat.shareable.desc': 'Belles pages publiques de listes avec QR codes et liens prêts pour les réseaux.',
    'pricing.feat.privacy.title': 'Confidentialité d’abord',
    'pricing.feat.privacy.desc': 'Infrastructure décentralisée. Vous détenez vos clés, vos données et votre audience.',
    'pricing.feat.subscriptions.title': 'Paliers d’abonnement (chemin)',
    'pricing.feat.subscriptions.desc': 'Affichez dès aujourd’hui les paliers supporter / patron / champion. Le Lightning récurrent (BOLT 12) est prévu — pas encore de règlement en production.',
  },
  de: {
    'pricing.metaTitle': 'Preise',
    'pricing.metaDesc': 'KATOA-Preise: 0 $/Monat, 0 % Plattformgebühr bis zum 31. Dez. 2026. Behalte 100 % deiner Bitcoin-Einnahmen über Lightning.',
    'pricing.hero.fees': '0 % Plattformgebühr bis zum 31. Dezember 2026',
    'pricing.hero.body': 'Für jeden Creator, jede Organisation und jede Sache — getragen von Give A Bit und Community-Unterstützung; Lightning-/Netzwerkgebühren zahlen die Unterstützer.',
    'pricing.feat.zeroFees.title': '0 % Plattformgebühr',
    'pricing.feat.zeroFees.desc': 'Behalte jeden Sat, den du verdienst. Keine Stufen, kein Upsell, keine Überraschungen — nie.',
    'pricing.feat.lightning.title': 'Bitcoin Lightning',
    'pricing.feat.lightning.desc': 'Lightning-Abwicklung in Sekunden, sobald der Empfang verfügbar ist (Roadmap: in Arbeit). Keine 7-Tage-Sperren.',
    'pricing.feat.global.title': 'Global standardmäßig',
    'pricing.feat.global.desc': 'Nimm Unterstützung aus 195+ Ländern an — ohne Währungsumrechnungsgebühren.',
    'pricing.feat.noBank.title': 'Kein Bankkonto nötig',
    'pricing.feat.noBank.desc': 'Starte ohne Bankkonto, KYC oder Freigabe eines Zahlungsdienstleisters.',
    'pricing.feat.onchain.title': 'On-Chain & Lightning',
    'pricing.feat.onchain.desc': 'Unterstützer zahlen, wie ihre Wallet funktioniert — Hauptkette oder L2.',
    'pricing.feat.unlimited.title': 'Unbegrenzte Wunschlisten',
    'pricing.feat.unlimited.desc': 'Unbegrenzte Wunschlisten, solange Give A Bit den Speicher trägt; eine Obergrenze veröffentlichen wir vorab, falls sich die Infrastrukturkosten ändern.',
    'pricing.feat.dashboard.title': 'Creator-Dashboard',
    'pricing.feat.dashboard.desc': 'Verfolge Geschenke, Follower und Ziele. Ehrliche Zahlen — Demo ist gekennzeichnet, bis Live-Zähler existieren.',
    'pricing.feat.shareable.title': 'Teilbare Seiten',
    'pricing.feat.shareable.desc': 'Schöne öffentliche Wunschlisten mit QR-Codes und social-ready Links.',
    'pricing.feat.privacy.title': 'Privatsphäre zuerst',
    'pricing.feat.privacy.desc': 'Dezentrale Infrastruktur. Du besitzt deine Schlüssel, Daten und dein Publikum.',
    'pricing.feat.subscriptions.title': 'Abo-Stufen (Pfad)',
    'pricing.feat.subscriptions.desc': 'Zeige heute Supporter-/Patron-/Champion-Stufen. Wiederkehrendes Lightning (BOLT 12) ist geplant — noch keine Live-Abrechnung.',
  },
  ja: {
    'pricing.metaTitle': '料金',
    'pricing.metaDesc': 'KATOAの料金：月額$0、プラットフォーム手数料0%（2026年12月31日まで）。LightningでBitcoin収益の100%を手元に。',
    'pricing.hero.fees': 'プラットフォーム手数料0%（2026年12月31日まで）',
    'pricing.hero.body': 'すべてのクリエイター・非営利団体・活動のために — Give A Bit とコミュニティの支援で負担。Lightning／ネットワーク手数料は支援者が負担します。',
    'pricing.feat.zeroFees.title': 'プラットフォーム手数料0%',
    'pricing.feat.zeroFees.desc': '稼いだsatsはすべて手元に。段階課金もアップセルも、サプライズもありません。',
    'pricing.feat.lightning.title': 'Bitcoin Lightning',
    'pricing.feat.lightning.desc': '受信機能が提供されればLightningで数秒で決済します（ロードマップ：進行中）。7日間の保留はありません。',
    'pricing.feat.global.title': '最初からグローバル',
    'pricing.feat.global.desc': '195か国以上からの支援を、為替手数料なしで受け取れます。',
    'pricing.feat.noBank.title': '銀行不要',
    'pricing.feat.noBank.desc': '銀行口座、KYC、決済代行の承認なしで始められます。',
    'pricing.feat.onchain.title': 'オンチェーンとLightning',
    'pricing.feat.onchain.desc': 'サポーターはウォレットに合わせて支払えます — メインチェーンでもL2でも。',
    'pricing.feat.unlimited.title': '無制限のウィッシュリスト',
    'pricing.feat.unlimited.desc': 'Give A Bitがストレージを負担する間は無制限。インフラ費用が変われば、上限を事前に公表します。',
    'pricing.feat.dashboard.title': 'クリエイターダッシュボード',
    'pricing.feat.dashboard.desc': 'ギフト、フォロワー、目標をダッシュボードで追跡。数字は正直に — ライブ計数が揃うまでデモと表示します。',
    'pricing.feat.shareable.title': '共有できるページ',
    'pricing.feat.shareable.desc': 'QRコードとSNS向けリンク付きの公開ウィッシュリストページ。',
    'pricing.feat.privacy.title': 'プライバシー優先',
    'pricing.feat.privacy.desc': '分散インフラ。鍵、データ、オーディエンスはあなたのもの。',
    'pricing.feat.subscriptions.title': 'サブスク段階（予定の道）',
    'pricing.feat.subscriptions.desc': 'supporter / patron / champion の段階は今日表示できます。定期Lightning（BOLT 12）は計画中で、本番の着金はまだです。',
  },
  zh: {
    'pricing.metaTitle': '定价',
    'pricing.metaDesc': 'KATOA 定价：每月 $0，平台抽成 0%（至2026年12月31日）。通过闪电网络保留比特币收入的 100%。',
    'pricing.hero.fees': '平台抽成 0%（至2026年12月31日）',
    'pricing.hero.body': '面向每位创作者、公益组织与项目——由 Give A Bit 与社区支持承担；闪电网络手续费由支持者支付。',
    'pricing.feat.zeroFees.title': '平台抽成 0%',
    'pricing.feat.zeroFees.desc': '赚到的每一聪都归你。没有档位、没有加价、没有意外。',
    'pricing.feat.lightning.title': '比特币闪电网络',
    'pricing.feat.lightning.desc': '接收功能上线后，通过闪电网络在数秒内完成结算（路线图：进行中）。没有 7 天冻结。',
    'pricing.feat.global.title': '默认全球可用',
    'pricing.feat.global.desc': '接受来自 195+ 个国家的支持，没有货币兑换费。',
    'pricing.feat.noBank.title': '无需银行',
    'pricing.feat.noBank.desc': '无需银行账户、KYC 或支付公司审批即可开始。',
    'pricing.feat.onchain.title': '链上与闪电网络',
    'pricing.feat.onchain.desc': '支持者按钱包能力付款 — 主链或二层均可。',
    'pricing.feat.unlimited.title': '无限心愿单',
    'pricing.feat.unlimited.desc': 'Give A Bit 负担存储期间心愿单无限制；若基础设施成本变化，我们会提前公布上限。',
    'pricing.feat.dashboard.title': '创作者控制台',
    'pricing.feat.dashboard.desc': '在控制台跟踪礼物、关注者和目标。数字诚实 — 在真实计数上线前会标注演示。',
    'pricing.feat.shareable.title': '可分享页面',
    'pricing.feat.shareable.desc': '带二维码和社交链接的精美公开心愿单页面。',
    'pricing.feat.privacy.title': '隐私优先',
    'pricing.feat.privacy.desc': '去中心化基础设施。密钥、数据和受众归你所有。',
    'pricing.feat.subscriptions.title': '订阅档位（路径）',
    'pricing.feat.subscriptions.desc': '今天即可展示 supporter / patron / champion 档位。定期闪电支付（BOLT 12）已规划 — 尚未上线结算。',
  },
};

const pageStrings = {
  en: {
    'dashboard.title': 'Creator Dashboard',
    'dashboard.subtitle': 'Manage your projects and track your success',
    'dashboard.loading': 'Loading your dashboard...',
    'dashboard.projects': 'Projects',
    'dashboard.wishlists': 'Wishlists',
    'dashboard.raised': 'Total Raised',
    'dashboard.createProject': 'Create Project',
    'dashboard.demo': 'Demo preview — UI only. Connect Supabase to save projects and accept real payments.',
    'wishlist.loading': 'Loading wishlist...',
    'wishlist.notFound': 'Wishlist Not Found',
    'wishlist.notFoundDesc': "This wishlist doesn't exist or has been removed.",
    'wishlist.sendGift': 'Send Gift',
    'wishlist.supportItems': 'Support specific items in this wishlist',
    'wishlist.explore': 'Explore Wishlists',
    'about.hero.line1': 'The Platform That',
    'about.hero.highlight': 'Actually Serves Creators',
    'about.hero.subtitle': "We're not just another platform. We're a movement.",
    'about.hero.badge': 'Built by creators, for creators',
    'about.problem.title': 'The Creator Economy is Broken',
    'about.problem.subtitle': 'Traditional platforms extract too much. Here\'s how much creators lose:',
    'comparison.hero.line1': 'Why Choose',
    'comparison.hero.highlight': 'KATOA?',
    'comparison.hero.subtitle': "We're not just another creator platform. We're built on Bitcoin Lightning, designed for global creators, and committed to 0% platform fees through 31 Dec 2026.",
    'comparison.saveMonthly': 'Save up to ${amount}/month with KATOA',
    'explore.allProjects': 'Explore All Projects',
    'explore.loadMore': 'Load more',
    'explore.remaining': 'remaining',
    'explore.filters': 'Filters',
    'explore.favoritesOnly': 'Favorites only',
    'explore.clearFilters': 'Clear All Filters',
    'explore.searchProjects': 'Search projects, creators, tags...',
    'explore.quickFilters': 'Quick filters:',
    'explore.showResults': 'Show ${count} results',
    'explore.loadingMap': 'Loading map…',
    'explore.videoCreators': 'Video Creators',
    'explore.videoCreatorsDesc': 'Premium video wishlists — hover to preview. Upload MP4 or MOV on your project.',
    'explore.exclusive': 'EXCLUSIVE',
    'explore.video': 'VIDEO',
    'explore.supporters': 'supporters',
    'explore.videosOnly': 'Videos',
    'explore.hoverPreview': 'Hover to preview',
    'explore.creatorCategory': 'Creator',
    'dashboard.videoHint': 'Upload MP4 or MOV cover videos on your wishlists.',
    'dashboard.videoHintLink': 'Browse video creators',
    'video.upload.title': 'Cover Video',
    'video.upload.hint': 'MP4, MOV, WebM — up to 200MB',
    'video.upload.success': 'Video uploaded',
    'video.upload.error': 'Video upload failed',
    'video.upload.tooLarge': 'Video must be under ${max}MB',
    'video.upload.invalid': 'Use MP4, MOV, WebM, or M4V',
    'video.upload.empty': 'Add a cover video',
    'video.upload.drop': 'Drop a video or click to browse',
    'video.upload.change': 'Change video',
    'video.upload.remove': 'Remove video',
    'cover.image.title': 'Cover image',
    'cover.image.empty': 'Add a cover image',
    'cover.image.drop': 'Drop an image or click to browse',
    'cover.image.change': 'Change cover',
    'cover.image.remove': 'Remove cover',
    'cover.image.hint': 'JPG, PNG, WebP — up to ${max}MB',
    'cover.image.invalid': 'Use a JPG, PNG, WebP, or GIF',
    'cover.image.tooLarge': 'Image must be under ${max}MB',
    'explore.demoBanner': 'Showing sample projects — live database unavailable. Explore freely with demo data.',
    'explore.browseAll': 'Browse all projects',
    'explore.clearFiltersAction': 'Clear filters',
    'explore.filtersTitle': 'Filters',
    'explore.closeFilters': 'Close filters',
    'explore.filtersActive': 'Filters active',
    'explore.countryFlag': 'Country flag',
    'explore.sortBy': 'Sort By',
    'explore.sortRecent': 'Most Recent',
    'explore.sortTrending': 'Most Funded',
    'explore.sortFunded': 'Highest Progress',
    'explore.sortGoal': 'Biggest Goals',
    'explore.category': 'Category',
    'explore.allCategories': 'All Categories',
    'explore.location': 'Location',
    'explore.noFavorites': 'No favorites yet',
    'explore.noProjects': 'No projects yet',
    'explore.emptyDescFavorites': 'Tap the heart on projects you love — they will show up here.',
    'explore.emptyDescNone': 'Check back soon for new creators and causes!',
    'explore.funded': 'Funded',
    'explore.addFavorite': 'Add to favorites',
    'explore.removeFavorite': 'Remove from favorites',
    'explore.recentlyViewed': 'Recently viewed',
    'explore.clear': 'Clear',
    'explore.searchAria': 'Search projects',
    'explore.loadingProjects': 'Loading projects',
    'dashboard.placeholder.title': 'Project title',
    'dashboard.placeholder.description': 'Description',
    'dashboard.placeholder.titleExample': 'e.g., Community Garden Fund',
    'dashboard.placeholder.descriptionLong': 'Tell people about your project and what you\'re trying to achieve...',
    'dashboard.placeholder.slug': 'community-garden-fund',
    'project.placeholder.title': 'Project Title',
    'project.placeholder.description': 'Describe your project and what you\'re building...',
    'project.placeholder.wishlistTitle': 'e.g., Recording Equipment Fund',
    'project.placeholder.wishlistDescription': 'Tell supporters what this wishlist is for and why it matters...',
    'project.placeholder.url': 'https://amazon.com/wishlist/...',
    'project.placeholder.wishlistName': 'My Wishlist',
    'project.placeholder.slug': 'my-wishlist',
    'project.placeholder.wishlistDesc': 'What is this wishlist for?',
    'settings.placeholder.username': 'johndoe',
    'settings.placeholder.bio': 'Tell the world about yourself...',
    'settings.placeholder.lightning': 'username@wallet.com',
    'settings.placeholder.nostr': 'npub1...',
    'wishlist.placeholder.amount': '21000',
    'wishlist.placeholder.name': 'Anonymous',
    'wishlist.placeholder.message': 'Leave a message for the creator...',
    'auth.placeholder.username': 'johndoe',
    'auth.placeholder.email': 'you@example.com',
    'auth.placeholder.password': '••••••••',
    'notfound.title': 'Page not found',
    'notfound.subtitle': 'This route doesn\'t exist. Head back to explore creators or start your own wishlist.',
    'notfound.backHome': 'Back to Home',
    'notfound.metaTitle': 'Page Not Found',
    'notfound.metaDesc': 'The page you\'re looking for doesn\'t exist on KATOA.',
    'privacy.title': 'Privacy Policy',
    'privacy.lastUpdated': 'Last Updated: October 27, 2025',
    'pitch.navHint': 'Use arrow keys or swipe to navigate',
    'pitch.prevSlide': 'Previous slide',
    'pitch.nextSlide': 'Next slide',
    'pitch.goToSlide': 'Go to slide ${n}',
    'pitch.slideAnnouncement': 'Slide ${current} of ${total}: ${title}',
    'pitch.startEarning': 'Start Earning 100%',
    'pitch.comparePlatforms': 'Compare Platforms',
    'pitch.metaTitle': 'Pitch Deck',
    'pitch.metaDesc': 'KATOA marketing presentation — zero-fee Bitcoin creator platform.',
    'dashboard.empty.title': 'Start Your First Project',
    'dashboard.empty.description': 'Projects help you organize multiple wishlists under one umbrella. Perfect for campaigns, causes, or creator portfolios.',
    'dashboard.empty.action': 'Create Your First Project',
    'dashboard.welcome': 'Welcome back',
    'dashboard.welcomeGuest': 'Your creator home',
    'dashboard.newProject': 'New project',
    'dashboard.yourProjects': 'Your projects',
    'dashboard.yourProjectsSub': 'Organize wishlists under one roof',
    'dashboard.following': 'Following',
    'dashboard.followingSub': 'Projects, wishlists, and creators you support',
    'dashboard.followingEmpty.title': 'Not following anyone yet',
    'dashboard.followingEmpty.description': 'Explore creators and causes, then they’ll show up here.',
    'dashboard.followingEmpty.action': 'Explore now',
    'dashboard.stats.following': 'Following',
    'dashboard.quick.settings': 'Settings',
    'dashboard.quick.messages': 'Messages',
    'dashboard.quick.templates': 'Templates',
    'dashboard.quick.explore': 'Explore',
    'dashboard.creators': 'Creators',
    'dashboard.lightningMissing': 'Add a Lightning address so fans can pay you instantly. No bank, no cut.',
    'dashboard.lightningMissingCta': 'Add wallet',
    'dashboard.growTitle': 'Grow without the platform cut',
    'dashboard.growBody': 'Cover video, product links, and a shareable page. Fans fund sats or buy the item for you.',
    'dashboard.viewPublic': 'View public page',
    'dashboard.manage': 'Manage',
    'dashboard.noDescription': 'No description yet',
    'dashboard.filterAll': 'All',
    'dashboard.noFollowsFilter': 'Nothing in this filter yet.',
    'dashboard.uploadDemo': 'Cover upload needs a live account.',
    'dashboard.createDemo': 'Saved locally in this demo — connect a real account to publish.',
    'dashboard.editAria': 'Edit project',
    'dashboard.deleteAria': 'Delete project',
    'dashboard.changeCover': 'Change cover',
    'dashboard.addCover': 'Add a cover image',
    'dashboard.wishlistsCount': 'Wishlists',
    'dashboard.createTitle': 'Create a project',
    'dashboard.slugHelp': 'Public URL: katoa.org/project/${slug}',
    'dashboard.sectionShare': 'Share & embed',
    'terms.metaTitle': 'Terms of Service',
    'terms.metaDesc': 'KATOA Terms of Service — rules and guidelines for using our zero-fee Bitcoin creator platform.',
    'terms.lastUpdated': 'Last Updated: October 27, 2025',
    'explore.schemaName': 'KATOA Creator Wishlists',
    'explore.metaTitle': 'Explore',
    'explore.metaDesc': 'Browse Bitcoin creator wishlists worldwide.',
    'wishlist.countryFlag': 'Country: ${country}',
    'wishlist.countryFlagGeneric': 'Country flag',
    'payment.modal.add': 'Add Payment Method',
    'payment.modal.edit': 'Edit Payment Method',
    'settings.nostrHint': 'Optional — paste your npub1… public key for Nostr identity',
    ...aboutComparisonEn,
  },
  es: {
    'dashboard.title': 'Panel del Creador',
    'dashboard.subtitle': 'Gestiona tus proyectos y sigue tu éxito',
    'dashboard.loading': 'Cargando tu panel...',
    'dashboard.projects': 'Proyectos',
    'dashboard.wishlists': 'Listas',
    'dashboard.raised': 'Total recaudado',
    'dashboard.createProject': 'Crear proyecto',
    'dashboard.demo': 'Vista demo — solo UI. Conecta Supabase para guardar proyectos y aceptar pagos reales.',
    'wishlist.loading': 'Cargando lista...',
    'wishlist.notFound': 'Lista no encontrada',
    'wishlist.notFoundDesc': 'Esta lista no existe o ha sido eliminada.',
    'wishlist.sendGift': 'Enviar regalo',
    'wishlist.supportItems': 'Apoya artículos específicos de esta lista',
    'wishlist.explore': 'Explorar listas',
    'about.hero.line1': 'La plataforma que',
    'about.hero.highlight': 'realmente sirve a los creadores',
    'about.hero.subtitle': 'No somos otra plataforma más. Somos un movimiento.',
    'about.hero.badge': 'Hecho por creadores, para creadores',
    'about.problem.title': 'La economía creadora está rota',
    'about.problem.subtitle': 'Las plataformas tradicionales extraen demasiado. Esto es lo que pierden los creadores:',
    'comparison.hero.line1': 'Por qué elegir',
    'comparison.hero.highlight': 'KATOA?',
    'comparison.hero.subtitle': 'No somos otra plataforma de creadores. Estamos construidos sobre Bitcoin Lightning, diseñados para creadores globales, con 0% de comisiones hasta el 31 dic 2026.',
    'comparison.saveMonthly': 'Ahorra hasta ${amount}/mes con KATOA',
    'explore.allProjects': 'Explorar todos los proyectos',
    'explore.loadMore': 'Cargar más',
    'explore.remaining': 'restantes',
    'explore.filters': 'Filtros',
    'explore.favoritesOnly': 'Solo favoritos',
    'explore.clearFilters': 'Borrar todos los filtros',
    'explore.searchProjects': 'Buscar proyectos, creadores, etiquetas...',
    'explore.quickFilters': 'Filtros rápidos:',
    'explore.showResults': 'Mostrar ${count} resultados',
    'explore.loadingMap': 'Cargando mapa…',
    'explore.videoCreators': 'Creadores de video',
    'explore.videoCreatorsDesc': 'Listas premium con video — pasa el cursor para previsualizar. Sube MP4 o MOV en tu proyecto.',
    'explore.exclusive': 'EXCLUSIVO',
    'explore.video': 'VIDEO',
    'explore.supporters': 'seguidores',
    'explore.videosOnly': 'Videos',
    'explore.hoverPreview': 'Pasa el cursor para previsualizar',
    'explore.creatorCategory': 'Creador',
    'dashboard.videoHint': 'Sube videos de portada MP4 o MOV en tus listas.',
    'dashboard.videoHintLink': 'Ver creadores de video',
    'video.upload.title': 'Video de portada',
    'video.upload.hint': 'MP4, MOV, WebM — hasta 200 MB',
    'video.upload.success': 'Video subido',
    'video.upload.error': 'Error al subir el video',
    'video.upload.tooLarge': 'El video debe pesar menos de ${max} MB',
    'video.upload.invalid': 'Usa MP4, MOV, WebM o M4V',
    'video.upload.empty': 'Añade un video de portada',
    'video.upload.drop': 'Arrastra un video o haz clic para elegir',
    'video.upload.change': 'Cambiar video',
    'video.upload.remove': 'Quitar video',
    'cover.image.title': 'Imagen de portada',
    'cover.image.empty': 'Añade una imagen de portada',
    'cover.image.drop': 'Arrastra una imagen o haz clic para elegir',
    'cover.image.change': 'Cambiar portada',
    'cover.image.remove': 'Quitar portada',
    'cover.image.hint': 'JPG, PNG, WebP — hasta ${max} MB',
    'cover.image.invalid': 'Usa JPG, PNG, WebP o GIF',
    'cover.image.tooLarge': 'La imagen debe pesar menos de ${max} MB',
    'explore.demoBanner': 'Mostrando proyectos de ejemplo — base de datos no disponible. Explora libremente con datos demo.',
    'explore.browseAll': 'Ver todos los proyectos',
    'explore.clearFiltersAction': 'Borrar filtros',
    'explore.filtersTitle': 'Filtros',
    'explore.closeFilters': 'Cerrar filtros',
    'explore.filtersActive': 'Filtros activos',
    'explore.countryFlag': 'Bandera del país',
    'explore.sortBy': 'Ordenar por',
    'explore.sortRecent': 'Más recientes',
    'explore.sortTrending': 'Más financiados',
    'explore.sortFunded': 'Mayor progreso',
    'explore.sortGoal': 'Metas más grandes',
    'explore.category': 'Categoría',
    'explore.allCategories': 'Todas las categorías',
    'explore.location': 'Ubicación',
    'explore.noFavorites': 'Aún no hay favoritos',
    'explore.noProjects': 'Aún no hay proyectos',
    'explore.emptyDescFavorites': 'Toca el corazón en los proyectos que te gusten — aparecerán aquí.',
    'explore.emptyDescNone': '¡Vuelve pronto para ver nuevos creadores y causas!',
    'explore.funded': 'Financiado',
    'explore.addFavorite': 'Añadir a favoritos',
    'explore.removeFavorite': 'Quitar de favoritos',
    'explore.recentlyViewed': 'Vistos recientemente',
    'explore.clear': 'Borrar',
    'explore.searchAria': 'Buscar proyectos',
    'explore.loadingProjects': 'Cargando proyectos',
    'dashboard.placeholder.title': 'Título del proyecto',
    'dashboard.placeholder.description': 'Descripción',
    'dashboard.placeholder.titleExample': 'ej., Fondo del jardín comunitario',
    'dashboard.placeholder.descriptionLong': 'Cuéntale a la gente sobre tu proyecto y lo que intentas lograr...',
    'dashboard.placeholder.slug': 'fondo-jardin-comunitario',
    'project.placeholder.title': 'Título del proyecto',
    'project.placeholder.description': 'Describe tu proyecto y lo que estás construyendo...',
    'project.placeholder.wishlistTitle': 'ej., Fondo de equipo de grabación',
    'project.placeholder.wishlistDescription': 'Cuéntales a los seguidores para qué es esta lista y por qué importa...',
    'project.placeholder.url': 'https://amazon.com/wishlist/...',
    'project.placeholder.wishlistName': 'Mi lista',
    'project.placeholder.slug': 'mi-lista',
    'project.placeholder.wishlistDesc': '¿Para qué es esta lista?',
    'settings.placeholder.username': 'johndoe',
    'settings.placeholder.bio': 'Cuéntale al mundo sobre ti...',
    'settings.placeholder.lightning': 'usuario@wallet.com',
    'settings.placeholder.nostr': 'npub1...',
    'wishlist.placeholder.amount': '21000',
    'wishlist.placeholder.name': 'Anónimo',
    'wishlist.placeholder.message': 'Deja un mensaje para el creador...',
    'auth.placeholder.username': 'johndoe',
    'auth.placeholder.email': 'tu@ejemplo.com',
    'auth.placeholder.password': '••••••••',
    'notfound.title': 'Página no encontrada',
    'notfound.subtitle': 'Esta ruta no existe. Vuelve a explorar creadores o crea tu propia lista.',
    'notfound.backHome': 'Volver al inicio',
    'notfound.metaTitle': 'Página no encontrada',
    'notfound.metaDesc': 'La página que buscas no existe en KATOA.',
    'privacy.title': 'Política de privacidad',
    'privacy.lastUpdated': 'Última actualización: 27 de octubre de 2025',
    'pitch.navHint': 'Usa las flechas o desliza para navegar',
    'pitch.prevSlide': 'Diapositiva anterior',
    'pitch.nextSlide': 'Siguiente diapositiva',
    'pitch.goToSlide': 'Ir a la diapositiva ${n}',
    'pitch.slideAnnouncement': 'Diapositiva ${current} de ${total}: ${title}',
    'pitch.startEarning': 'Empieza a ganar 100%',
    'pitch.comparePlatforms': 'Comparar plataformas',
    'pitch.metaTitle': 'Presentación',
    'pitch.metaDesc': 'Presentación de KATOA — plataforma Bitcoin sin comisiones para creadores.',
    'dashboard.empty.title': 'Empieza tu primer proyecto',
    'dashboard.empty.description': 'Los proyectos agrupan varias listas bajo un mismo techo. Ideal para campañas, causas o portfolios de creador.',
    'dashboard.empty.action': 'Crea tu primer proyecto',
    'dashboard.welcome': 'Bienvenido de nuevo',
    'dashboard.welcomeGuest': 'Tu casa de creador',
    'dashboard.newProject': 'Nuevo proyecto',
    'dashboard.yourProjects': 'Tus proyectos',
    'dashboard.yourProjectsSub': 'Organiza listas bajo un mismo techo',
    'dashboard.following': 'Siguiendo',
    'dashboard.followingSub': 'Proyectos, listas y creadores que apoyas',
    'dashboard.followingEmpty.title': 'Aún no sigues a nadie',
    'dashboard.followingEmpty.description': 'Explora creadores y causas; aparecerán aquí.',
    'dashboard.followingEmpty.action': 'Explorar ahora',
    'dashboard.stats.following': 'Siguiendo',
    'dashboard.quick.settings': 'Ajustes',
    'dashboard.quick.messages': 'Mensajes',
    'dashboard.quick.templates': 'Plantillas',
    'dashboard.quick.explore': 'Explorar',
    'dashboard.creators': 'Creadores',
    'dashboard.lightningMissing': 'Añade una dirección Lightning para que te paguen al instante. Sin banco, sin recorte.',
    'dashboard.lightningMissingCta': 'Añadir wallet',
    'dashboard.growTitle': 'Crece sin comisión de plataforma',
    'dashboard.growBody': 'Video de portada, enlaces de producto y una página para compartir. Los fans envían sats o compran el artículo por ti.',
    'dashboard.viewPublic': 'Ver página pública',
    'dashboard.manage': 'Gestionar',
    'dashboard.noDescription': 'Aún sin descripción',
    'dashboard.filterAll': 'Todos',
    'dashboard.noFollowsFilter': 'Nada en este filtro todavía.',
    'dashboard.uploadDemo': 'Subir portada requiere una cuenta real.',
    'dashboard.createDemo': 'Guardado localmente en esta demo — conecta una cuenta real para publicar.',
    'dashboard.editAria': 'Editar proyecto',
    'dashboard.deleteAria': 'Eliminar proyecto',
    'dashboard.changeCover': 'Cambiar portada',
    'dashboard.addCover': 'Añadir imagen de portada',
    'dashboard.wishlistsCount': 'Listas',
    'dashboard.createTitle': 'Crear un proyecto',
    'dashboard.slugHelp': 'URL pública: katoa.org/project/${slug}',
    'dashboard.sectionShare': 'Compartir e incrustar',
    'terms.metaTitle': 'Términos del Servicio',
    'terms.metaDesc': 'Términos del Servicio de KATOA — reglas y directrices para usar nuestra plataforma de creadores Bitcoin sin comisiones.',
    'explore.metaTitle': 'Explorar',
    'explore.metaDesc': 'Descubre listas de deseos de creadores de Bitcoin en todo el mundo.',
    ...aboutComparisonEs,
  },
  pt: {
    'dashboard.title': 'Painel do Criador',
    'dashboard.subtitle': 'Gerencie seus projetos e acompanhe seu sucesso',
    'dashboard.loading': 'Carregando seu painel...',
    'dashboard.projects': 'Projetos',
    'dashboard.wishlists': 'Listas',
    'dashboard.raised': 'Total arrecadado',
    'dashboard.createProject': 'Criar projeto',
    'dashboard.demo': 'Prévia demo — apenas UI. Conecte o Supabase para salvar projetos e aceitar pagamentos reais.',
    'wishlist.loading': 'Carregando lista...',
    'wishlist.notFound': 'Lista não encontrada',
    'wishlist.notFoundDesc': 'Esta lista não existe ou foi removida.',
    'wishlist.sendGift': 'Enviar presente',
    'wishlist.supportItems': 'Apoie itens específicos desta lista',
    'wishlist.explore': 'Explorar listas',
    'about.hero.line1': 'A plataforma que',
    'about.hero.highlight': 'realmente serve criadores',
    'about.hero.subtitle': 'Não somos mais uma plataforma. Somos um movimento.',
    'about.hero.badge': 'Feito por criadores, para criadores',
    'about.problem.title': 'A economia criativa está quebrada',
    'about.problem.subtitle': 'Plataformas tradicionais extraem demais. Veja quanto os criadores perdem:',
    'comparison.hero.line1': 'Por que escolher',
    'comparison.hero.highlight': 'KATOA?',
    'comparison.hero.subtitle': 'Não somos mais uma plataforma de criadores. Somos construídos no Bitcoin Lightning, para criadores globais, com 0% de taxas até 31 dez 2026.',
    'comparison.saveMonthly': 'Economize até ${amount}/mês com KATOA',
    'explore.allProjects': 'Explorar todos os projetos',
    'explore.loadMore': 'Carregar mais',
    'explore.remaining': 'restantes',
    'explore.filters': 'Filtros',
    'explore.favoritesOnly': 'Apenas favoritos',
    'explore.clearFilters': 'Limpar todos os filtros',
    'explore.searchProjects': 'Buscar projetos, criadores, tags...',
    'explore.quickFilters': 'Filtros rápidos:',
    'explore.showResults': 'Mostrar ${count} resultados',
    'explore.loadingMap': 'Carregando mapa…',
    'explore.demoBanner': 'Mostrando projetos de exemplo — banco de dados indisponível. Explore livremente com dados demo.',
    'explore.browseAll': 'Ver todos os projetos',
    'explore.clearFiltersAction': 'Limpar filtros',
    'explore.filtersTitle': 'Filtros',
    'explore.closeFilters': 'Fechar filtros',
    'explore.filtersActive': 'Filtros ativos',
    'explore.countryFlag': 'Bandeira do país',
    'explore.sortBy': 'Ordenar por',
    'explore.sortRecent': 'Mais recentes',
    'explore.sortTrending': 'Mais financiados',
    'explore.sortFunded': 'Maior progresso',
    'explore.sortGoal': 'Maiores metas',
    'explore.category': 'Categoria',
    'explore.allCategories': 'Todas as categorias',
    'explore.location': 'Localização',
    'explore.noFavorites': 'Nenhum favorito ainda',
    'explore.noProjects': 'Nenhum projeto ainda',
    'explore.emptyDescFavorites': 'Toque no coração nos projetos que você ama — eles aparecerão aqui.',
    'explore.emptyDescNone': 'Volte em breve para novos criadores e causas!',
    'explore.funded': 'Financiado',
    'explore.addFavorite': 'Adicionar aos favoritos',
    'explore.removeFavorite': 'Remover dos favoritos',
    'explore.recentlyViewed': 'Vistos recentemente',
    'explore.clear': 'Limpar',
    'explore.searchAria': 'Buscar projetos',
    'explore.loadingProjects': 'Carregando projetos',
    'dashboard.placeholder.title': 'Título do projeto',
    'dashboard.placeholder.description': 'Descrição',
    'dashboard.placeholder.titleExample': 'ex., Fundo da horta comunitária',
    'dashboard.placeholder.descriptionLong': 'Conte às pessoas sobre seu projeto e o que você está tentando alcançar...',
    'dashboard.placeholder.slug': 'fundo-horta-comunitaria',
    'project.placeholder.title': 'Título do projeto',
    'project.placeholder.description': 'Descreva seu projeto e o que você está construindo...',
    'project.placeholder.wishlistTitle': 'ex., Fundo de equipamento de gravação',
    'project.placeholder.wishlistDescription': 'Conte aos apoiadores para que serve esta lista e por que importa...',
    'project.placeholder.url': 'https://amazon.com/wishlist/...',
    'project.placeholder.wishlistName': 'Minha lista',
    'project.placeholder.slug': 'minha-lista',
    'project.placeholder.wishlistDesc': 'Para que serve esta lista?',
    'settings.placeholder.username': 'johndoe',
    'settings.placeholder.bio': 'Conte ao mundo sobre você...',
    'settings.placeholder.lightning': 'usuario@wallet.com',
    'settings.placeholder.nostr': 'npub1...',
    'wishlist.placeholder.amount': '21000',
    'wishlist.placeholder.name': 'Anônimo',
    'wishlist.placeholder.message': 'Deixe uma mensagem para o criador...',
    'auth.placeholder.username': 'johndoe',
    'auth.placeholder.email': 'voce@exemplo.com',
    'auth.placeholder.password': '••••••••',
    'notfound.title': 'Página não encontrada',
    'notfound.subtitle': 'Esta rota não existe. Volte para explorar criadores ou crie sua própria lista.',
    'notfound.backHome': 'Voltar ao início',
    'notfound.metaTitle': 'Página não encontrada',
    'notfound.metaDesc': 'A página que você procura não existe no KATOA.',
    'privacy.title': 'Política de privacidade',
    'privacy.lastUpdated': 'Última atualização: 27 de outubro de 2025',
    'pitch.navHint': 'Use as setas ou deslize para navegar',
    'pitch.prevSlide': 'Slide anterior',
    'pitch.nextSlide': 'Próximo slide',
    'pitch.goToSlide': 'Ir para o slide ${n}',
    'pitch.slideAnnouncement': 'Slide ${current} de ${total}: ${title}',
    'pitch.startEarning': 'Comece a ganhar 100%',
    'pitch.comparePlatforms': 'Comparar plataformas',
    'pitch.metaTitle': 'Apresentação',
    'pitch.metaDesc': 'Apresentação KATOA — plataforma Bitcoin sem taxas para criadores.',
    'dashboard.videoHint': 'Envie vídeos de capa MP4 ou MOV nas suas listas.',
    'dashboard.videoHintLink': 'Ver criadores de vídeo',
    'video.upload.title': 'Vídeo de capa',
    'video.upload.hint': 'MP4, MOV, WebM — até 200 MB',
    'video.upload.success': 'Vídeo enviado',
    'video.upload.error': 'Falha ao enviar o vídeo',
    'video.upload.tooLarge': 'O vídeo deve ter menos de ${max} MB',
    'video.upload.invalid': 'Use MP4, MOV, WebM ou M4V',
    'video.upload.empty': 'Adicione um vídeo de capa',
    'video.upload.drop': 'Arraste um vídeo ou clique para escolher',
    'video.upload.change': 'Trocar vídeo',
    'video.upload.remove': 'Remover vídeo',
    'cover.image.title': 'Imagem de capa',
    'cover.image.empty': 'Adicione uma imagem de capa',
    'cover.image.drop': 'Arraste uma imagem ou clique para escolher',
    'cover.image.change': 'Trocar capa',
    'cover.image.remove': 'Remover capa',
    'cover.image.hint': 'JPG, PNG, WebP — até ${max} MB',
    'cover.image.invalid': 'Use JPG, PNG, WebP ou GIF',
    'cover.image.tooLarge': 'A imagem deve ter menos de ${max} MB',
    'dashboard.empty.title': 'Comece seu primeiro projeto',
    'dashboard.empty.description': 'Projetos agrupam várias listas sob um mesmo teto. Perfeito para campanhas, causas ou portfólios de criador.',
    'dashboard.empty.action': 'Crie seu primeiro projeto',
    'dashboard.welcome': 'Bem-vindo de volta',
    'dashboard.welcomeGuest': 'Sua casa de criador',
    'dashboard.newProject': 'Novo projeto',
    'dashboard.yourProjects': 'Seus projetos',
    'dashboard.yourProjectsSub': 'Organize listas sob um mesmo teto',
    'dashboard.following': 'Seguindo',
    'dashboard.followingSub': 'Projetos, listas e criadores que você apoia',
    'dashboard.followingEmpty.title': 'Você ainda não segue ninguém',
    'dashboard.followingEmpty.description': 'Explore criadores e causas; eles aparecem aqui.',
    'dashboard.followingEmpty.action': 'Explorar agora',
    'dashboard.stats.following': 'Seguindo',
    'dashboard.quick.settings': 'Configurações',
    'dashboard.quick.messages': 'Mensagens',
    'dashboard.quick.templates': 'Modelos',
    'dashboard.quick.explore': 'Explorar',
    'dashboard.creators': 'Criadores',
    'dashboard.lightningMissing': 'Adicione um endereço Lightning para receber na hora. Sem banco, sem corte.',
    'dashboard.lightningMissingCta': 'Adicionar wallet',
    'dashboard.growTitle': 'Cresça sem a taxa da plataforma',
    'dashboard.growBody': 'Vídeo de capa, links de produto e uma página para compartilhar. Fãs enviam sats ou compram o item por você.',
    'dashboard.viewPublic': 'Ver página pública',
    'dashboard.manage': 'Gerenciar',
    'dashboard.noDescription': 'Ainda sem descrição',
    'dashboard.filterAll': 'Todos',
    'dashboard.noFollowsFilter': 'Nada neste filtro ainda.',
    'dashboard.uploadDemo': 'Upload de capa precisa de uma conta real.',
    'dashboard.createDemo': 'Salvo localmente nesta demo — conecte uma conta real para publicar.',
    'dashboard.editAria': 'Editar projeto',
    'dashboard.deleteAria': 'Excluir projeto',
    'dashboard.changeCover': 'Trocar capa',
    'dashboard.addCover': 'Adicionar imagem de capa',
    'dashboard.wishlistsCount': 'Listas',
    'dashboard.createTitle': 'Criar um projeto',
    'dashboard.slugHelp': 'URL pública: katoa.org/project/${slug}',
    'dashboard.sectionShare': 'Compartilhar e incorporar',
    'terms.metaTitle': 'Termos de Serviço',
    'terms.metaDesc': 'Termos de Serviço da KATOA — regras e diretrizes para usar nossa plataforma de criadores Bitcoin sem taxas.',
    'explore.metaTitle': 'Explorar',
    'explore.metaDesc': 'Navegue por listas de desejos de criadores de Bitcoin no mundo todo.',
    ...aboutComparisonPt,
  },
  fr: {
    'dashboard.title': 'Tableau de bord créateur',
    'dashboard.subtitle': 'Gérez vos projets et suivez votre succès',
    'dashboard.loading': 'Chargement du tableau de bord...',
    'dashboard.projects': 'Projets',
    'dashboard.wishlists': 'Listes',
    'dashboard.raised': 'Total collecté',
    'dashboard.createProject': 'Créer un projet',
    'dashboard.demo': 'Aperçu démo — UI uniquement. Connectez Supabase pour enregistrer des projets et accepter de vrais paiements.',
    'wishlist.loading': 'Chargement de la liste...',
    'wishlist.notFound': 'Liste introuvable',
    'wishlist.notFoundDesc': 'Cette liste n\'existe pas ou a été supprimée.',
    'wishlist.sendGift': 'Envoyer un cadeau',
    'wishlist.supportItems': 'Soutenez des articles spécifiques de cette liste',
    'wishlist.explore': 'Explorer les listes',
    'about.hero.line1': 'La plateforme qui',
    'about.hero.highlight': 'sert vraiment les créateurs',
    'about.hero.subtitle': 'Nous ne sommes pas une plateforme de plus. Nous sommes un mouvement.',
    'about.hero.badge': 'Conçu par des créateurs, pour des créateurs',
    'about.problem.title': 'L\'économie créative est cassée',
    'about.problem.subtitle': 'Les plateformes traditionnelles prélèvent trop. Voici ce que les créateurs perdent :',
    'comparison.hero.line1': 'Pourquoi choisir',
    'comparison.hero.highlight': 'KATOA ?',
    'comparison.hero.subtitle': 'Nous ne sommes pas une plateforme de plus. Nous sommes construits sur Bitcoin Lightning, pour les créateurs du monde entier, avec 0 % de frais jusqu’au 31 déc. 2026.',
    'comparison.saveMonthly': 'Économisez jusqu\'à ${amount}/mois avec KATOA',
    'explore.allProjects': 'Explorer tous les projets',
    'explore.loadMore': 'Charger plus',
    'explore.remaining': 'restants',
    'explore.filters': 'Filtres',
    'explore.favoritesOnly': 'Favoris uniquement',
    'explore.clearFilters': 'Effacer tous les filtres',
    'explore.searchProjects': 'Rechercher projets, créateurs, tags...',
    'explore.quickFilters': 'Filtres rapides :',
    'explore.showResults': 'Afficher ${count} résultats',
    'explore.loadingMap': 'Chargement de la carte…',
    'explore.demoBanner': 'Projets exemple affichés — base de données indisponible. Explorez librement avec des données démo.',
    'explore.browseAll': 'Parcourir tous les projets',
    'explore.clearFiltersAction': 'Effacer les filtres',
    'explore.filtersTitle': 'Filtres',
    'explore.closeFilters': 'Fermer les filtres',
    'explore.filtersActive': 'Filtres actifs',
    'explore.countryFlag': 'Drapeau du pays',
    'explore.sortBy': 'Trier par',
    'explore.sortRecent': 'Plus récents',
    'explore.sortTrending': 'Plus financés',
    'explore.sortFunded': 'Progression la plus élevée',
    'explore.sortGoal': 'Plus grands objectifs',
    'explore.category': 'Catégorie',
    'explore.allCategories': 'Toutes les catégories',
    'explore.location': 'Emplacement',
    'explore.noFavorites': 'Pas encore de favoris',
    'explore.noProjects': 'Pas encore de projets',
    'explore.emptyDescFavorites': 'Appuyez sur le cœur des projets que vous aimez — ils apparaîtront ici.',
    'explore.emptyDescNone': 'Revenez bientôt pour de nouveaux créateurs et causes !',
    'explore.funded': 'Financé',
    'explore.addFavorite': 'Ajouter aux favoris',
    'explore.removeFavorite': 'Retirer des favoris',
    'explore.recentlyViewed': 'Consultés récemment',
    'explore.clear': 'Effacer',
    'explore.searchAria': 'Rechercher des projets',
    'explore.loadingProjects': 'Chargement des projets',
    'dashboard.placeholder.title': 'Titre du projet',
    'dashboard.placeholder.description': 'Description',
    'dashboard.placeholder.titleExample': 'ex., Fonds du jardin communautaire',
    'dashboard.placeholder.descriptionLong': 'Parlez de votre projet et de ce que vous essayez d\'accomplir...',
    'dashboard.placeholder.slug': 'fonds-jardin-communautaire',
    'project.placeholder.title': 'Titre du projet',
    'project.placeholder.description': 'Décrivez votre projet et ce que vous construisez...',
    'project.placeholder.wishlistTitle': 'ex., Fonds équipement d\'enregistrement',
    'project.placeholder.wishlistDescription': 'Dites aux supporters à quoi sert cette liste et pourquoi c\'est important...',
    'project.placeholder.url': 'https://amazon.com/wishlist/...',
    'project.placeholder.wishlistName': 'Ma liste',
    'project.placeholder.slug': 'ma-liste',
    'project.placeholder.wishlistDesc': 'À quoi sert cette liste ?',
    'settings.placeholder.username': 'johndoe',
    'settings.placeholder.bio': 'Parlez de vous au monde...',
    'settings.placeholder.lightning': 'utilisateur@wallet.com',
    'settings.placeholder.nostr': 'npub1...',
    'wishlist.placeholder.amount': '21000',
    'wishlist.placeholder.name': 'Anonyme',
    'wishlist.placeholder.message': 'Laissez un message pour le créateur...',
    'auth.placeholder.username': 'johndoe',
    'auth.placeholder.email': 'vous@exemple.com',
    'auth.placeholder.password': '••••••••',
    'notfound.title': 'Page introuvable',
    'notfound.subtitle': 'Cette route n\'existe pas. Retournez explorer les créateurs ou créez votre propre liste.',
    'notfound.backHome': 'Retour à l\'accueil',
    'notfound.metaTitle': 'Page introuvable',
    'notfound.metaDesc': 'La page que vous cherchez n\'existe pas sur KATOA.',
    'privacy.title': 'Politique de confidentialité',
    'privacy.lastUpdated': 'Dernière mise à jour : 27 octobre 2025',
    'pitch.navHint': 'Utilisez les flèches ou glissez pour naviguer',
    'pitch.prevSlide': 'Diapositive précédente',
    'pitch.nextSlide': 'Diapositive suivante',
    'pitch.goToSlide': 'Aller à la diapositive ${n}',
    'pitch.slideAnnouncement': 'Diapositive ${current} sur ${total} : ${title}',
    'pitch.startEarning': 'Gagnez 100%',
    'pitch.comparePlatforms': 'Comparer les plateformes',
    'pitch.metaTitle': 'Présentation',
    'pitch.metaDesc': 'Présentation KATOA — plateforme Bitcoin sans frais pour créateurs.',
    'dashboard.videoHint': 'Ajoutez des vidéos de couverture MP4 ou MOV à vos listes.',
    'dashboard.videoHintLink': 'Voir les créateurs vidéo',
    'video.upload.title': 'Vidéo de couverture',
    'video.upload.hint': 'MP4, MOV, WebM — jusqu’à 200 Mo',
    'video.upload.success': 'Vidéo envoyée',
    'video.upload.error': 'Échec de l’envoi de la vidéo',
    'video.upload.tooLarge': 'La vidéo doit faire moins de ${max} Mo',
    'video.upload.invalid': 'Utilisez MP4, MOV, WebM ou M4V',
    'video.upload.empty': 'Ajoutez une vidéo de couverture',
    'video.upload.drop': 'Glissez une vidéo ou cliquez pour parcourir',
    'video.upload.change': 'Changer la vidéo',
    'video.upload.remove': 'Supprimer la vidéo',
    'cover.image.title': 'Image de couverture',
    'cover.image.empty': 'Ajoutez une image de couverture',
    'cover.image.drop': 'Glissez une image ou cliquez pour parcourir',
    'cover.image.change': 'Changer la couverture',
    'cover.image.remove': 'Supprimer la couverture',
    'cover.image.hint': 'JPG, PNG, WebP — jusqu’à ${max} Mo',
    'cover.image.invalid': 'Utilisez JPG, PNG, WebP ou GIF',
    'cover.image.tooLarge': 'L’image doit faire moins de ${max} Mo',
    'dashboard.empty.title': 'Lancez votre premier projet',
    'dashboard.empty.description': 'Les projets regroupent plusieurs listes sous un même toit. Idéal pour campagnes, causes ou portfolios de créateur.',
    'dashboard.empty.action': 'Créer votre premier projet',
    'dashboard.welcome': 'Bon retour',
    'dashboard.welcomeGuest': 'Votre maison créateur',
    'dashboard.newProject': 'Nouveau projet',
    'dashboard.yourProjects': 'Vos projets',
    'dashboard.yourProjectsSub': 'Organisez vos listes sous un même toit',
    'dashboard.following': 'Suivis',
    'dashboard.followingSub': 'Projets, listes et créateurs que vous soutenez',
    'dashboard.followingEmpty.title': 'Vous ne suivez personne encore',
    'dashboard.followingEmpty.description': 'Explorez créateurs et causes ; ils apparaîtront ici.',
    'dashboard.followingEmpty.action': 'Explorer maintenant',
    'dashboard.stats.following': 'Suivis',
    'dashboard.quick.settings': 'Paramètres',
    'dashboard.quick.messages': 'Messages',
    'dashboard.quick.templates': 'Modèles',
    'dashboard.quick.explore': 'Explorer',
    'dashboard.creators': 'Créateurs',
    'dashboard.lightningMissing': 'Ajoutez une adresse Lightning pour être payé instantanément. Pas de banque, pas de commission.',
    'dashboard.lightningMissingCta': 'Ajouter un wallet',
    'dashboard.growTitle': 'Grandissez sans la commission de la plateforme',
    'dashboard.growBody': 'Vidéo de couverture, liens produits et une page à partager. Les fans envoient des sats ou achètent l\'article pour vous.',
    'dashboard.viewPublic': 'Voir la page publique',
    'dashboard.manage': 'Gérer',
    'dashboard.noDescription': 'Pas encore de description',
    'dashboard.filterAll': 'Tous',
    'dashboard.noFollowsFilter': 'Rien dans ce filtre pour l\'instant.',
    'dashboard.uploadDemo': 'L\'upload de couverture exige un compte réel.',
    'dashboard.createDemo': 'Enregistré localement dans cette démo — connectez un vrai compte pour publier.',
    'dashboard.editAria': 'Modifier le projet',
    'dashboard.deleteAria': 'Supprimer le projet',
    'dashboard.changeCover': 'Changer la couverture',
    'dashboard.addCover': 'Ajouter une image de couverture',
    'dashboard.wishlistsCount': 'Listes',
    'dashboard.createTitle': 'Créer un projet',
    'dashboard.slugHelp': 'URL publique : katoa.org/project/${slug}',
    'dashboard.sectionShare': 'Partager et intégrer',
    'terms.metaTitle': 'Conditions d’utilisation',
    'terms.metaDesc': 'Conditions d’utilisation KATOA — règles et directives pour utiliser notre plateforme de créateurs Bitcoin sans frais.',
    'explore.metaTitle': 'Explorer',
    'explore.metaDesc': 'Parcourez les listes de souhaits de créateurs Bitcoin dans le monde entier.',
    ...aboutComparisonFr,
  },
  de: {
    'dashboard.title': 'Creator-Dashboard',
    'dashboard.subtitle': 'Verwalte deine Projekte und verfolge deinen Erfolg',
    'dashboard.loading': 'Dashboard wird geladen...',
    'dashboard.projects': 'Projekte',
    'dashboard.wishlists': 'Wunschlisten',
    'dashboard.raised': 'Gesamt gesammelt',
    'dashboard.createProject': 'Projekt erstellen',
    'dashboard.demo': 'Demo-Vorschau — nur UI. Verbinde Supabase, um Projekte zu speichern und echte Zahlungen zu akzeptieren.',
    'wishlist.loading': 'Wunschliste wird geladen...',
    'wishlist.notFound': 'Wunschliste nicht gefunden',
    'wishlist.notFoundDesc': 'Diese Wunschliste existiert nicht oder wurde entfernt.',
    'wishlist.sendGift': 'Geschenk senden',
    'wishlist.supportItems': 'Unterstütze bestimmte Artikel auf dieser Wunschliste',
    'wishlist.explore': 'Listen entdecken',
    'about.hero.line1': 'Die Plattform, die',
    'about.hero.highlight': 'Creator wirklich dient',
    'about.hero.subtitle': 'Wir sind nicht nur eine weitere Plattform. Wir sind eine Bewegung.',
    'about.hero.badge': 'Von Creatorn für Creator',
    'about.problem.title': 'Die Creator-Ökonomie ist kaputt',
    'about.problem.subtitle': 'Traditionelle Plattformen nehmen zu viel. So viel verlieren Creator:',
    'comparison.hero.line1': 'Warum',
    'comparison.hero.highlight': 'KATOA?',
    'comparison.hero.subtitle': 'Wir sind nicht nur eine Creator-Plattform. Wir basieren auf Bitcoin Lightning, für Creator weltweit, mit 0 % Gebühren bis zum 31. Dez. 2026.',
    'comparison.saveMonthly': 'Spare bis zu ${amount}/Monat mit KATOA',
    'explore.allProjects': 'Alle Projekte entdecken',
    'explore.loadMore': 'Mehr laden',
    'explore.remaining': 'übrig',
    'explore.filters': 'Filter',
    'explore.favoritesOnly': 'Nur Favoriten',
    'explore.clearFilters': 'Alle Filter löschen',
    'explore.searchProjects': 'Projekte, Creator, Tags suchen...',
    'explore.quickFilters': 'Schnellfilter:',
    'explore.showResults': '${count} Ergebnisse anzeigen',
    'explore.loadingMap': 'Karte wird geladen…',
    'explore.demoBanner': 'Beispielprojekte — Live-Datenbank nicht verfügbar. Erkunde frei mit Demo-Daten.',
    'explore.browseAll': 'Alle Projekte durchsuchen',
    'explore.clearFiltersAction': 'Filter löschen',
    'explore.filtersTitle': 'Filter',
    'explore.closeFilters': 'Filter schließen',
    'explore.filtersActive': 'Filter aktiv',
    'explore.countryFlag': 'Länderflagge',
    'explore.sortBy': 'Sortieren nach',
    'explore.sortRecent': 'Neueste',
    'explore.sortTrending': 'Am meisten finanziert',
    'explore.sortFunded': 'Höchster Fortschritt',
    'explore.sortGoal': 'Größte Ziele',
    'explore.category': 'Kategorie',
    'explore.allCategories': 'Alle Kategorien',
    'explore.location': 'Standort',
    'explore.noFavorites': 'Noch keine Favoriten',
    'explore.noProjects': 'Noch keine Projekte',
    'explore.emptyDescFavorites': 'Tippe auf das Herz bei Projekten, die du magst — sie erscheinen hier.',
    'explore.emptyDescNone': 'Schau bald wieder vorbei für neue Creator und Anliegen!',
    'explore.funded': 'Finanziert',
    'explore.addFavorite': 'Zu Favoriten hinzufügen',
    'explore.removeFavorite': 'Aus Favoriten entfernen',
    'explore.recentlyViewed': 'Kürzlich angesehen',
    'explore.clear': 'Löschen',
    'explore.searchAria': 'Projekte suchen',
    'explore.loadingProjects': 'Projekte werden geladen',
    'dashboard.placeholder.title': 'Projekttitel',
    'dashboard.placeholder.description': 'Beschreibung',
    'dashboard.placeholder.titleExample': 'z.B. Gemeinschaftsgarten-Fonds',
    'dashboard.placeholder.descriptionLong': 'Erzähle Menschen von deinem Projekt und was du erreichen willst...',
    'dashboard.placeholder.slug': 'gemeinschaftsgarten-fonds',
    'project.placeholder.title': 'Projekttitel',
    'project.placeholder.description': 'Beschreibe dein Projekt und was du aufbaust...',
    'project.placeholder.wishlistTitle': 'z.B. Aufnahmeausrüstung-Fonds',
    'project.placeholder.wishlistDescription': 'Erkläre Unterstützern, wofür diese Liste ist und warum sie wichtig ist...',
    'project.placeholder.url': 'https://amazon.com/wishlist/...',
    'project.placeholder.wishlistName': 'Meine Wunschliste',
    'project.placeholder.slug': 'meine-wunschliste',
    'project.placeholder.wishlistDesc': 'Wofür ist diese Wunschliste?',
    'settings.placeholder.username': 'johndoe',
    'settings.placeholder.bio': 'Erzähle der Welt von dir...',
    'settings.placeholder.lightning': 'benutzer@wallet.com',
    'settings.placeholder.nostr': 'npub1...',
    'wishlist.placeholder.amount': '21000',
    'wishlist.placeholder.name': 'Anonym',
    'wishlist.placeholder.message': 'Hinterlasse eine Nachricht für den Creator...',
    'auth.placeholder.username': 'johndoe',
    'auth.placeholder.email': 'du@beispiel.com',
    'auth.placeholder.password': '••••••••',
    'notfound.title': 'Seite nicht gefunden',
    'notfound.subtitle': 'Diese Route existiert nicht. Geh zurück, um Creator zu entdecken oder starte deine eigene Wunschliste.',
    'notfound.backHome': 'Zurück zur Startseite',
    'notfound.metaTitle': 'Seite nicht gefunden',
    'notfound.metaDesc': 'Die gesuchte Seite existiert nicht auf KATOA.',
    'privacy.title': 'Datenschutzrichtlinie',
    'privacy.lastUpdated': 'Zuletzt aktualisiert: 27. Oktober 2025',
    'pitch.navHint': 'Pfeiltasten oder Wischen zum Navigieren',
    'pitch.prevSlide': 'Vorherige Folie',
    'pitch.nextSlide': 'Nächste Folie',
    'pitch.goToSlide': 'Zu Folie ${n} gehen',
    'pitch.slideAnnouncement': 'Folie ${current} von ${total}: ${title}',
    'pitch.startEarning': 'Verdiene 100%',
    'pitch.comparePlatforms': 'Plattformen vergleichen',
    'pitch.metaTitle': 'Pitch Deck',
    'pitch.metaDesc': 'KATOA-Präsentation — gebührenfreie Bitcoin-Creator-Plattform.',
    'dashboard.videoHint': 'Lade MP4- oder MOV-Covervideos auf deine Wunschlisten hoch.',
    'dashboard.videoHintLink': 'Video-Creator entdecken',
    'video.upload.title': 'Cover-Video',
    'video.upload.hint': 'MP4, MOV, WebM — bis zu 200 MB',
    'video.upload.success': 'Video hochgeladen',
    'video.upload.error': 'Video-Upload fehlgeschlagen',
    'video.upload.tooLarge': 'Video muss kleiner als ${max} MB sein',
    'video.upload.invalid': 'MP4, MOV, WebM oder M4V verwenden',
    'video.upload.empty': 'Cover-Video hinzufügen',
    'video.upload.drop': 'Video hierher ziehen oder klicken zum Auswählen',
    'video.upload.change': 'Video ändern',
    'video.upload.remove': 'Video entfernen',
    'cover.image.title': 'Coverbild',
    'cover.image.empty': 'Coverbild hinzufügen',
    'cover.image.drop': 'Bild hierher ziehen oder klicken zum Auswählen',
    'cover.image.change': 'Cover ändern',
    'cover.image.remove': 'Cover entfernen',
    'cover.image.hint': 'JPG, PNG, WebP — bis zu ${max} MB',
    'cover.image.invalid': 'JPG, PNG, WebP oder GIF verwenden',
    'cover.image.tooLarge': 'Bild muss kleiner als ${max} MB sein',
    'dashboard.empty.title': 'Starte dein erstes Projekt',
    'dashboard.empty.description': 'Projekte bündeln mehrere Wunschlisten unter einem Dach. Ideal für Kampagnen, Anliegen oder Creator-Portfolios.',
    'dashboard.empty.action': 'Erstes Projekt erstellen',
    'dashboard.welcome': 'Willkommen zurück',
    'dashboard.welcomeGuest': 'Dein Creator-Zuhause',
    'dashboard.newProject': 'Neues Projekt',
    'dashboard.yourProjects': 'Deine Projekte',
    'dashboard.yourProjectsSub': 'Wunschlisten unter einem Dach',
    'dashboard.following': 'Folge ich',
    'dashboard.followingSub': 'Projekte, Wunschlisten und Creator, die du unterstützt',
    'dashboard.followingEmpty.title': 'Du folgst noch niemandem',
    'dashboard.followingEmpty.description': 'Entdecke Creator und Anliegen — sie erscheinen hier.',
    'dashboard.followingEmpty.action': 'Jetzt entdecken',
    'dashboard.stats.following': 'Folge ich',
    'dashboard.quick.settings': 'Einstellungen',
    'dashboard.quick.messages': 'Nachrichten',
    'dashboard.quick.templates': 'Vorlagen',
    'dashboard.quick.explore': 'Entdecken',
    'dashboard.creators': 'Creator',
    'dashboard.lightningMissing': 'Füge eine Lightning-Adresse hinzu, damit Fans dich sofort bezahlen. Keine Bank, kein Abzug.',
    'dashboard.lightningMissingCta': 'Wallet hinzufügen',
    'dashboard.growTitle': 'Wachsen ohne Plattformgebühr',
    'dashboard.growBody': 'Covervideo, Produktlinks und eine teilbare Seite. Fans senden Sats oder kaufen den Artikel für dich.',
    'dashboard.viewPublic': 'Öffentliche Seite ansehen',
    'dashboard.manage': 'Verwalten',
    'dashboard.noDescription': 'Noch keine Beschreibung',
    'dashboard.filterAll': 'Alle',
    'dashboard.noFollowsFilter': 'Noch nichts in diesem Filter.',
    'dashboard.uploadDemo': 'Cover-Upload braucht ein echtes Konto.',
    'dashboard.createDemo': 'Lokal in dieser Demo gespeichert — verbinde ein echtes Konto zum Veröffentlichen.',
    'dashboard.editAria': 'Projekt bearbeiten',
    'dashboard.deleteAria': 'Projekt löschen',
    'dashboard.changeCover': 'Cover ändern',
    'dashboard.addCover': 'Coverbild hinzufügen',
    'dashboard.wishlistsCount': 'Wunschlisten',
    'dashboard.createTitle': 'Projekt erstellen',
    'dashboard.slugHelp': 'Öffentliche URL: katoa.org/project/${slug}',
    'dashboard.sectionShare': 'Teilen & einbetten',
    'terms.metaTitle': 'Nutzungsbedingungen',
    'terms.metaDesc': 'KATOA Nutzungsbedingungen — Regeln und Richtlinien für die Nutzung unserer gebührenfreien Bitcoin-Creator-Plattform.',
    'explore.metaTitle': 'Entdecken',
    'explore.metaDesc': 'Durchsuche Bitcoin-Creator-Wunschlisten weltweit.',
    ...aboutComparisonDe,
  },
  ja: {
    'dashboard.title': 'クリエイターダッシュボード',
    'dashboard.subtitle': 'プロジェクトを管理し、成果を追跡',
    'dashboard.loading': 'ダッシュボードを読み込み中...',
    'dashboard.projects': 'プロジェクト',
    'dashboard.wishlists': 'ウィッシュリスト',
    'dashboard.raised': '総支援額',
    'dashboard.createProject': 'プロジェクト作成',
    'dashboard.demo': 'デモプレビュー — UIのみ。Supabaseを接続してプロジェクトを保存し、実際の支払いを受け付けてください。',
    'wishlist.loading': 'ウィッシュリストを読み込み中...',
    'wishlist.notFound': 'ウィッシュリストが見つかりません',
    'wishlist.notFoundDesc': 'このウィッシュリストは存在しないか、削除されました。',
    'wishlist.sendGift': 'ギフトを送る',
    'wishlist.supportItems': 'このリストの特定のアイテムを支援',
    'wishlist.explore': 'リストを探す',
    'about.hero.line1': 'クリエイターに',
    'about.hero.highlight': '本当に役立つプラットフォーム',
    'about.hero.subtitle': '私たちは単なるプラットフォームではありません。ムーブメントです。',
    'about.hero.badge': 'クリエイターによる、クリエイターのための',
    'about.problem.title': 'クリエイター経済は壊れている',
    'about.problem.subtitle': '従来のプラットフォームは取りすぎです。クリエイターが失う金額：',
    'comparison.hero.line1': 'なぜ',
    'comparison.hero.highlight': 'KATOA？',
    'comparison.hero.subtitle': '単なるクリエイタープラットフォームではありません。Bitcoin Lightning上に構築され、世界中のクリエイター向け、0%手数料（2026年12月31日まで）です。',
    'comparison.saveMonthly': 'KATOAで月最大${amount}節約',
    'explore.allProjects': 'すべてのプロジェクトを探す',
    'explore.loadMore': 'もっと読み込む',
    'explore.remaining': '件残り',
    'explore.filters': 'フィルター',
    'explore.favoritesOnly': 'お気に入りのみ',
    'explore.clearFilters': 'すべてのフィルターをクリア',
    'explore.searchProjects': 'プロジェクト、クリエイター、タグを検索...',
    'explore.quickFilters': 'クイックフィルター:',
    'explore.showResults': '${count}件の結果を表示',
    'explore.loadingMap': '地図を読み込み中…',
    'explore.demoBanner': 'サンプルプロジェクトを表示中 — ライブDB利用不可。デモデータで自由に探索できます。',
    'explore.browseAll': 'すべてのプロジェクトを見る',
    'explore.clearFiltersAction': 'フィルターをクリア',
    'explore.filtersTitle': 'フィルター',
    'explore.closeFilters': 'フィルターを閉じる',
    'explore.filtersActive': 'フィルター適用中',
    'explore.countryFlag': '国旗',
    'explore.sortBy': '並び替え',
    'explore.sortRecent': '最新',
    'explore.sortTrending': '最も資金調達',
    'explore.sortFunded': '進捗率が高い',
    'explore.sortGoal': '目標額が大きい',
    'explore.category': 'カテゴリー',
    'explore.allCategories': 'すべてのカテゴリー',
    'explore.location': '場所',
    'explore.noFavorites': 'お気に入りはまだありません',
    'explore.noProjects': 'プロジェクトはまだありません',
    'explore.emptyDescFavorites': '気に入ったプロジェクトのハートをタップ — ここに表示されます。',
    'explore.emptyDescNone': '新しいクリエイターと活動をお楽しみに！',
    'explore.funded': '達成',
    'explore.addFavorite': 'お気に入りに追加',
    'explore.removeFavorite': 'お気に入りから削除',
    'explore.recentlyViewed': '最近閲覧',
    'explore.clear': 'クリア',
    'explore.searchAria': 'プロジェクトを検索',
    'explore.loadingProjects': 'プロジェクトを読み込み中',
    'dashboard.placeholder.title': 'プロジェクト名',
    'dashboard.placeholder.description': '説明',
    'dashboard.placeholder.titleExample': '例：コミュニティガーデン基金',
    'dashboard.placeholder.descriptionLong': 'プロジェクトと達成したいことについて伝えてください...',
    'dashboard.placeholder.slug': 'community-garden-fund',
    'project.placeholder.title': 'プロジェクト名',
    'project.placeholder.description': 'プロジェクトと構築しているものを説明...',
    'project.placeholder.wishlistTitle': '例：録音機材基金',
    'project.placeholder.wishlistDescription': 'このリストの目的と重要性を支援者に伝えてください...',
    'project.placeholder.url': 'https://amazon.com/wishlist/...',
    'project.placeholder.wishlistName': 'マイリスト',
    'project.placeholder.slug': 'my-wishlist',
    'project.placeholder.wishlistDesc': 'このリストは何のため？',
    'settings.placeholder.username': 'johndoe',
    'settings.placeholder.bio': '自分について世界に伝えてください...',
    'settings.placeholder.lightning': 'username@wallet.com',
    'settings.placeholder.nostr': 'npub1...',
    'wishlist.placeholder.amount': '21000',
    'wishlist.placeholder.name': '匿名',
    'wishlist.placeholder.message': 'クリエイターへのメッセージを残す...',
    'auth.placeholder.username': 'johndoe',
    'auth.placeholder.email': 'you@example.com',
    'auth.placeholder.password': '••••••••',
    'notfound.title': 'ページが見つかりません',
    'notfound.subtitle': 'このページは存在しません。クリエイターを探すか、自分のウィッシュリストを始めましょう。',
    'notfound.backHome': 'ホームに戻る',
    'notfound.metaTitle': 'ページが見つかりません',
    'notfound.metaDesc': 'お探しのページはKATOAに存在しません。',
    'privacy.title': 'プライバシーポリシー',
    'privacy.lastUpdated': '最終更新：2025年10月27日',
    'pitch.navHint': '矢印キーまたはスワイプで移動',
    'pitch.prevSlide': '前のスライド',
    'pitch.nextSlide': '次のスライド',
    'pitch.goToSlide': 'スライド${n}へ',
    'pitch.slideAnnouncement': 'スライド${current}/${total}: ${title}',
    'pitch.startEarning': '100%を稼ぎ始める',
    'pitch.comparePlatforms': 'プラットフォームを比較',
    'pitch.metaTitle': 'ピッチデック',
    'pitch.metaDesc': 'KATOAプレゼン — 手数料ゼロのBitcoinクリエイタープラットフォーム。',
    'dashboard.videoHint': 'ウィッシュリストにMP4またはMOVのカバー動画をアップロード。',
    'dashboard.videoHintLink': '動画クリエイターを見る',
    'video.upload.title': 'カバー動画',
    'video.upload.hint': 'MP4、MOV、WebM — 最大200MB',
    'video.upload.success': '動画をアップロードしました',
    'video.upload.error': '動画のアップロードに失敗しました',
    'video.upload.tooLarge': '動画は${max}MB未満にしてください',
    'video.upload.invalid': 'MP4、MOV、WebM、M4Vを使用してください',
    'video.upload.empty': 'カバー動画を追加',
    'video.upload.drop': '動画をドラッグするか、クリックして選択',
    'video.upload.change': '動画を変更',
    'video.upload.remove': '動画を削除',
    'cover.image.title': 'カバー画像',
    'cover.image.empty': 'カバー画像を追加',
    'cover.image.drop': '画像をドラッグするか、クリックして選択',
    'cover.image.change': 'カバーを変更',
    'cover.image.remove': 'カバーを削除',
    'cover.image.hint': 'JPG、PNG、WebP — 最大${max}MB',
    'cover.image.invalid': 'JPG、PNG、WebP、GIFを使用してください',
    'cover.image.tooLarge': '画像は${max}MB未満にしてください',
    'dashboard.empty.title': '最初のプロジェクトを始める',
    'dashboard.empty.description': 'プロジェクトは複数のウィッシュリストをひとつにまとめます。キャンペーン、活動、ポートフォリオに最適。',
    'dashboard.empty.action': '最初のプロジェクトを作成',
    'dashboard.welcome': 'おかえりなさい',
    'dashboard.welcomeGuest': 'クリエイターのホーム',
    'dashboard.newProject': '新規プロジェクト',
    'dashboard.yourProjects': 'あなたのプロジェクト',
    'dashboard.yourProjectsSub': 'ウィッシュリストをひとつにまとめる',
    'dashboard.following': 'フォロー中',
    'dashboard.followingSub': '応援しているプロジェクト、ウィッシュリスト、クリエイター',
    'dashboard.followingEmpty.title': 'まだ誰もフォローしていません',
    'dashboard.followingEmpty.description': 'クリエイターと活動を探索すると、ここに表示されます。',
    'dashboard.followingEmpty.action': '今すぐ探索',
    'dashboard.stats.following': 'フォロー中',
    'dashboard.quick.settings': '設定',
    'dashboard.quick.messages': 'メッセージ',
    'dashboard.quick.templates': 'テンプレート',
    'dashboard.quick.explore': '探索',
    'dashboard.creators': 'クリエイター',
    'dashboard.lightningMissing': 'Lightningアドレスを追加して、ファンから即時入金。銀行も手数料もなし。',
    'dashboard.lightningMissingCta': 'ウォレットを追加',
    'dashboard.growTitle': 'プラットフォーム手数料なしで伸ばす',
    'dashboard.growBody': 'カバー動画、商品リンク、共有ページ。ファンはsatsを送るか、代わりに購入します。',
    'dashboard.viewPublic': '公開ページを見る',
    'dashboard.manage': '管理',
    'dashboard.noDescription': '説明はまだありません',
    'dashboard.filterAll': 'すべて',
    'dashboard.noFollowsFilter': 'このフィルターにはまだ何もありません。',
    'dashboard.uploadDemo': 'カバーのアップロードには本番アカウントが必要です。',
    'dashboard.createDemo': 'このデモではローカル保存 — 公開するには本番アカウントを接続してください。',
    'dashboard.editAria': 'プロジェクトを編集',
    'dashboard.deleteAria': 'プロジェクトを削除',
    'dashboard.changeCover': 'カバーを変更',
    'dashboard.addCover': 'カバー画像を追加',
    'dashboard.wishlistsCount': 'ウィッシュリスト',
    'dashboard.createTitle': 'プロジェクトを作成',
    'dashboard.slugHelp': '公開URL: katoa.org/project/${slug}',
    'dashboard.sectionShare': '共有と埋め込み',
    'terms.metaTitle': '利用規約',
    'terms.metaDesc': 'KATOA利用規約 — 手数料ゼロのBitcoinクリエイタープラットフォームご利用のルールとガイドライン。',
    'explore.metaTitle': '探索',
    'explore.metaDesc': '世界中のBitcoinクリエイターのウィッシュリストを閲覧。',
    ...aboutComparisonJa,
  },
  zh: {
    'dashboard.title': '创作者仪表板',
    'dashboard.subtitle': '管理项目并追踪成果',
    'dashboard.loading': '正在加载仪表板...',
    'dashboard.projects': '项目',
    'dashboard.wishlists': '心愿单',
    'dashboard.raised': '总筹集额',
    'dashboard.createProject': '创建项目',
    'dashboard.demo': '演示预览 — 仅UI。连接Supabase以保存项目并接受真实付款。',
    'wishlist.loading': '正在加载心愿单...',
    'wishlist.notFound': '未找到心愿单',
    'wishlist.notFoundDesc': '此心愿单不存在或已被删除。',
    'wishlist.sendGift': '发送礼物',
    'wishlist.supportItems': '支持此心愿单中的特定物品',
    'wishlist.explore': '探索心愿单',
    'about.hero.line1': '真正',
    'about.hero.highlight': '为创作者服务的平台',
    'about.hero.subtitle': '我们不只是另一个平台。我们是一场运动。',
    'about.hero.badge': '由创作者打造，为创作者服务',
    'about.problem.title': '创作者经济已经崩溃',
    'about.problem.subtitle': '传统平台抽取太多。以下是创作者损失的金额：',
    'comparison.hero.line1': '为什么选择',
    'comparison.hero.highlight': 'KATOA？',
    'comparison.hero.subtitle': '我们不只是另一个创作者平台。基于比特币闪电网络，为全球创作者设计，零费用（至2026年12月31日）。',
    'comparison.saveMonthly': '使用KATOA每月最多节省${amount}',
    'explore.allProjects': '探索所有项目',
    'explore.loadMore': '加载更多',
    'explore.remaining': '剩余',
    'explore.filters': '筛选',
    'explore.favoritesOnly': '仅收藏',
    'explore.clearFilters': '清除所有筛选',
    'explore.searchProjects': '搜索项目、创作者、标签...',
    'explore.quickFilters': '快速筛选：',
    'explore.showResults': '显示${count}个结果',
    'explore.loadingMap': '正在加载地图…',
    'explore.demoBanner': '显示示例项目 — 实时数据库不可用。请使用演示数据自由探索。',
    'explore.browseAll': '浏览所有项目',
    'explore.clearFiltersAction': '清除筛选',
    'explore.filtersTitle': '筛选',
    'explore.closeFilters': '关闭筛选',
    'explore.filtersActive': '筛选已启用',
    'explore.countryFlag': '国旗',
    'explore.sortBy': '排序',
    'explore.sortRecent': '最新',
    'explore.sortTrending': '筹集最多',
    'explore.sortFunded': '进度最高',
    'explore.sortGoal': '目标最大',
    'explore.category': '类别',
    'explore.allCategories': '所有类别',
    'explore.location': '位置',
    'explore.noFavorites': '暂无收藏',
    'explore.noProjects': '暂无项目',
    'explore.emptyDescFavorites': '点击您喜欢的项目上的心形 — 它们会显示在这里。',
    'explore.emptyDescNone': '敬请期待新的创作者和活动！',
    'explore.funded': '已筹集',
    'explore.addFavorite': '添加到收藏',
    'explore.removeFavorite': '从收藏中移除',
    'explore.recentlyViewed': '最近浏览',
    'explore.clear': '清除',
    'explore.searchAria': '搜索项目',
    'explore.loadingProjects': '正在加载项目',
    'dashboard.placeholder.title': '项目标题',
    'dashboard.placeholder.description': '描述',
    'dashboard.placeholder.titleExample': '例如：社区花园基金',
    'dashboard.placeholder.descriptionLong': '向人们介绍您的项目以及您想要实现的目标...',
    'dashboard.placeholder.slug': 'community-garden-fund',
    'project.placeholder.title': '项目标题',
    'project.placeholder.description': '描述您的项目以及您正在构建的内容...',
    'project.placeholder.wishlistTitle': '例如：录音设备基金',
    'project.placeholder.wishlistDescription': '告诉支持者此心愿单的用途及其重要性...',
    'project.placeholder.url': 'https://amazon.com/wishlist/...',
    'project.placeholder.wishlistName': '我的心愿单',
    'project.placeholder.slug': 'my-wishlist',
    'project.placeholder.wishlistDesc': '此心愿单的用途是什么？',
    'settings.placeholder.username': 'johndoe',
    'settings.placeholder.bio': '向世界介绍您自己...',
    'settings.placeholder.lightning': 'username@wallet.com',
    'settings.placeholder.nostr': 'npub1...',
    'wishlist.placeholder.amount': '21000',
    'wishlist.placeholder.name': '匿名',
    'wishlist.placeholder.message': '给创作者留言...',
    'auth.placeholder.username': 'johndoe',
    'auth.placeholder.email': 'you@example.com',
    'auth.placeholder.password': '••••••••',
    'notfound.title': '页面未找到',
    'notfound.subtitle': '此页面不存在。返回探索创作者或创建您自己的心愿单。',
    'notfound.backHome': '返回首页',
    'notfound.metaTitle': '页面未找到',
    'notfound.metaDesc': '您要查找的页面在KATOA上不存在。',
    'privacy.title': '隐私政策',
    'privacy.lastUpdated': '最后更新：2025年10月27日',
    'pitch.navHint': '使用方向键或滑动导航',
    'pitch.prevSlide': '上一张幻灯片',
    'pitch.nextSlide': '下一张幻灯片',
    'pitch.goToSlide': '转到幻灯片${n}',
    'pitch.slideAnnouncement': '幻灯片${current}/${total}：${title}',
    'pitch.startEarning': '开始赚取100%',
    'pitch.comparePlatforms': '比较平台',
    'pitch.metaTitle': '推介演示',
    'pitch.metaDesc': 'KATOA推介 — 零费用比特币创作者平台。',
    'dashboard.videoHint': '在心愿单上传 MP4 或 MOV 封面视频。',
    'dashboard.videoHintLink': '浏览视频创作者',
    'video.upload.title': '封面视频',
    'video.upload.hint': 'MP4、MOV、WebM — 最大 200MB',
    'video.upload.success': '视频已上传',
    'video.upload.error': '视频上传失败',
    'video.upload.tooLarge': '视频必须小于 ${max}MB',
    'video.upload.invalid': '请使用 MP4、MOV、WebM 或 M4V',
    'video.upload.empty': '添加封面视频',
    'video.upload.drop': '拖放视频，或点击选择',
    'video.upload.change': '更换视频',
    'video.upload.remove': '移除视频',
    'cover.image.title': '封面图片',
    'cover.image.empty': '添加封面图片',
    'cover.image.drop': '拖放图片，或点击选择',
    'cover.image.change': '更换封面',
    'cover.image.remove': '移除封面',
    'cover.image.hint': 'JPG、PNG、WebP — 最大 ${max}MB',
    'cover.image.invalid': '请使用 JPG、PNG、WebP 或 GIF',
    'cover.image.tooLarge': '图片必须小于 ${max}MB',
    'dashboard.empty.title': '开始您的第一个项目',
    'dashboard.empty.description': '项目把多份心愿单归到一处。适合活动、公益或创作者作品集。',
    'dashboard.empty.action': '创建第一个项目',
    'dashboard.welcome': '欢迎回来',
    'dashboard.welcomeGuest': '您的创作者主页',
    'dashboard.newProject': '新建项目',
    'dashboard.yourProjects': '您的项目',
    'dashboard.yourProjectsSub': '把心愿单收归一处',
    'dashboard.following': '关注',
    'dashboard.followingSub': '您支持的项目、心愿单和创作者',
    'dashboard.followingEmpty.title': '还没有关注任何人',
    'dashboard.followingEmpty.description': '探索创作者和公益，他们会出现在这里。',
    'dashboard.followingEmpty.action': '立即探索',
    'dashboard.stats.following': '关注',
    'dashboard.quick.settings': '设置',
    'dashboard.quick.messages': '消息',
    'dashboard.quick.templates': '模板',
    'dashboard.quick.explore': '探索',
    'dashboard.creators': '创作者',
    'dashboard.lightningMissing': '添加闪电地址，让粉丝即时付款。无银行，无抽成。',
    'dashboard.lightningMissingCta': '添加钱包',
    'dashboard.growTitle': '增长，平台零抽成',
    'dashboard.growBody': '封面视频、商品链接和可分享页面。粉丝打赏sats或替您购买。',
    'dashboard.viewPublic': '查看公开页',
    'dashboard.manage': '管理',
    'dashboard.noDescription': '暂无描述',
    'dashboard.filterAll': '全部',
    'dashboard.noFollowsFilter': '此筛选下暂无内容。',
    'dashboard.uploadDemo': '封面上传需要真实账户。',
    'dashboard.createDemo': '已保存在本演示本地 — 连接真实账户即可发布。',
    'dashboard.editAria': '编辑项目',
    'dashboard.deleteAria': '删除项目',
    'dashboard.changeCover': '更换封面',
    'dashboard.addCover': '添加封面图',
    'dashboard.wishlistsCount': '心愿单',
    'dashboard.createTitle': '创建项目',
    'dashboard.slugHelp': '公开链接：katoa.org/project/${slug}',
    'dashboard.sectionShare': '分享与嵌入',
    'terms.metaTitle': '服务条款',
    'terms.metaDesc': 'KATOA 服务条款 — 使用我们零费用比特币创作者平台的规则与准则。',
    'explore.metaTitle': '探索',
    'explore.metaDesc': '浏览全球比特币创作者的心愿单。',
    ...aboutComparisonZh,
  },
};

const commonStrings = {
  en: {
    'common.loading': 'Loading...',
    'common.offline': 'Offline',
    'a11y.skipToContent': 'Skip to content',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.create': 'Create',
    'common.search': 'Search',
    'common.go': 'Go',
    'common.learnMore': 'Learn more',
    'common.getStarted': 'Get Started',
    'common.signIn': 'Sign In',
    'common.signUp': 'Sign Up',
    'onboarding.badge': '60-second setup',
    'onboarding.title': 'Quick start checklist',
    'onboarding.subtitle': 'Tick these off to launch your first wishlist',
    'onboarding.item.account': 'Create your free account',
    'onboarding.item.wallet': 'Add a Lightning address',
    'onboarding.item.wishlist': 'Create your first wishlist',
    'onboarding.item.share': 'Share your public page',
    'onboarding.item.firstsat': 'Receive (or send) your first sat',
    'common.delete': 'Delete',
    'confirm.deleteWishlist.title': 'Delete Wishlist',
    'confirm.deleteWishlist.message': 'Are you sure you want to delete this wishlist? This cannot be undone.',
    'confirm.deleteAddress.title': 'Delete Address',
    'confirm.deleteAddress.message': 'Are you sure you want to delete this payment address?',
    'confirm.deletePaymentMethod.title': 'Delete Payment Method',
    'confirm.deletePaymentMethod.message': 'Are you sure you want to delete this payment method?',
    'confirm.deleteProject.title': 'Delete Project',
    'confirm.deleteProject.message': 'Are you sure you want to delete this project? This will also delete all associated wishlists.',
    'error.createProject': 'Failed to create project',
    'error.deleteProject': 'Failed to delete project',
    'error.updateProject': 'Failed to update project',
    'error.uploadBackground': 'Failed to upload background',
    'error.createWishlist': 'Failed to create wishlist',
    'error.deleteWishlist': 'Failed to delete wishlist',
    'error.updateWishlist': 'Failed to update wishlist',
    'error.addAddress': 'Failed to add address',
    'error.deleteAddress': 'Failed to delete address',
    'error.savePaymentMethod': 'Failed to save payment method',
    'error.deletePaymentMethod': 'Failed to delete payment method',
    'error.updateProfile': 'Failed to update profile',
    'error.uploadAvatar': 'Failed to upload avatar',
    'error.uploadBanner': 'Failed to upload banner',
    'error.qrImageScan': 'QR scanning from image requires additional library. Please paste the address manually.',
    'success.deleted': 'Deleted successfully',
    'success.saved': 'Saved successfully',
    'error.loadDashboard': 'Could not load dashboard data. Please try again.',
  },
  es: {
    'common.loading': 'Cargando...',
    'common.offline': 'Sin conexión',
    'a11y.skipToContent': 'Saltar al contenido',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.create': 'Crear',
    'common.search': 'Buscar',
    'common.go': 'Ir',
    'common.learnMore': 'Saber más',
    'common.getStarted': 'Comenzar',
    'common.signIn': 'Iniciar sesión',
    'common.signUp': 'Registrarse',
    'onboarding.badge': 'Configuración en 60 segundos',
    'onboarding.title': 'Lista de inicio rápido',
    'onboarding.subtitle': 'Marca estos pasos para lanzar tu primera lista',
    'onboarding.item.account': 'Crea tu cuenta gratis',
    'onboarding.item.wallet': 'Añade una dirección Lightning',
    'onboarding.item.wishlist': 'Crea tu primera lista',
    'onboarding.item.share': 'Comparte tu página pública',
    'onboarding.item.firstsat': 'Recibe (o envía) tu primer sat',
    'common.delete': 'Eliminar',
    'confirm.deleteWishlist.title': 'Eliminar lista',
    'confirm.deleteWishlist.message': '¿Seguro que quieres eliminar esta lista? No se puede deshacer.',
    'confirm.deleteAddress.title': 'Eliminar dirección',
    'confirm.deleteAddress.message': '¿Seguro que quieres eliminar esta dirección de pago?',
    'confirm.deletePaymentMethod.title': 'Eliminar método de pago',
    'confirm.deletePaymentMethod.message': '¿Seguro que quieres eliminar este método de pago?',
    'confirm.deleteProject.title': 'Eliminar proyecto',
    'confirm.deleteProject.message': '¿Seguro que quieres eliminar este proyecto? También se eliminarán todas las listas asociadas.',
    'error.createProject': 'Error al crear el proyecto',
    'error.deleteProject': 'Error al eliminar el proyecto',
    'error.updateProject': 'Error al actualizar el proyecto',
    'error.uploadBackground': 'Error al subir el fondo',
    'error.createWishlist': 'Error al crear la lista',
    'error.deleteWishlist': 'Error al eliminar la lista',
    'error.updateWishlist': 'Error al actualizar la lista',
    'error.addAddress': 'Error al añadir la dirección',
    'error.deleteAddress': 'Error al eliminar la dirección',
    'error.savePaymentMethod': 'Error al guardar el método de pago',
    'error.deletePaymentMethod': 'Error al eliminar el método de pago',
    'error.updateProfile': 'Error al actualizar el perfil',
    'error.uploadAvatar': 'Error al subir el avatar',
    'error.uploadBanner': 'Error al subir el banner',
    'error.qrImageScan': 'El escaneo QR desde imagen requiere una biblioteca adicional. Pega la dirección manualmente.',
    'success.deleted': 'Eliminado correctamente',
    'success.saved': 'Guardado correctamente',
    'error.loadDashboard': 'No se pudieron cargar los datos del panel. Inténtalo de nuevo.',
  },
  pt: {
    'common.loading': 'Carregando...',
    'common.offline': 'Offline',
    'a11y.skipToContent': 'Ir para o conteúdo',
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.create': 'Criar',
    'common.search': 'Pesquisar',
    'common.go': 'Ir',
    'common.learnMore': 'Saiba mais',
    'common.getStarted': 'Começar',
    'common.signIn': 'Entrar',
    'common.signUp': 'Cadastrar',
    'onboarding.badge': 'Configuração em 60 segundos',
    'onboarding.title': 'Checklist de início rápido',
    'onboarding.subtitle': 'Marque estes itens para lançar sua primeira lista',
    'onboarding.item.account': 'Crie sua conta gratuita',
    'onboarding.item.wallet': 'Adicione um endereço Lightning',
    'onboarding.item.wishlist': 'Crie sua primeira lista',
    'onboarding.item.share': 'Compartilhe sua página pública',
    'onboarding.item.firstsat': 'Receba (ou envie) seu primeiro sat',
    'common.delete': 'Excluir',
    'confirm.deleteWishlist.title': 'Excluir lista',
    'confirm.deleteWishlist.message': 'Tem certeza que deseja excluir esta lista? Isso não pode ser desfeito.',
    'confirm.deleteAddress.title': 'Excluir endereço',
    'confirm.deleteAddress.message': 'Tem certeza que deseja excluir este endereço de pagamento?',
    'confirm.deletePaymentMethod.title': 'Excluir método de pagamento',
    'confirm.deletePaymentMethod.message': 'Tem certeza que deseja excluir este método de pagamento?',
    'confirm.deleteProject.title': 'Excluir projeto',
    'confirm.deleteProject.message': 'Tem certeza que deseja excluir este projeto? Todas as listas associadas também serão excluídas.',
    'error.createProject': 'Falha ao criar projeto',
    'error.deleteProject': 'Falha ao excluir projeto',
    'error.updateProject': 'Falha ao atualizar projeto',
    'error.uploadBackground': 'Falha ao enviar fundo',
    'error.createWishlist': 'Falha ao criar lista',
    'error.deleteWishlist': 'Falha ao excluir lista',
    'error.updateWishlist': 'Falha ao atualizar lista',
    'error.addAddress': 'Falha ao adicionar endereço',
    'error.deleteAddress': 'Falha ao excluir endereço',
    'error.savePaymentMethod': 'Falha ao salvar método de pagamento',
    'error.deletePaymentMethod': 'Falha ao excluir método de pagamento',
    'error.updateProfile': 'Falha ao atualizar perfil',
    'error.uploadAvatar': 'Falha ao enviar avatar',
    'error.uploadBanner': 'Falha ao enviar banner',
    'error.qrImageScan': 'Escaneamento QR de imagem requer biblioteca adicional. Cole o endereço manualmente.',
    'success.deleted': 'Excluído com sucesso',
    'success.saved': 'Salvo com sucesso',
    'error.loadDashboard': 'Não foi possível carregar os dados do painel. Tente novamente.',
  },
  fr: {
    'common.loading': 'Chargement...',
    'common.offline': 'Hors ligne',
    'a11y.skipToContent': 'Aller au contenu',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.create': 'Créer',
    'common.search': 'Rechercher',
    'common.go': 'Aller',
    'common.learnMore': 'En savoir plus',
    'common.getStarted': 'Commencer',
    'common.signIn': 'Connexion',
    'common.signUp': 'S\'inscrire',
    'onboarding.badge': 'Configuration en 60 secondes',
    'onboarding.title': 'Liste de démarrage rapide',
    'onboarding.subtitle': 'Cochez ces étapes pour lancer votre première liste',
    'onboarding.item.account': 'Créez votre compte gratuit',
    'onboarding.item.wallet': 'Ajoutez une adresse Lightning',
    'onboarding.item.wishlist': 'Créez votre première liste',
    'onboarding.item.share': 'Partagez votre page publique',
    'onboarding.item.firstsat': 'Recevez (ou envoyez) votre premier sat',
    'common.delete': 'Supprimer',
    'confirm.deleteWishlist.title': 'Supprimer la liste',
    'confirm.deleteWishlist.message': 'Êtes-vous sûr de vouloir supprimer cette liste ? Cette action est irréversible.',
    'confirm.deleteAddress.title': 'Supprimer l\'adresse',
    'confirm.deleteAddress.message': 'Êtes-vous sûr de vouloir supprimer cette adresse de paiement ?',
    'confirm.deletePaymentMethod.title': 'Supprimer le mode de paiement',
    'confirm.deletePaymentMethod.message': 'Êtes-vous sûr de vouloir supprimer ce mode de paiement ?',
    'confirm.deleteProject.title': 'Supprimer le projet',
    'confirm.deleteProject.message': 'Êtes-vous sûr de vouloir supprimer ce projet ? Toutes les listes associées seront également supprimées.',
    'error.createProject': 'Échec de la création du projet',
    'error.deleteProject': 'Échec de la suppression du projet',
    'error.updateProject': 'Échec de la mise à jour du projet',
    'error.uploadBackground': 'Échec du téléchargement de l\'arrière-plan',
    'error.createWishlist': 'Échec de la création de la liste',
    'error.deleteWishlist': 'Échec de la suppression de la liste',
    'error.updateWishlist': 'Échec de la mise à jour de la liste',
    'error.addAddress': 'Échec de l\'ajout de l\'adresse',
    'error.deleteAddress': 'Échec de la suppression de l\'adresse',
    'error.savePaymentMethod': 'Échec de l\'enregistrement du mode de paiement',
    'error.deletePaymentMethod': 'Échec de la suppression du mode de paiement',
    'error.updateProfile': 'Échec de la mise à jour du profil',
    'error.uploadAvatar': 'Échec du téléchargement de l\'avatar',
    'error.uploadBanner': 'Échec du téléchargement de la bannière',
    'error.qrImageScan': 'Le scan QR depuis une image nécessite une bibliothèque supplémentaire. Collez l\'adresse manuellement.',
    'success.deleted': 'Supprimé avec succès',
    'success.saved': 'Enregistré avec succès',
    'error.loadDashboard': 'Impossible de charger le tableau de bord. Réessayez.',
  },
  de: {
    'common.loading': 'Laden...',
    'common.offline': 'Offline',
    'a11y.skipToContent': 'Zum Inhalt springen',
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen',
    'common.create': 'Erstellen',
    'common.search': 'Suchen',
    'common.go': 'Los',
    'common.learnMore': 'Mehr erfahren',
    'common.getStarted': 'Loslegen',
    'common.signIn': 'Anmelden',
    'common.signUp': 'Registrieren',
    'onboarding.badge': '60-Sekunden-Setup',
    'onboarding.title': 'Schnellstart-Checkliste',
    'onboarding.subtitle': 'Hake diese Punkte ab, um deine erste Wunschliste zu starten',
    'onboarding.item.account': 'Kostenloses Konto erstellen',
    'onboarding.item.wallet': 'Lightning-Adresse hinzufügen',
    'onboarding.item.wishlist': 'Erste Wunschliste erstellen',
    'onboarding.item.share': 'Öffentliche Seite teilen',
    'onboarding.item.firstsat': 'Ersten Sat empfangen (oder senden)',
    'common.delete': 'Löschen',
    'confirm.deleteWishlist.title': 'Wunschliste löschen',
    'confirm.deleteWishlist.message': 'Möchtest du diese Wunschliste wirklich löschen? Dies kann nicht rückgängig gemacht werden.',
    'confirm.deleteAddress.title': 'Adresse löschen',
    'confirm.deleteAddress.message': 'Möchtest du diese Zahlungsadresse wirklich löschen?',
    'confirm.deletePaymentMethod.title': 'Zahlungsmethode löschen',
    'confirm.deletePaymentMethod.message': 'Möchtest du diese Zahlungsmethode wirklich löschen?',
    'confirm.deleteProject.title': 'Projekt löschen',
    'confirm.deleteProject.message': 'Möchtest du dieses Projekt wirklich löschen? Alle zugehörigen Wunschlisten werden ebenfalls gelöscht.',
    'error.createProject': 'Projekt konnte nicht erstellt werden',
    'error.deleteProject': 'Projekt konnte nicht gelöscht werden',
    'error.updateProject': 'Projekt konnte nicht aktualisiert werden',
    'error.uploadBackground': 'Hintergrund konnte nicht hochgeladen werden',
    'error.createWishlist': 'Wunschliste konnte nicht erstellt werden',
    'error.deleteWishlist': 'Wunschliste konnte nicht gelöscht werden',
    'error.updateWishlist': 'Wunschliste konnte nicht aktualisiert werden',
    'error.addAddress': 'Adresse konnte nicht hinzugefügt werden',
    'error.deleteAddress': 'Adresse konnte nicht gelöscht werden',
    'error.savePaymentMethod': 'Zahlungsmethode konnte nicht gespeichert werden',
    'error.deletePaymentMethod': 'Zahlungsmethode konnte nicht gelöscht werden',
    'error.updateProfile': 'Profil konnte nicht aktualisiert werden',
    'error.uploadAvatar': 'Avatar konnte nicht hochgeladen werden',
    'error.uploadBanner': 'Banner konnte nicht hochgeladen werden',
    'error.qrImageScan': 'QR-Scan aus Bild erfordert eine zusätzliche Bibliothek. Bitte Adresse manuell einfügen.',
    'success.deleted': 'Erfolgreich gelöscht',
    'success.saved': 'Erfolgreich gespeichert',
    'error.loadDashboard': 'Dashboard-Daten konnten nicht geladen werden. Bitte erneut versuchen.',
  },
  ja: {
    'common.loading': '読み込み中...',
    'common.offline': 'オフライン',
    'a11y.skipToContent': 'コンテンツへスキップ',
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.create': '作成',
    'common.search': '検索',
    'common.go': '移動',
    'common.learnMore': '詳しく見る',
    'common.getStarted': '始める',
    'common.signIn': 'ログイン',
    'common.signUp': '新規登録',
    'onboarding.badge': '60秒セットアップ',
    'onboarding.title': 'クイックスタートチェックリスト',
    'onboarding.subtitle': '最初のウィッシュリストを公開するためのステップ',
    'onboarding.item.account': '無料アカウントを作成',
    'onboarding.item.wallet': 'Lightningアドレスを追加',
    'onboarding.item.wishlist': '最初のウィッシュリストを作成',
    'onboarding.item.share': '公開ページを共有',
    'onboarding.item.firstsat': '最初のsatを受け取る（または送る）',
    'common.delete': '削除',
    'confirm.deleteWishlist.title': 'ウィッシュリストを削除',
    'confirm.deleteWishlist.message': 'このウィッシュリストを削除してもよろしいですか？元に戻せません。',
    'confirm.deleteAddress.title': 'アドレスを削除',
    'confirm.deleteAddress.message': 'この支払いアドレスを削除してもよろしいですか？',
    'confirm.deletePaymentMethod.title': '支払い方法を削除',
    'confirm.deletePaymentMethod.message': 'この支払い方法を削除してもよろしいですか？',
    'confirm.deleteProject.title': 'プロジェクトを削除',
    'confirm.deleteProject.message': 'このプロジェクトを削除してもよろしいですか？関連するウィッシュリストもすべて削除されます。',
    'error.createProject': 'プロジェクトの作成に失敗しました',
    'error.deleteProject': 'プロジェクトの削除に失敗しました',
    'error.updateProject': 'プロジェクトの更新に失敗しました',
    'error.uploadBackground': '背景のアップロードに失敗しました',
    'error.createWishlist': 'ウィッシュリストの作成に失敗しました',
    'error.deleteWishlist': 'ウィッシュリストの削除に失敗しました',
    'error.updateWishlist': 'ウィッシュリストの更新に失敗しました',
    'error.addAddress': 'アドレスの追加に失敗しました',
    'error.deleteAddress': 'アドレスの削除に失敗しました',
    'error.savePaymentMethod': '支払い方法の保存に失敗しました',
    'error.deletePaymentMethod': '支払い方法の削除に失敗しました',
    'error.updateProfile': 'プロフィールの更新に失敗しました',
    'error.uploadAvatar': 'アバターのアップロードに失敗しました',
    'error.uploadBanner': 'バナーのアップロードに失敗しました',
    'error.qrImageScan': '画像からのQRスキャンには追加ライブラリが必要です。アドレスを手動で貼り付けてください。',
    'success.deleted': '削除しました',
    'success.saved': '保存しました',
    'error.loadDashboard': 'ダッシュボードの読み込みに失敗しました。もう一度お試しください。',
  },
  zh: {
    'common.loading': '加载中...',
    'common.offline': '离线',
    'a11y.skipToContent': '跳转到内容',
    'common.save': '保存',
    'common.cancel': '取消',
    'common.create': '创建',
    'common.search': '搜索',
    'common.go': '前往',
    'common.learnMore': '了解更多',
    'common.getStarted': '开始使用',
    'common.signIn': '登录',
    'common.signUp': '注册',
    'onboarding.badge': '60秒设置',
    'onboarding.title': '快速入门清单',
    'onboarding.subtitle': '勾选这些步骤以发布你的第一个心愿单',
    'onboarding.item.account': '创建免费账户',
    'onboarding.item.wallet': '添加闪电地址',
    'onboarding.item.wishlist': '创建第一个心愿单',
    'onboarding.item.share': '分享你的公开页面',
    'onboarding.item.firstsat': '收到（或发送）第一枚 sat',
    'common.delete': '删除',
    'confirm.deleteWishlist.title': '删除心愿单',
    'confirm.deleteWishlist.message': '确定要删除此心愿单吗？此操作无法撤销。',
    'confirm.deleteAddress.title': '删除地址',
    'confirm.deleteAddress.message': '确定要删除此支付地址吗？',
    'confirm.deletePaymentMethod.title': '删除支付方式',
    'confirm.deletePaymentMethod.message': '确定要删除此支付方式吗？',
    'confirm.deleteProject.title': '删除项目',
    'confirm.deleteProject.message': '确定要删除此项目吗？所有关联的心愿单也将被删除。',
    'error.createProject': '创建项目失败',
    'error.deleteProject': '删除项目失败',
    'error.updateProject': '更新项目失败',
    'error.uploadBackground': '上传背景失败',
    'error.createWishlist': '创建心愿单失败',
    'error.deleteWishlist': '删除心愿单失败',
    'error.updateWishlist': '更新心愿单失败',
    'error.addAddress': '添加地址失败',
    'error.deleteAddress': '删除地址失败',
    'error.savePaymentMethod': '保存支付方式失败',
    'error.deletePaymentMethod': '删除支付方式失败',
    'error.updateProfile': '更新个人资料失败',
    'error.uploadAvatar': '上传头像失败',
    'error.uploadBanner': '上传横幅失败',
    'error.qrImageScan': '从图片扫描二维码需要额外库。请手动粘贴地址。',
    'success.deleted': '删除成功',
    'success.saved': '保存成功',
    'error.loadDashboard': '无法加载仪表盘数据，请重试。',
  },
};

export const translations: Record<Language, Record<string, string>> = {
  en: {
    'nav.explore': 'Explore',
    'nav.dashboard': 'Dashboard',
    'nav.settings': 'Settings',
    'nav.login': 'Login',
    'nav.signup': 'Sign Up',
    'hero.title': 'Fund Dreams with Bitcoin',
    'hero.subtitle': 'Create wishlists, share your story, and receive Bitcoin support from around the world',
    'hero.cta': 'Get Started',
    'hero.explore': 'Explore Wishlists',
    'footer.about': 'About',
    'footer.contact': 'Contact',
    'footer.pricing': 'Pricing',
    'footer.terms': 'Terms of Service',
    'footer.privacy': 'Privacy Policy',
    'footer.tagline': 'Empowering dreams with Bitcoin',
    'explore.title': 'Explore Wishlists',
    'explore.subtitle': 'Discover amazing creators and support their dreams around the world',
    'explore.search': 'Search wishlists...',
    'explore.allCountries': 'All Countries',
    'explore.showMap': 'Show Map',
    'explore.hideMap': 'Hide Map',
    'explore.mapTitle': 'Wishlists Around the World',
    'explore.raised': 'raised',
    'explore.noResults': 'No results found',
    'explore.tryAgain': 'Try adjusting your filters',
    'about.title': 'About Katoa',
    'about.subtitle': 'Our Mission',
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Get in Touch',
    'pricing.title': 'Pricing',
    'pricing.subtitle': 'Simple, Transparent',
    ...homeStrings.en,
    ...navUiStrings.en,
    ...contactPageStrings.en,
    ...faqPageStrings.en,
    ...pricingPageStrings.en,
    ...commonStrings.en,
    ...pageStrings.en,
    ...remainingEn,
    ...trustStrings.en,
  },
  es: {
    'nav.explore': 'Explorar',
    'nav.dashboard': 'Panel',
    'nav.settings': 'Ajustes',
    'nav.login': 'Iniciar Sesión',
    'nav.signup': 'Registrarse',
    'hero.title': 'Financia Sueños con Bitcoin',
    'hero.subtitle': 'Crea listas de deseos, comparte tu historia y recibe apoyo en Bitcoin de todo el mundo',
    'hero.cta': 'Comenzar',
    'hero.explore': 'Explorar Listas',
    'footer.about': 'Acerca de',
    'footer.contact': 'Contacto',
    'footer.pricing': 'Precios',
    'footer.terms': 'Términos de Servicio',
    'footer.privacy': 'Política de Privacidad',
    'footer.tagline': 'Empoderando sueños con Bitcoin',
    'explore.title': 'Explorar Listas de Deseos',
    'explore.subtitle': 'Descubre creadores increíbles y apoya sus sueños en todo el mundo',
    'explore.search': 'Buscar listas...',
    'explore.allCountries': 'Todos los Países',
    'explore.showMap': 'Mostrar Mapa',
    'explore.hideMap': 'Ocultar Mapa',
    'explore.mapTitle': 'Listas de Deseos en el Mundo',
    'explore.raised': 'recaudado',
    'explore.noResults': 'No se encontraron resultados',
    'explore.tryAgain': 'Intenta ajustar tus filtros',
    'about.title': 'Acerca de Katoa',
    'about.subtitle': 'Nuestra Misión',
    'contact.title': 'Contáctanos',
    'contact.subtitle': 'Ponte en Contacto',
    'pricing.title': 'Precios',
    'pricing.subtitle': 'Simple y Transparente',
    ...homeStrings.es,
    ...navUiStrings.es,
    ...contactPageStrings.es,
    ...faqPageStrings.es,
    ...pricingPageStrings.es,
    ...commonStrings.es,
    ...pageStrings.es,
    ...remainingEs,
    ...trustStrings.es,
    // Keys the UI renders that only English used to define; see i18nUsage.test.ts.
    'a11y.closeDialog': 'Cerrar diálogo',
    'a11y.dialog': 'Diálogo',
    'a11y.navigatedTo': 'Navegaste a',
    'contact.messageCopied': 'Mensaje copiado — pégalo en tu correo si no se abre ninguna app de correo',
    'demo.badge': 'Demo',
    'donate.addressLabel': 'Dirección Bitcoin',
    'donate.copied': 'Copiado al portapapeles',
    'donate.copyAddress': 'Copiar dirección',
    'donate.customAmount': 'Importe personalizado (sats)',
    'donate.openInWallet': 'Abrir en la cartera',
    'donate.safeHarbour':
      'Software educativo. Envías sats directamente al destinatario — KATOA nunca guarda tus fondos.',
    'donate.scanSubtitle': 'Cualquier cartera Bitcoin · autocustodia',
    'donate.scanTitle': 'Escanea para donar',
    'donate.share': 'Compartir',
    'donate.tapCopy': 'Toca para copiar la dirección',
    'donate.thanks': 'Tus sats mantienen KATOA gratis y de código abierto. Gracias.',
    'donate.tipAmount': 'Importe sugerido',
    'explore.loadingMore': 'Cargando más proyectos',
    'explore.schemaName': 'Wishlists de creadores de KATOA',
    'footer.copyFailed': 'No se pudo copiar la dirección',
    'gift.invalidAmount': 'Introduce un importe válido en sats (mayor que 0)',
    'home.stats.sampleRaised': 'Sats de muestra recaudados',
    'home.statsSample':
      'Las estadísticas de abajo vienen del catálogo de muestra público (marcado como demo) hasta que se conecten los contadores reales.',
    'home.statsUnavailable':
      'Los contadores en vivo no están disponibles ahora mismo — sin cifras infladas.',
    'payment.modal.add': 'Añadir método de pago',
    'payment.modal.edit': 'Editar método de pago',
    'payment.tabList': 'Método de pago',
    'product.importHelp':
      'Pega una URL de Amazon, Nike, Etsy, Shopify o de cualquier tienda. Tus seguidores pueden financiarlo en sats o comprarte el artículo.',
    'product.importTitle': 'Añadir desde enlace de producto',
    'product.invalidUrl': 'Introduce un enlace de producto válido (https://…)',
    'product.parseFailed':
      'No se pudo leer toda la página — edita los detalles y añádelo. El enlace de compra sigue funcionando.',
    'progress.ofGoal': 'del objetivo alcanzado',
    'roadmap.inApp.meta': 'Lo que ya está disponible y lo que viene en KATOA.',
    'roadmap.inApp.title': 'Hoja de ruta pública',
    'security.title': 'Seguridad y custodia',
    'settings.nostrHint': 'Opcional — pega tu clave pública npub1… para tu identidad Nostr',
    'share.facebook': 'Compartir en Facebook',
    'share.linkedin': 'Compartir en LinkedIn',
    'share.nostr': 'Compartir con una nota de Nostr',
    'share.promo': '¡Apoya esta wishlist con Bitcoin!',
    'share.reddit': 'Compartir en Reddit',
    'share.telegram': 'Compartir en Telegram',
    'share.whatsapp': 'Compartir en WhatsApp',
    'terms.lastUpdated': 'Última actualización: 27 de octubre de 2025',
    'ticker.summary':
      'Logros recientes de creadores: cero comisiones, pagos Lightning instantáneos, apoyo global.',
    'tipMenu.presetsLabel': 'Tus importes de propina (ajustes)',
    'wishlist.countryFlag': 'País: ${country}',
    'wishlist.countryFlagGeneric': 'Bandera del país',
  },
  pt: {
    'nav.explore': 'Explorar',
    'nav.dashboard': 'Painel',
    'nav.settings': 'Configurações',
    'nav.login': 'Entrar',
    'nav.signup': 'Cadastrar',
    'hero.title': 'Financie Sonhos com Bitcoin',
    'hero.subtitle': 'Crie listas de desejos, compartilhe sua história e receba apoio em Bitcoin do mundo todo',
    'hero.cta': 'Começar',
    'hero.explore': 'Explorar Listas',
    'footer.about': 'Sobre',
    'footer.contact': 'Contato',
    'footer.pricing': 'Preços',
    'footer.terms': 'Termos de Serviço',
    'footer.privacy': 'Política de Privacidade',
    'footer.tagline': 'Capacitando sonhos com Bitcoin',
    'explore.title': 'Explorar Listas de Desejos',
    'explore.subtitle': 'Descubra criadores incríveis e apoie seus sonhos ao redor do mundo',
    'explore.search': 'Pesquisar listas...',
    'explore.allCountries': 'Todos os Países',
    'explore.showMap': 'Mostrar Mapa',
    'explore.hideMap': 'Esconder Mapa',
    'explore.mapTitle': 'Listas de Desejos pelo Mundo',
    'explore.raised': 'arrecadado',
    'explore.noResults': 'Nenhum resultado encontrado',
    'explore.tryAgain': 'Tente ajustar seus filtros',
    'about.title': 'Sobre Katoa',
    'about.subtitle': 'Nossa Missão',
    'contact.title': 'Fale Conosco',
    'contact.subtitle': 'Entre em Contato',
    'pricing.title': 'Preços',
    'pricing.subtitle': 'Simples e Transparente',
    ...homeStrings.pt,
    ...navUiStrings.pt,
    ...contactPageStrings.pt,
    ...faqPageStrings.pt,
    ...pricingPageStrings.pt,
    ...commonStrings.pt,
    ...pageStrings.pt,
    ...remainingPt,
    ...trustStrings.pt,
    // Keys the UI renders that only English used to define; see i18nUsage.test.ts.
    'a11y.closeDialog': 'Fechar diálogo',
    'a11y.dialog': 'Diálogo',
    'a11y.navigatedTo': 'Navegou para',
    'contact.messageCopied': 'Mensagem copiada — cole no seu e-mail se nenhum app de e-mail abrir',
    'demo.badge': 'Demo',
    'donate.addressLabel': 'Endereço Bitcoin',
    'donate.copied': 'Copiado para a área de transferência',
    'donate.copyAddress': 'Copiar endereço',
    'donate.customAmount': 'Valor personalizado (sats)',
    'donate.openInWallet': 'Abrir na carteira',
    'donate.safeHarbour':
      'Software educacional. Você envia sats diretamente ao destinatário — o KATOA nunca guarda seus fundos.',
    'donate.scanSubtitle': 'Qualquer carteira Bitcoin · autocustódia',
    'donate.scanTitle': 'Escaneie para doar',
    'donate.share': 'Compartilhar',
    'donate.tapCopy': 'Toque para copiar o endereço',
    'donate.thanks': 'Seus sats mantêm o KATOA gratuito e de código aberto. Obrigado.',
    'donate.tipAmount': 'Valor sugerido',
    'explore.loadingMore': 'Carregando mais projetos',
    'explore.schemaName': 'Wishlists de criadores do KATOA',
    'explore.exclusive': 'EXCLUSIVO',
    'explore.supporters': 'apoiadores',
    'explore.video': 'VÍDEO',
    'explore.videoCreators': 'Criadores de vídeo',
    'footer.copyFailed': 'Não foi possível copiar o endereço',
    'gift.invalidAmount': 'Informe um valor válido em sats (maior que 0)',
    'home.stats.sampleRaised': 'Sats de exemplo arrecadados',
    'home.statsSample':
      'As estatísticas abaixo vêm do catálogo de exemplo público (marcado como demo) até que os contadores reais sejam conectados.',
    'home.statsUnavailable':
      'Os contadores ao vivo não estão disponíveis agora — sem estimativas infladas.',
    'payment.modal.add': 'Adicionar método de pagamento',
    'payment.modal.edit': 'Editar método de pagamento',
    'payment.tabList': 'Método de pagamento',
    'product.importHelp':
      'Cole uma URL da Amazon, Nike, Etsy, Shopify ou de qualquer loja. Seus apoiadores podem financiar em sats ou comprar o item para você.',
    'product.importTitle': 'Adicionar por link de produto',
    'product.invalidUrl': 'Informe um link de produto válido (https://…)',
    'product.parseFailed':
      'Não foi possível ler a página inteira — edite os detalhes e adicione. O link de compra continua funcionando.',
    'progress.ofGoal': 'da meta alcançada',
    'roadmap.inApp.meta': 'O que já foi entregue e o que vem a seguir no KATOA.',
    'roadmap.inApp.title': 'Roteiro público',
    'security.title': 'Segurança e custódia',
    'settings.nostrHint': 'Opcional — cole sua chave pública npub1… para a identidade Nostr',
    'share.facebook': 'Compartilhar no Facebook',
    'share.linkedin': 'Compartilhar no LinkedIn',
    'share.nostr': 'Compartilhar com uma nota Nostr',
    'share.promo': 'Apoie esta wishlist com Bitcoin!',
    'share.reddit': 'Compartilhar no Reddit',
    'share.telegram': 'Compartilhar no Telegram',
    'share.whatsapp': 'Compartilhar no WhatsApp',
    'terms.lastUpdated': 'Última atualização: 27 de outubro de 2025',
    'ticker.summary':
      'Conquistas recentes de criadores: zero taxas, pagamentos Lightning instantâneos, apoio global.',
    'tipMenu.presetsLabel': 'Seus valores de gorjeta (configurações)',
    'wishlist.countryFlag': 'País: ${country}',
    'wishlist.countryFlagGeneric': 'Bandeira do país',
  },
  fr: {
    'nav.explore': 'Explorer',
    'nav.dashboard': 'Tableau de Bord',
    'nav.settings': 'Paramètres',
    'nav.login': 'Connexion',
    'nav.signup': 'S\'inscrire',
    'hero.title': 'Financez des Rêves avec Bitcoin',
    'hero.subtitle': 'Créez des listes de souhaits, partagez votre histoire et recevez du soutien Bitcoin du monde entier',
    'hero.cta': 'Commencer',
    'hero.explore': 'Explorer les Listes',
    'footer.about': 'À Propos',
    'footer.contact': 'Contact',
    'footer.pricing': 'Tarifs',
    'footer.terms': 'Conditions d\'Utilisation',
    'footer.privacy': 'Politique de Confidentialité',
    'footer.tagline': 'Donner vie aux rêves avec Bitcoin',
    'explore.title': 'Explorer les Listes de Souhaits',
    'explore.subtitle': 'Découvrez des créateurs incroyables et soutenez leurs rêves à travers le monde',
    'explore.search': 'Rechercher des listes...',
    'explore.allCountries': 'Tous les Pays',
    'explore.showMap': 'Afficher la Carte',
    'explore.hideMap': 'Masquer la Carte',
    'explore.mapTitle': 'Listes de Souhaits dans le Monde',
    'explore.raised': 'collecté',
    'explore.noResults': 'Aucun résultat trouvé',
    'explore.tryAgain': 'Essayez d\'ajuster vos filtres',
    'about.title': 'À Propos de Katoa',
    'about.subtitle': 'Notre Mission',
    'contact.title': 'Nous Contacter',
    'contact.subtitle': 'Prenez Contact',
    'pricing.title': 'Tarifs',
    'pricing.subtitle': 'Simple et Transparent',
    ...homeStrings.fr,
    ...navUiStrings.fr,
    ...contactPageStrings.fr,
    ...faqPageStrings.fr,
    ...pricingPageStrings.fr,
    ...commonStrings.fr,
    ...pageStrings.fr,
    ...remainingFr,
    ...trustStrings.fr,
    // Keys the UI renders that only English used to define; see i18nUsage.test.ts.
    'a11y.closeDialog': 'Fermer la boîte de dialogue',
    'a11y.dialog': 'Boîte de dialogue',
    'a11y.navigatedTo': 'Navigation vers',
    'contact.messageCopied':
      'Message copié — collez-le dans votre e-mail si aucune application de messagerie ne s’ouvre',
    'demo.badge': 'Démo',
    'donate.addressLabel': 'Adresse Bitcoin',
    'donate.copied': 'Copié dans le presse-papiers',
    'donate.copyAddress': 'Copier l’adresse',
    'donate.customAmount': 'Montant personnalisé (sats)',
    'donate.openInWallet': 'Ouvrir dans le portefeuille',
    'donate.safeHarbour':
      'Logiciel éducatif. Vous envoyez les sats directement au destinataire — KATOA ne détient jamais vos fonds.',
    'donate.scanSubtitle': 'N’importe quel portefeuille Bitcoin · autoconservation',
    'donate.scanTitle': 'Scannez pour faire un don',
    'donate.share': 'Partager',
    'donate.tapCopy': 'Touchez pour copier l’adresse',
    'donate.thanks': 'Vos sats gardent KATOA gratuit et open source. Merci.',
    'donate.tipAmount': 'Montant suggéré',
    'explore.loadingMore': 'Chargement de projets supplémentaires',
    'explore.schemaName': 'Wishlists de créateurs KATOA',
    'explore.exclusive': 'EXCLUSIF',
    'explore.supporters': 'soutiens',
    'explore.video': 'VIDÉO',
    'explore.videoCreators': 'Créateurs vidéo',
    'footer.copyFailed': 'Impossible de copier l’adresse',
    'gift.invalidAmount': 'Saisissez un montant valide en sats (supérieur à 0)',
    'home.stats.sampleRaised': 'Sats d’exemple collectés',
    'home.statsSample':
      'Les statistiques ci-dessous proviennent du catalogue d’exemple public (marqué démo) tant que les compteurs réels ne sont pas branchés.',
    'home.statsUnavailable':
      'Compteurs en direct indisponibles pour le moment — aucun chiffre gonflé affiché.',
    'payment.modal.add': 'Ajouter un moyen de paiement',
    'payment.modal.edit': 'Modifier le moyen de paiement',
    'payment.tabList': 'Moyen de paiement',
    'product.importHelp':
      'Collez une URL Amazon, Nike, Etsy, Shopify ou de n’importe quelle boutique. Vos soutiens peuvent financer en sats ou acheter l’article pour vous.',
    'product.importTitle': 'Ajouter depuis un lien produit',
    'product.invalidUrl': 'Saisissez un lien de produit valide (https://…)',
    'product.parseFailed':
      'Impossible de lire toute la page — modifiez les détails, puis ajoutez. Le lien d’achat fonctionne toujours.',
    'progress.ofGoal': 'de l’objectif atteint',
    'roadmap.inApp.meta': 'Ce qui est livré et ce qui arrive ensuite pour KATOA.',
    'roadmap.inApp.title': 'Feuille de route publique',
    'security.title': 'Sécurité et conservation',
    'settings.nostrHint':
      'Facultatif — collez votre clé publique npub1… pour votre identité Nostr',
    'share.facebook': 'Partager sur Facebook',
    'share.linkedin': 'Partager sur LinkedIn',
    'share.nostr': 'Partager via une note Nostr',
    'share.promo': 'Soutenez cette wishlist avec Bitcoin !',
    'share.reddit': 'Partager sur Reddit',
    'share.telegram': 'Partager sur Telegram',
    'share.whatsapp': 'Partager sur WhatsApp',
    'terms.lastUpdated': 'Dernière mise à jour : 27 octobre 2025',
    'ticker.summary':
      'Réussites récentes de créateurs : zéro frais, paiements Lightning instantanés, soutien mondial.',
    'tipMenu.presetsLabel': 'Vos montants de pourboire (paramètres)',
    'wishlist.countryFlag': 'Pays : ${country}',
    'wishlist.countryFlagGeneric': 'Drapeau du pays',
  },
  de: {
    'nav.explore': 'Entdecken',
    'nav.dashboard': 'Dashboard',
    'nav.settings': 'Einstellungen',
    'nav.login': 'Anmelden',
    'nav.signup': 'Registrieren',
    'hero.title': 'Finanziere Träume mit Bitcoin',
    'hero.subtitle': 'Erstelle Wunschlisten, teile deine Geschichte und erhalte Bitcoin-Unterstützung aus der ganzen Welt',
    'hero.cta': 'Loslegen',
    'hero.explore': 'Listen Entdecken',
    'footer.about': 'Über Uns',
    'footer.contact': 'Kontakt',
    'footer.pricing': 'Preise',
    'footer.terms': 'Nutzungsbedingungen',
    'footer.privacy': 'Datenschutz',
    'footer.tagline': 'Träume verwirklichen mit Bitcoin',
    'explore.title': 'Wunschlisten Entdecken',
    'explore.subtitle': 'Entdecke erstaunliche Kreative und unterstütze ihre Träume weltweit',
    'explore.search': 'Listen durchsuchen...',
    'explore.allCountries': 'Alle Länder',
    'explore.showMap': 'Karte Anzeigen',
    'explore.hideMap': 'Karte Verbergen',
    'explore.mapTitle': 'Wunschlisten Weltweit',
    'explore.raised': 'gesammelt',
    'explore.noResults': 'Keine Ergebnisse gefunden',
    'explore.tryAgain': 'Versuche deine Filter anzupassen',
    'about.title': 'Über Katoa',
    'about.subtitle': 'Unsere Mission',
    'contact.title': 'Kontakt',
    'contact.subtitle': 'Melde Dich',
    'pricing.title': 'Preise',
    'pricing.subtitle': 'Einfach und Transparent',
    ...homeStrings.de,
    ...navUiStrings.de,
    ...contactPageStrings.de,
    ...faqPageStrings.de,
    ...pricingPageStrings.de,
    ...commonStrings.de,
    ...pageStrings.de,
    ...remainingDe,
    ...trustStrings.de,
    // Keys the UI renders that only English used to define; see i18nUsage.test.ts.
    'a11y.closeDialog': 'Dialog schließen',
    'a11y.dialog': 'Dialog',
    'a11y.navigatedTo': 'Navigiert zu',
    'contact.messageCopied':
      'Nachricht kopiert — füge sie in deine E-Mail ein, falls sich keine Mail-App öffnet',
    'demo.badge': 'Demo',
    'donate.addressLabel': 'Bitcoin-Adresse',
    'donate.copied': 'In die Zwischenablage kopiert',
    'donate.copyAddress': 'Adresse kopieren',
    'donate.customAmount': 'Eigener Betrag (Sats)',
    'donate.openInWallet': 'In der Wallet öffnen',
    'donate.safeHarbour':
      'Lernsoftware. Du sendest Sats direkt an den Empfänger — KATOA verwahrt dein Geld nie.',
    'donate.scanSubtitle': 'Jede Bitcoin-Wallet · Selbstverwahrung',
    'donate.scanTitle': 'Zum Spenden scannen',
    'donate.share': 'Teilen',
    'donate.tapCopy': 'Tippen, um die Adresse zu kopieren',
    'donate.thanks': 'Deine Sats halten KATOA kostenlos und quelloffen. Danke.',
    'donate.tipAmount': 'Vorgeschlagener Betrag',
    'explore.loadingMore': 'Weitere Projekte werden geladen',
    'explore.schemaName': 'KATOA-Creator-Wishlists',
    'explore.exclusive': 'EXKLUSIV',
    'explore.supporters': 'Unterstützer',
    'explore.video': 'VIDEO',
    'explore.videoCreators': 'Videocreator',
    'footer.copyFailed': 'Adresse konnte nicht kopiert werden',
    'gift.invalidAmount': 'Gib einen gültigen Betrag in Sats ein (größer als 0)',
    'home.stats.sampleRaised': 'Beispiel-Sats gesammelt',
    'home.statsSample':
      'Die Zahlen unten stammen aus dem öffentlichen Beispielkatalog (als Demo gekennzeichnet), bis echte Produktzähler angebunden sind.',
    'home.statsUnavailable':
      'Live-Zähler sind gerade nicht verfügbar — keine geschönten Schätzungen.',
    'payment.modal.add': 'Zahlungsmethode hinzufügen',
    'payment.modal.edit': 'Zahlungsmethode bearbeiten',
    'payment.tabList': 'Zahlungsmethode',
    'product.importHelp':
      'Füge eine URL von Amazon, Nike, Etsy, Shopify oder einem anderen Shop ein. Unterstützer können in Sats beitragen oder den Artikel für dich kaufen.',
    'product.importTitle': 'Aus Produktlink hinzufügen',
    'product.invalidUrl': 'Gib einen gültigen Produktlink ein (https://…)',
    'product.parseFailed':
      'Die Seite konnte nicht vollständig gelesen werden — bearbeite die Angaben und füge sie dann hinzu. Der Kaufen-Link funktioniert weiterhin.',
    'progress.ofGoal': 'des Ziels erreicht',
    'roadmap.inApp.meta': 'Was bei KATOA schon live ist und was als Nächstes kommt.',
    'roadmap.inApp.title': 'Öffentliche Roadmap',
    'security.title': 'Sicherheit & Verwahrung',
    'settings.nostrHint':
      'Optional — füge deinen öffentlichen Schlüssel npub1… für deine Nostr-Identität ein',
    'share.facebook': 'Auf Facebook teilen',
    'share.linkedin': 'Auf LinkedIn teilen',
    'share.nostr': 'Über eine Nostr-Notiz teilen',
    'share.promo': 'Unterstütze diese Wishlist mit Bitcoin!',
    'share.reddit': 'Auf Reddit teilen',
    'share.telegram': 'Auf Telegram teilen',
    'share.whatsapp': 'Auf WhatsApp teilen',
    'terms.lastUpdated': 'Zuletzt aktualisiert: 27. Oktober 2025',
    'ticker.summary':
      'Aktuelle Creator-Erfolge: null Gebühren, sofortige Lightning-Auszahlungen, weltweite Unterstützung.',
    'tipMenu.presetsLabel': 'Deine Trinkgeld-Beträge (Einstellungen)',
    'wishlist.countryFlag': 'Land: ${country}',
    'wishlist.countryFlagGeneric': 'Länderflagge',
  },
  ja: {
    'nav.explore': '探索',
    'nav.dashboard': 'ダッシュボード',
    'nav.settings': '設定',
    'nav.login': 'ログイン',
    'nav.signup': '新規登録',
    'hero.title': 'Bitcoinで夢を応援',
    'hero.subtitle': 'ウィッシュリストを作成し、あなたのストーリーを共有して、世界中からBitcoinサポートを受け取りましょう',
    'hero.cta': '始める',
    'hero.explore': 'リストを探す',
    'footer.about': '私たちについて',
    'footer.contact': 'お問い合わせ',
    'footer.pricing': '料金',
    'footer.terms': '利用規約',
    'footer.privacy': 'プライバシーポリシー',
    'footer.tagline': 'Bitcoinで夢を実現',
    'explore.title': 'ウィッシュリストを探す',
    'explore.subtitle': '素晴らしいクリエイターを発見し、世界中の夢を応援しよう',
    'explore.search': 'リストを検索...',
    'explore.allCountries': 'すべての国',
    'explore.showMap': '地図を表示',
    'explore.hideMap': '地図を非表示',
    'explore.mapTitle': '世界中のウィッシュリスト',
    'explore.raised': '達成',
    'explore.noResults': '結果が見つかりません',
    'explore.tryAgain': 'フィルターを調整してください',
    'about.title': 'Katoaについて',
    'about.subtitle': '私たちのミッション',
    'contact.title': 'お問い合わせ',
    'contact.subtitle': 'ご連絡ください',
    'pricing.title': '料金',
    'pricing.subtitle': 'シンプルで透明',
    ...homeStrings.ja,
    ...navUiStrings.ja,
    ...contactPageStrings.ja,
    ...faqPageStrings.ja,
    ...pricingPageStrings.ja,
    ...commonStrings.ja,
    ...pageStrings.ja,
    ...remainingJa,
    ...trustStrings.ja,
    // Keys the UI renders that only English used to define; see i18nUsage.test.ts.
    'a11y.closeDialog': 'ダイアログを閉じる',
    'a11y.dialog': 'ダイアログ',
    'a11y.navigatedTo': '移動しました:',
    'contact.messageCopied':
      'メッセージをコピーしました — メールアプリが開かない場合はメールに貼り付けてください',
    'demo.badge': 'デモ',
    'donate.addressLabel': 'Bitcoinアドレス',
    'donate.copied': 'クリップボードにコピーしました',
    'donate.copyAddress': 'アドレスをコピー',
    'donate.customAmount': '金額を指定（sats）',
    'donate.openInWallet': 'ウォレットで開く',
    'donate.safeHarbour':
      '教育用ソフトウェアです。satsは受け取り手に直接送られ、KATOAが資金を預かることはありません。',
    'donate.scanSubtitle': 'Bitcoinウォレットならどれでも · セルフカストディ',
    'donate.scanTitle': 'スキャンして寄付',
    'donate.share': '共有',
    'donate.tapCopy': 'タップしてアドレスをコピー',
    'donate.thanks': 'あなたのsatsがKATOAを無料かつオープンソースに保ちます。ありがとうございます。',
    'donate.tipAmount': 'おすすめの金額',
    'explore.loadingMore': 'プロジェクトをさらに読み込み中',
    'explore.schemaName': 'KATOAのクリエイターウィッシュリスト',
    'explore.exclusive': '限定',
    'explore.supporters': 'サポーター',
    'explore.video': '動画',
    'explore.videoCreators': '動画クリエイター',
    'footer.copyFailed': 'アドレスをコピーできませんでした',
    'gift.invalidAmount': '有効な金額をsatsで入力してください（0より大きい）',
    'home.stats.sampleRaised': 'サンプルで集まったsats',
    'home.statsSample':
      '以下の数値は、実際のカウンターが接続されるまで、公開サンプルカタログ（デモ表示）によるものです。',
    'home.statsUnavailable':
      'ライブカウンターは現在利用できません — 見栄えのための推計値は表示しません。',
    'payment.modal.add': '支払い方法を追加',
    'payment.modal.edit': '支払い方法を編集',
    'payment.tabList': '支払い方法',
    'product.importHelp':
      'Amazon、Nike、Etsy、Shopifyなど、どのショップのURLでも貼り付けられます。サポーターはsatsで支援するか、商品を代わりに購入できます。',
    'product.importTitle': '商品リンクから追加',
    'product.invalidUrl': '有効な商品リンクを入力してください（https://…）',
    'product.parseFailed':
      'ページを最後まで読み取れませんでした — 内容を編集して追加してください。購入リンクはそのまま使えます。',
    'progress.ofGoal': '目標達成',
    'roadmap.inApp.meta': 'KATOAで公開済みの内容と今後の予定。',
    'roadmap.inApp.title': '公開ロードマップ',
    'security.title': 'セキュリティとカストディ',
    'settings.nostrHint':
      '任意 — Nostrのアイデンティティ用に公開鍵 npub1… を貼り付けてください',
    'share.facebook': 'Facebookで共有',
    'share.linkedin': 'LinkedInで共有',
    'share.nostr': 'Nostrのノートで共有',
    'share.promo': 'このウィッシュリストをBitcoinで支援しよう！',
    'share.reddit': 'Redditで共有',
    'share.telegram': 'Telegramで共有',
    'share.whatsapp': 'WhatsAppで共有',
    'terms.lastUpdated': '最終更新: 2025年10月27日',
    'ticker.summary':
      'クリエイターの最近の成果：手数料ゼロ、Lightningで即時支払い、世界中からの支援。',
    'tipMenu.presetsLabel': 'チップのプリセット（設定）',
    'wishlist.countryFlag': '国: ${country}',
    'wishlist.countryFlagGeneric': '国旗',
  },
  zh: {
    'nav.explore': '探索',
    'nav.dashboard': '仪表板',
    'nav.settings': '设置',
    'nav.login': '登录',
    'nav.signup': '注册',
    'hero.title': '用比特币资助梦想',
    'hero.subtitle': '创建心愿单，分享您的故事，并从世界各地获得比特币支持',
    'hero.cta': '开始使用',
    'hero.explore': '探索心愿单',
    'footer.about': '关于我们',
    'footer.contact': '联系我们',
    'footer.pricing': '定价',
    'footer.terms': '服务条款',
    'footer.privacy': '隐私政策',
    'footer.tagline': '用比特币实现梦想',
    'explore.title': '探索心愿单',
    'explore.subtitle': '发现了不起的创作者，支持他们在全世界的梦想',
    'explore.search': '搜索心愿单...',
    'explore.allCountries': '所有国家',
    'explore.showMap': '显示地图',
    'explore.hideMap': '隐藏地图',
    'explore.mapTitle': '世界各地的心愿单',
    'explore.raised': '已筹集',
    'explore.noResults': '未找到结果',
    'explore.tryAgain': '尝试调整您的筛选条件',
    'about.title': '关于 Katoa',
    'about.subtitle': '我们的使命',
    'contact.title': '联系我们',
    'contact.subtitle': '保持联系',
    'pricing.title': '定价',
    'pricing.subtitle': '简单透明',
    ...homeStrings.zh,
    ...navUiStrings.zh,
    ...contactPageStrings.zh,
    ...faqPageStrings.zh,
    ...pricingPageStrings.zh,
    ...commonStrings.zh,
    ...pageStrings.zh,
    ...remainingZh,
    ...trustStrings.zh,
    // Keys the UI renders that only English used to define; see i18nUsage.test.ts.
    'a11y.closeDialog': '关闭对话框',
    'a11y.dialog': '对话框',
    'a11y.navigatedTo': '已导航至',
    'contact.messageCopied': '消息已复制 — 如果没有打开邮件应用，请粘贴到你的邮件中',
    'demo.badge': '演示',
    'donate.addressLabel': 'Bitcoin 地址',
    'donate.copied': '已复制到剪贴板',
    'donate.copyAddress': '复制地址',
    'donate.customAmount': '自定义金额（sats）',
    'donate.openInWallet': '在钱包中打开',
    'donate.safeHarbour':
      '教育用途软件。sats 直接发送给收款人 — KATOA 从不保管你的资金。',
    'donate.scanSubtitle': '任意 Bitcoin 钱包 · 自托管',
    'donate.scanTitle': '扫码捐赠',
    'donate.share': '分享',
    'donate.tapCopy': '点按复制地址',
    'donate.thanks': '你的 sats 让 KATOA 保持免费和开源。谢谢。',
    'donate.tipAmount': '建议金额',
    'explore.loadingMore': '正在加载更多项目',
    'explore.schemaName': 'KATOA 创作者心愿单',
    'explore.exclusive': '独家',
    'explore.supporters': '支持者',
    'explore.video': '视频',
    'explore.videoCreators': '视频创作者',
    'footer.copyFailed': '无法复制地址',
    'gift.invalidAmount': '请输入有效的 sats 金额（大于 0）',
    'home.stats.sampleRaised': '示例筹集 sats',
    'home.statsSample':
      '在接入真实产品计数器之前，下方数据来自公开示例目录（标注为演示）。',
    'home.statsUnavailable': '实时计数器暂时不可用 — 不显示虚高的估算。',
    'payment.modal.add': '添加支付方式',
    'payment.modal.edit': '编辑支付方式',
    'payment.tabList': '支付方式',
    'product.importHelp':
      '粘贴 Amazon、Nike、Etsy、Shopify 或任意商店的网址。支持者可以用 sats 资助，或替你把商品买下。',
    'product.importTitle': '从商品链接添加',
    'product.invalidUrl': '请输入有效的商品链接（https://…）',
    'product.parseFailed':
      '无法完整读取该页面 — 请编辑详情后再添加。购买链接仍然有效。',
    'progress.ofGoal': '目标已达成',
    'roadmap.inApp.meta': 'KATOA 已上线的功能与接下来的计划。',
    'roadmap.inApp.title': '公开路线图',
    'security.title': '安全与托管',
    'settings.nostrHint': '可选 — 粘贴你的 npub1… 公钥作为 Nostr 身份',
    'share.facebook': '分享到 Facebook',
    'share.linkedin': '分享到 LinkedIn',
    'share.nostr': '通过 Nostr 笔记分享',
    'share.promo': '用 Bitcoin 支持这个心愿单！',
    'share.reddit': '分享到 Reddit',
    'share.telegram': '分享到 Telegram',
    'share.whatsapp': '分享到 WhatsApp',
    'terms.lastUpdated': '最后更新：2025 年 10 月 27 日',
    'ticker.summary':
      '创作者近期成果：零手续费、Lightning 即时付款、全球支持。',
    'tipMenu.presetsLabel': '你的打赏预设（设置）',
    'wishlist.countryFlag': '国家/地区：${country}',
    'wishlist.countryFlagGeneric': '国旗',
  },
};

export const languageFlags: Record<Language, string> = {
  en: '🇬🇧',
  es: '🇪🇸',
  pt: '🇧🇷',
  fr: '🇫🇷',
  de: '🇩🇪',
  ja: '🇯🇵',
  zh: '🇨🇳',
};

/**
 * Picker labels are English-only on purpose.
 * Never put CJK (or any non-Latin script) here: the label shows in the English UI
 * and the product rule is that English never displays Chinese characters.
 * The active locale is still shown by the flag + aria-selected state.
 */
export const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese',
  fr: 'French',
  de: 'German',
  ja: 'Japanese',
  zh: 'Chinese',
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(resolveInitialLanguage);

  useEffect(() => {
    localStorage.setItem('katoa-language', language);
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang && isValidLanguage(urlLang) && urlLang !== language) {
      setLanguageState(urlLang);
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
